import { useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import axios from 'axios'
import { loadStripe } from '@stripe/stripe-js'
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { marked } from 'marked'
import DOMPurify from 'dompurify'
import Toggle from '../../components/Toggle'
import LanguageToggle from '../../components/LanguageToggle'
import { useLanguage } from '../../context/LanguageContext'
import { PreviewWidget } from '../setup/BrandingSetup'
import { mdToHtml } from '../../components/MarkdownField'
import { formatPhone, isValidPhone } from '../../utils/phone'
import { TIME_WINDOWS } from '../../utils/timeWindows'
import { trackEmbed } from '../../utils/embedTrack'

/**
 * Customer-facing reseller iframe — Phase 7.
 *
 * Mounted at /embed/reseller/:iframeKey on PlayStay-Web. Public route
 * (no AuthProvider needed). The iframeKey resolves to a grouping +
 * reseller + active packages via /api/public/reseller-embed/*.
 *
 * Three steps:
 *   1. Gallery — show all active packages in the grouping
 *   2. Detail  — pick package, fill in dates / golfers / courses / hotel
 *      / addons / customer info
 *   3. Confirm — show summary, "pay" button (Phase 7 stub: hits the
 *      /confirm endpoint that flips status without real payment)
 *
 * Brand resolution applied at every level — logo + primary color from
 * the reseller's effective brand (package > grouping > account).
 *
 * Does NOT use the auth-aware tournamentApi — uses a fresh axios with
 * NO Authorization header, so customer browsers don't need to be
 * logged in to anything.
 */
export default function ResellerEmbed() {
  const { iframeKey } = useParams()
  const { setLangId } = useLanguage()
  // Engagement: one 'view' ping per embed load (fire-and-forget).
  useEffect(() => { trackEmbed(iframeKey, 'view', 'packages') }, [iframeKey])
  // Bound funnel tracker — every engagement ping routes through here so
  // sub-flows don't each need to carry the iframeKey. Fire-and-forget.
  const track = useMemo(
    () => (eventType, surface, props) => trackEmbed(iframeKey, eventType, surface, props),
    [iframeKey]
  )
  const [step, setStep] = useState('gallery') // gallery | detail | confirming | confirmed | error
  const [ctx, setCtx] = useState(null)
  const [selectedPkg, setSelectedPkg] = useState(null)
  const [pkgDetail, setPkgDetail] = useState(null)
  const [confirmedBooking, setConfirmedBooking] = useState(null)
  const [error, setError] = useState(null)
  // True when this iframe was loaded via #package-{slug} deep-link
  // (e.g. from the account-wide search widget). Drives the Back
  // behavior: deep-link entries pop the browser history (returning
  // to the search) instead of opening the per-grouping gallery.
  const [enteredViaDeepLink, setEnteredViaDeepLink] = useState(false)
  // Resume mode: the customer portal sends them here with
  // ?resumeBookingId=N&resumeToken=T to pick up a saved 'building'
  // booking. When set, BookingFlow hydrates from this data and starts
  // at the Review stage.
  const [resumeState, setResumeState] = useState(null)
  const [resumeError, setResumeError] = useState(null)

  // Public axios — bypass any global auth headers.
  // Use VITE_API_ORIGIN so the iframe (hosted on stayplay.integolf.com or
  // the reseller's custom domain) actually reaches the API host instead of
  // the static SPA host serving the index.html for every /api/* request.
  //
  // Internal-viewer mode: the embed is public, but when it loads on the
  // same origin where a super-admin or reseller-owner is already logged
  // in, the portal_token sits in localStorage. We attach it to every
  // request so the backend can recognise an 'internal' viewer and unlock
  // debug-only fields (raw fees, markup breakdown). Real customers have
  // no token -> no auth header -> normal public path.
  const api = useMemo(() => {
    const apiOrigin = import.meta.env.VITE_API_ORIGIN || window.location.origin
    const instance = axios.create({ baseURL: `${apiOrigin}/api/public/reseller-embed/${iframeKey}`, validateStatus: () => true })
    instance.interceptors.request.use(cfg => {
      const t = localStorage.getItem('portal_token') || localStorage.getItem('token')
      if (t) cfg.headers = { ...cfg.headers, Authorization: 'Bearer ' + t }
      return cfg
    })
    return instance
  }, [iframeKey])

  // Decode the JWT client-side (no signature check -- the backend does
  // that) to identify whether the viewer is internal. Used only for UI
  // gating (debug panel visibility); never for security decisions.
  const internalViewer = useMemo(() => {
    try {
      const t = localStorage.getItem('portal_token') || localStorage.getItem('token')
      if (!t) return null
      const payload = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')))
      return { email: payload.email, groupId: payload.group_id, id: payload.id }
    } catch { return null }
  }, [])

  // Fixed-height iframe model: the parent gives us a height (900px by
  // default per the snippet in GroupingEdit's EmbedTab) and we let the
  // iframe content scroll internally when it overflows. No postMessage
  // resize plumbing — it was fragile in the wild (host page sidebars,
  // overflow:hidden parents, lazy-loaded images all caused issues).

  useEffect(() => {
    api.get('/context').then(r => {
      if (r.status >= 400) { setError(r.data?.message || `Error ${r.status}`); setStep('error') }
      else {
        setCtx(r.data)
        // NB: no 'results' ping here — the gallery showing packages is implied
        // by 'view'. The funnel's "Saw results" stage is reserved for post-
        // search inventory (hotels/rounds in the build, or account-search
        // results), so it stays after "Ran a search" in the drop-off order.
        // EPIC #106 P2 -- open the embed in the grouping's default language.
        // The customer can still switch via the header toggle.
        const dl = r.data?.grouping?.defaultLanguage
        if (dl) setLangId(dl === 'fr' ? 2 : 1)
        const params = new URLSearchParams(window.location.search || '')
        const resumeBookingId = params.get('resumeBookingId')
        const resumeToken = params.get('resumeToken')
        // Deep-link support — when the iframe is loaded with
        // `#package-{slug}` (e.g. from the account-wide search
        // widget), jump straight into the booking flow for that
        // package. Falls back to gallery if no match.
        const hash = String(window.location.hash || '')
        const m = hash.match(/^#package-([\w-]+)/)
        if (resumeBookingId && resumeToken) {
          // Resume flow: fetch the saved booking, then enter detail
          // with the corresponding package. BookingFlow reads
          // resumeState and starts at the Review stage.
          api.get(`/bookings/${resumeBookingId}/resume?token=${encodeURIComponent(resumeToken)}`).then(rr => {
            if (rr.status >= 400) {
              setResumeError(rr.data?.message || 'Could not resume this trip')
              // Fall through to normal flow so the customer can still book.
              return
            }
            setResumeState(rr.data)
            const pkg = (r.data.packages || []).find(p => p.Slug === rr.data.package.slug)
            if (pkg) {
              setEnteredViaDeepLink(true)
              setTimeout(() => selectPackage(pkg), 0)
            } else {
              setResumeError('The package is no longer available. Start a new booking.')
            }
          }).catch(e => setResumeError(e.message))
        } else if (m) {
          const slug = m[1]
          const pkg = (r.data.packages || []).find(p => p.Slug === slug)
          if (pkg) {
            setEnteredViaDeepLink(true)
            // Defer one tick so React has the ctx in state before we
            // transition to 'detail' (selectPackage reads it).
            setTimeout(() => selectPackage(pkg), 0)
          }
        }
      }
    }).catch(err => { setError(err.message); setStep('error') })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api])

  function selectPackage(pkg) {
    setSelectedPkg(pkg)
    setStep('detail')
    setPkgDetail(null)
    api.get(`/packages/${pkg.Slug}`).then(r => {
      if (r.status >= 400) { setError(r.data?.message); setStep('error') }
      else setPkgDetail(r.data)
    })
  }
  function backToGallery() {
    // If the customer landed here via a deep-link from the
    // account-wide search widget, "back" should return them to the
    // search rather than dump them on the per-grouping gallery (a
    // page they never saw). The iframe navigated itself when they
    // clicked a search tile, so history.back() pops back to the
    // search view inside the same iframe.
    if (enteredViaDeepLink && window.history.length > 1) {
      window.history.back()
      return
    }
    setStep('gallery'); setSelectedPkg(null); setPkgDetail(null)
  }

  if (step === 'error')         return <Frame brand={null}><div style={errorBox}>{error}</div></Frame>
  if (!ctx)                     return <Frame brand={null}><Loading /></Frame>

  const brand = pkgDetail?.brand || ctx.brand
  const branding = ctx.branding || {}
  const reseller = ctx.reseller
  // EPIC #116 — golf-bundle stores have their own (much simpler) flow:
  // browse prepaid passes → checkout → confirmed. No hotels / tee-times.
  const isBundles = ctx?.grouping?.type === 'bundles'

  return (
    <Frame
      brand={brand}
      branding={branding}
      reseller={reseller}
      groupingName={ctx?.grouping?.name}
      // Per-grouping toggle: hide the iframe header (logo + email)
      // when this grouping has ShowHeader off. Applies to every step
      // in the iframe (gallery, booking, confirmed) since header
      // chrome is grouping-wide.
      hideHeader={ctx?.grouping?.showHeader === false}
    >
      {isBundles ? (
        <BundleStore ctx={ctx} api={api} brand={brand} branding={branding} reseller={reseller} />
      ) : (
        <>
          {step === 'gallery'   && <Gallery ctx={ctx} onPick={selectPackage} onBookTeeTime={() => setStep('teetime')} onRequestTrip={() => setStep('request')} onBuildYourOwn={() => setStep('byo')} brand={brand} branding={branding} />}
          {step === 'teetime'   && <TeeTimeFlow ctx={ctx} api={api} brand={brand} branding={branding} internalViewer={internalViewer} track={track}
                                                onBack={() => setStep('gallery')}
                                                onConfirmed={(b) => { track('book', 'teetime', { bookingId: b?.bookingId || null }); setConfirmedBooking(b); setStep('confirmed') }} />}
          {step === 'byo'       && <BuildYourOwnFlow ctx={ctx} api={api} brand={brand} branding={branding} internalViewer={internalViewer} track={track}
                                                onBack={() => setStep('gallery')}
                                                onConfirmed={(b) => { track('book', 'byo', { bookingId: b?.bookingId || null }); setConfirmedBooking(b); setStep('confirmed') }} />}
          {step === 'request'   && <TripRequestFlow ctx={ctx} api={api} brand={brand} branding={branding} onBack={() => setStep('gallery')} />}
          {step === 'detail'    && <BookingFlow detail={pkgDetail} ctx={ctx} api={api} brand={brand} branding={branding} internalViewer={internalViewer} track={track}
                                                onBack={backToGallery}
                                                resumeState={resumeState}
                                                resumeError={resumeError}
                                                onConfirmed={(b) => { track('book', 'package', { bookingId: b?.bookingId || null }); setConfirmedBooking(b); setStep('confirmed') }} />}
          {step === 'confirmed' && <ConfirmedScreen booking={confirmedBooking} brand={brand} branding={branding} reseller={reseller} />}
        </>
      )}
    </Frame>
  )
}

// ─── Branding helpers ──────────────────────────────────────────────────
//
// Read tokens from either the new grouping branding row OR the legacy
// brand profile fallback. headerFontStyle / bodyFontStyle return inline
// CSS objects you spread into a style attribute.
function headerFontStyle(branding) {
  if (!branding?.headerFont) return {}
  return {
    fontFamily: `'${branding.headerFont}', Georgia, serif`,
    fontWeight: branding.headerWeight || 700,
    textTransform: branding.headerCase || 'normal',
    letterSpacing: branding.headerLetterSpacing || 'normal',
  }
}
function bodyFontStyle(branding) {
  if (!branding?.bodyFont) return {}
  return { fontFamily: `'${branding.bodyFont}', system-ui, sans-serif` }
}

// ─── Frame: brand-driven shell ───────────────────────────────────────────

function Frame({ brand, branding, reseller, groupingName, hideHeader, children }) {
  // Branding tokens win over legacy brand profile. Both fall back to a
  // dark slate so the iframe never renders unstyled.
  const primary  = branding?.primaryColor    || brand?.primaryColor   || '#0f172a'
  const logoUrl  = branding?.logoUrl         || brand?.logoUrl        || null
  const bodyStyle = bodyFontStyle(branding)

  // Make the iframe transparent so the host page's background bleeds
  // through — this is what makes the embed feel native instead of a
  // visible "card on a card." The header (when shown) still has its
  // own white surface so the brand identity area stays defined.
  // Setting on html/body too because iframes need both to inherit
  // transparency cross-document.
  useEffect(() => {
    if (window === window.parent) return
    // Make the iframe transparent so the host page bleeds through. The
    // global stylesheet (index.css) sets `background: var(--bg)` on
    // html, body, AND #root — clear all three. Height is left at the
    // stylesheet default (100%) so the iframe scrolls internally when
    // content exceeds the allocated height.
    const root = document.getElementById('root')
    const prev = {
      html: document.documentElement.style.background,
      body: document.body.style.background,
      root: root?.style.background,
    }
    document.documentElement.style.background = 'transparent'
    document.body.style.background = 'transparent'
    if (root) root.style.background = 'transparent'
    return () => {
      document.documentElement.style.background = prev.html
      document.body.style.background = prev.body
      if (root) root.style.background = prev.root
    }
  }, [])

  const isEmbedded = window !== window.parent
  return (
    <div style={{
      // Standalone: fill the window so backgrounds + footer sit correctly.
      // Embedded: DO NOT use 100vh — the parent auto-resizes the iframe to our
      // content height, so 100vh (= the iframe's height) would feed back into
      // the height we post and the iframe would grow without bound. Let the
      // content define its own height when framed.
      minHeight: isEmbedded ? 0 : '100vh',
      background: isEmbedded ? 'transparent' : '#f8fafc',
      color: '#0f172a',
      ...bodyStyle,
      // Fallback when branding has no body font set
      fontFamily: bodyStyle.fontFamily || 'system-ui, -apple-system, sans-serif',
    }}>
      {!hideHeader ? (
        <header style={{ background: '#fff', borderBottom: '1px solid #e2e8f0', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          {logoUrl
            ? <img src={resolveUrl(logoUrl)} alt={reseller?.name || 'Brand'} style={{ height: 36, maxWidth: 180, objectFit: 'contain' }} />
            : reseller?.name && <strong style={{ fontSize: 18, ...headerFontStyle(branding) }}>{reseller.name}</strong>}
          {/* Grouping name shown next to the logo so customers know
              which destination they're browsing without us repeating
              it in the gallery body. */}
          {groupingName && (
            <span style={{
              fontSize: 16, fontWeight: 600, color: primary,
              borderLeft: '1px solid #e2e8f0', paddingLeft: 16, marginLeft: 4,
              ...headerFontStyle(branding),
            }}>
              {groupingName}
            </span>
          )}
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', justifyContent: 'flex-end', minWidth: 0 }}>
            {brand?.supportEmail && <span style={{ fontSize: 13, color: '#64748b' }}>Need help? <a href={`mailto:${brand.supportEmail}`} style={{ color: primary }}>{brand.supportEmail}</a></span>}
            <LanguageToggle />
          </div>
        </header>
      ) : (
        // Header hidden by the reseller: still surface a discreet language
        // switcher so customers can change language on a clean embed.
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '8px 16px 0' }}>
          <LanguageToggle />
        </div>
      )}
      <main style={{ maxWidth: 1100, margin: '0 auto', padding: isEmbedded ? '16px 24px' : '32px 24px' }}>{children}</main>
      <footer style={{ padding: '24px', textAlign: 'center', fontSize: 12, color: '#94a3b8', borderTop: isEmbedded ? 'none' : '1px solid #e2e8f0' }}>
        Powered by <a href="https://integolf.com" target="_blank" rel="noopener" style={{ color: '#00d19f', textDecoration: 'none', fontWeight: 600 }}>InteGolf</a>
      </footer>
    </div>
  )
}

// Image URLs may be /site-assets/... (local) or absolute. Local URLs need
// the API origin prepended when the iframe is hosted on a different domain.
function resolveUrl(u) {
  if (!u) return null
  if (u.startsWith('http')) return u
  const apiOrigin = import.meta.env.VITE_API_ORIGIN || window.location.origin
  return apiOrigin + u
}

// ─── Gallery ─────────────────────────────────────────────────────────────

// Three prominence tiers for the embed's "Book a tee time" banner, chosen
// per-grouping in the portal Settings tab. Returns container/title/sub/button
// style objects; `regular` is the subtle default, `extreme` is a full
// brand-colour attention banner.
function ttBannerTheme(style, primary) {
  if (style === 'extreme') {
    return {
      container: { background: primary, border: 'none', borderRadius: 16, padding: '24px 26px', boxShadow: '0 8px 24px rgba(0,0,0,0.20)' },
      title: { fontSize: 23, fontWeight: 900, color: '#fff', lineHeight: 1.15, letterSpacing: '-0.01em' },
      sub: { fontSize: 15, color: 'rgba(255,255,255,0.92)', marginTop: 2 },
      button: { padding: '15px 28px', background: '#fff', color: primary, borderRadius: 12, fontSize: 16, fontWeight: 900, boxShadow: '0 2px 10px rgba(0,0,0,0.20)' },
    }
  }
  if (style === 'bold') {
    return {
      container: { background: '#fff', border: `2px solid ${primary}`, borderRadius: 14, padding: '18px 22px', boxShadow: '0 2px 14px rgba(0,0,0,0.08)' },
      title: { fontSize: 19, fontWeight: 800, color: '#0f172a' },
      sub: { fontSize: 14, color: '#475569', marginTop: 2 },
      button: { padding: '13px 24px', background: primary, color: '#fff', borderRadius: 10, fontSize: 15, fontWeight: 800 },
    }
  }
  // regular (default)
  return {
    container: { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 18px' },
    title: { fontSize: 15, fontWeight: 700, color: '#0f172a' },
    sub: { fontSize: 13, color: '#64748b' },
    button: { padding: '10px 18px', background: primary, color: '#fff', borderRadius: 8, fontSize: 14, fontWeight: 700 },
  }
}

function Gallery({ ctx, onPick, onBookTeeTime, onRequestTrip, onBuildYourOwn, brand, branding }) {
  const { langId, t } = useLanguage()
  const primary = branding?.primaryColor || brand?.primaryColor || '#0f172a'
  const secondary = branding?.secondaryColor || '#0f172a'
  const allowTeeTime = !!ctx?.grouping?.allowTeeTimeOnly
  const allowTripRequest = !!ctx?.grouping?.allowCustomTripRequests
  const allowBuildYourOwn = !!ctx?.grouping?.buildYourOwnEnabled
  // Resolve each package's card description + the grouping intro to the language
  // the golfer is browsing in (French falls back to English when no French copy
  // exists). Mirrors BundleStore — without this the cards stayed English in FR.
  const packages = (ctx.packages || []).map(p => ({
    ...p,
    PackageName: (langId === 2 && p.PackageNameFr) ? p.PackageNameFr : p.PackageName,
    Description: (langId === 2 && p.DescriptionFr) ? p.DescriptionFr : p.Description,
  }))
  const groupingDesc = (langId === 2 && ctx.grouping?.descriptionFr)
    ? ctx.grouping.descriptionFr
    : ctx.grouping?.description
  // Render via the shared PreviewWidget so customers see the EXACT
  // layout the reseller chose in the portal Branding tab. Click events
  // bubble via data-pkg-id delegation; PreviewWidget calls onPackageClick
  // with the clicked package and we forward to BookingFlow.
  return (
    <>
      {/* Grouping name is now in the Frame header (next to the logo),
          so we don't repeat it in the gallery body. The description
          stays here as a sub-line for the page. */}
      {groupingDesc && <p style={{ color: '#475569', marginTop: 0, marginBottom: 24, fontSize: 15, lineHeight: 1.5, ...bodyFontStyle(branding) }}>{groupingDesc}</p>}
      {/* Standalone tee-time booking (grouping-level toggle). Sits above
          the package gallery so a golfer who just wants a round can jump
          straight in without wading through stay-and-play bundles. */}
      {allowTeeTime && (() => {
        const th = ttBannerTheme(ctx?.grouping?.teeTimeBannerStyle || 'regular', primary)
        return (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            flexWrap: 'wrap', marginBottom: 24, ...th.container,
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ ...headerFontStyle(branding), ...th.title }}>
                {t('sp_tt_banner_title', 'Just want to play a round?')}
              </div>
              <div style={th.sub}>
                {t('sp_tt_banner_sub', 'Book an individual tee time — pick your course, date and time.')}
              </div>
            </div>
            <button type="button" onClick={onBookTeeTime} style={{
              whiteSpace: 'nowrap', border: 'none', cursor: 'pointer', ...th.button,
            }}>
              {t('sp_tt_book_cta', 'Book a tee time')} →
            </button>
          </div>
        )
      })()}
      {/* Build-your-own trip (grouping-level toggle). Sits above the packages
          for price-sensitive shoppers who'd rather assemble their own trip. */}
      {allowBuildYourOwn && (() => {
        const th = ttBannerTheme(primary && 'bold', primary)
        return (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            flexWrap: 'wrap', marginBottom: 24, ...th.container,
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ ...headerFontStyle(branding), ...th.title }}>
                {t('sp_byo_card_title', 'Build your own trip')}
              </div>
              <div style={th.sub}>
                {t('sp_byo_card_sub', 'Pick your hotel and rounds - pay only for what you choose.')}
              </div>
            </div>
            <button type="button" onClick={onBuildYourOwn} style={{
              whiteSpace: 'nowrap', border: 'none', cursor: 'pointer', ...th.button,
            }}>
              {t('sp_byo_card_title', 'Build your own trip')} →
            </button>
          </div>
        )
      })()}
      {packages.length === 0
        ? ((allowTeeTime || allowTripRequest || allowBuildYourOwn)
            ? null
            : <div style={empty}>{t('sp_no_packages_available', 'No packages currently available.')}</div>)
        : <PreviewWidget packages={packages} branding={branding} onPackageClick={onPick} suppressPoweredBy />
      }
      {/* Custom trip (concierge) request — grouping-level toggle. Sits BELOW the
          packages (bottom of the page) so it reads as "none of these fit? ask us
          to hand-build one". Prominence (regular / bold / extreme) mirrors the
          tee-time banner, chosen per-grouping in the portal Settings tab. */}
      {allowTripRequest && (() => {
        const th = ttBannerTheme(ctx?.grouping?.customTripBannerStyle || 'regular', primary)
        return (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
            flexWrap: 'wrap', marginTop: 24, ...th.container,
          }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ ...headerFontStyle(branding), ...th.title }}>
                {t('sp_ctr_banner_title', 'Want something custom?')}
              </div>
              <div style={th.sub}>
                {t('sp_ctr_banner_sub', "Tell us your dates and group and we'll hand-build a trip and send you a price.")}
              </div>
            </div>
            <button type="button" onClick={onRequestTrip} style={{
              whiteSpace: 'nowrap', border: 'none', cursor: 'pointer', ...th.button,
            }}>
              {t('sp_ctr_banner_cta', 'Plan my trip')} →
            </button>
          </div>
        )
      })()}
    </>
  )
}

// ─── Custom trip request flow (grouping AllowCustomTripRequests) ─────────
//
// A concierge "Plan my trip" form: the golfer gives dates, group size, rooms,
// preferred courses (hints) and notes; we create a `requested` quote and email
// the reseller, who hand-builds the itinerary and sends a price. Nothing is
// charged here — it's a lead.
function TripRequestFlow({ ctx, api, brand, branding, onBack }) {
  const { t } = useLanguage()
  const primary = branding?.primaryColor || brand?.primaryColor || '#0f172a'

  const [courses, setCourses] = useState(null) // null = loading, [] = none
  const [form, setForm] = useState({
    arrivalDate: '', nights: 2, golfDays: 1, golfers: 2, rooms: 1,
    name: '', email: '', phone: '', notes: '', website: '', // website = honeypot
  })
  const [courseIds, setCourseIds] = useState(() => new Set())
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)
  const [done, setDone] = useState(null) // { quoteNumber }

  useEffect(() => {
    let cancelled = false
    api.get('/request-courses').then(r => {
      if (cancelled) return
      setCourses(r.status < 400 ? (r.data.courses || []) : [])
    }).catch(() => { if (!cancelled) setCourses([]) })
    return () => { cancelled = true }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const set = (patch) => setForm(f => ({ ...f, ...patch }))
  const toggleCourse = (id) => setCourseIds(prev => {
    const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next
  })

  async function submit() {
    setErr(null)
    if (!form.name.trim()) { setErr(t('sp_ctr_err_name', 'Please enter your name.')); return }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) { setErr(t('sp_ctr_err_email', 'Please enter a valid email.')); return }
    setSubmitting(true)
    try {
      const r = await api.post('/trip-requests', {
        arrivalDate: form.arrivalDate || null,
        nights: Number(form.nights), golfDays: Number(form.golfDays),
        golfers: Number(form.golfers), rooms: Number(form.rooms),
        courseIds: Array.from(courseIds),
        name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim(),
        notes: form.notes.trim(), website: form.website,
      })
      if (r.status >= 400) { setErr(r.data?.message || `Error ${r.status}`); setSubmitting(false); return }
      setDone({ quoteNumber: r.data?.quoteNumber || null })
    } catch (e) { setErr(e.message); setSubmitting(false) }
  }

  const lbl = { fontSize: 11, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#64748b', marginBottom: 4, display: 'block' }
  const inp = { width: '100%', padding: '9px 11px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, background: '#fff', boxSizing: 'border-box' }
  const numOpts = (from, to) => { const a = []; for (let i = from; i <= to; i++) a.push(i); return a }

  if (done) {
    return (
      <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center', padding: '24px 0' }}>
        <div style={{ width: 56, height: 56, borderRadius: '50%', background: `${primary}14`, color: primary, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 28 }}>✓</div>
        <h1 style={{ margin: '0 0 8px', fontSize: 22, ...headerFontStyle(branding) }}>{t('sp_ctr_done_title', 'Request received')}</h1>
        <p style={{ color: '#475569', fontSize: 14.5, lineHeight: 1.5, margin: '0 0 8px' }}>
          {t('sp_ctr_done_body', "Thanks! We'll build your trip and email you a price shortly.")}
        </p>
        {done.quoteNumber && <p style={{ color: '#94a3b8', fontSize: 13 }}>{t('sp_ctr_done_ref', 'Reference')}: <strong>{done.quoteNumber}</strong></p>}
        <button type="button" onClick={onBack} style={{ marginTop: 16, padding: '10px 20px', background: primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          {t('sp_ctr_done_back', 'Back to trips')}
        </button>
      </div>
    )
  }

  return (
    <div>
      <button type="button" onClick={onBack} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, marginBottom: 12, fontSize: 13 }}>{t('sp_back', '← Back')}</button>
      <h1 style={{ margin: '0 0 4px', fontSize: 24, ...headerFontStyle(branding) }}>{t('sp_ctr_flow_title', 'Plan my trip')}</h1>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 20 }}>
        {t('sp_ctr_flow_sub', "Tell us what you're after and we'll hand-build a trip, then send you a price. Nothing to pay now.")}
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 12, marginBottom: 16 }}>
        <div><label style={lbl}>{t('sp_ctr_f_arrival', 'Arrival date')}</label><input type="date" style={inp} value={form.arrivalDate} onChange={e => set({ arrivalDate: e.target.value })} /></div>
        <div><label style={lbl}>{t('sp_ctr_f_nights', 'Nights')}</label><select style={inp} value={form.nights} onChange={e => set({ nights: e.target.value })}>{numOpts(1, 14).map(n => <option key={n} value={n}>{n}</option>)}</select></div>
        <div><label style={lbl}>{t('sp_ctr_f_golfdays', 'Golf days')}</label><select style={inp} value={form.golfDays} onChange={e => set({ golfDays: e.target.value })}>{numOpts(1, 14).map(n => <option key={n} value={n}>{n}</option>)}</select></div>
        <div><label style={lbl}>{t('sp_ctr_f_golfers', 'Golfers')}</label><select style={inp} value={form.golfers} onChange={e => set({ golfers: e.target.value })}>{numOpts(1, 16).map(n => <option key={n} value={n}>{n}</option>)}</select></div>
        <div><label style={lbl}>{t('sp_ctr_f_rooms', 'Rooms')}</label><select style={inp} value={form.rooms} onChange={e => set({ rooms: e.target.value })}>{numOpts(1, 8).map(n => <option key={n} value={n}>{n}</option>)}</select></div>
      </div>

      {courses === null ? (
        <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 16 }}>{t('sp_ctr_courses_loading', 'Loading courses…')}</div>
      ) : courses.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <label style={lbl}>{t('sp_ctr_f_courses', 'Which courses? (optional)')}</label>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 8 }}>
            {courses.map(c => {
              const on = courseIds.has(c.coursesId)
              return (
                <label key={c.coursesId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', border: `1px solid ${on ? primary : '#e2e8f0'}`, background: on ? `${primary}0d` : '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>
                  <input type="checkbox" checked={on} onChange={() => toggleCourse(c.coursesId)} />
                  <span style={{ minWidth: 0 }}>
                    <span style={{ fontWeight: 600, color: '#0f172a' }}>{c.name}</span>
                    {c.city && <span style={{ color: '#94a3b8' }}> — {c.city}</span>}
                  </span>
                </label>
              )
            })}
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
        <div><label style={lbl}>{t('sp_ctr_f_name', 'Name')}</label><input style={inp} value={form.name} onChange={e => set({ name: e.target.value })} /></div>
        <div><label style={lbl}>{t('sp_ctr_f_email', 'Email')}</label><input type="email" style={inp} value={form.email} onChange={e => set({ email: e.target.value })} /></div>
        <div><label style={lbl}>{t('sp_ctr_f_phone', 'Phone')}</label><input style={inp} value={form.phone} onChange={e => set({ phone: e.target.value })} /></div>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={lbl}>{t('sp_ctr_f_notes', 'Notes (optional)')}</label>
        <textarea rows={3} style={{ ...inp, resize: 'vertical' }} placeholder={t('sp_ctr_notes_ph', "Preferred tee window, group needs, additional courses you'd like to add…")} value={form.notes} onChange={e => set({ notes: e.target.value })} />
      </div>

      {/* Honeypot — hidden from real users; bots fill it and get silently dropped. */}
      <input type="text" tabIndex={-1} autoComplete="off" value={form.website} onChange={e => set({ website: e.target.value })}
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }} aria-hidden="true" />

      {err && <div style={{ color: '#991b1b', fontSize: 13, marginBottom: 10 }}>{err}</div>}
      <button type="button" onClick={submit} disabled={submitting} style={{ width: '100%', padding: '12px 16px', background: primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: submitting ? 'default' : 'pointer' }}>
        {submitting ? t('sp_ctr_submitting', 'Sending…') : t('sp_ctr_submit', 'Send my request')}
      </button>
    </div>
  )
}

// ─── Standalone tee-time flow (grouping AllowTeeTimeOnly) ────────────────
//
// A bare "book a round" experience: pick a date + course + tee time, choose
// party size (default 2), enter golfer details, pay. No hotel, no package, no
// addons. Reuses the SAME PayScreen / Stripe checkout as the package flow —
// the backend returns an identical bookingForPay shape (bookingId, gateway,
// magicToken, stripePublishableKey, myTripUrl) plus an authoritative `total`.
function TeeTimeFlow({ ctx, api, brand, branding, internalViewer, onBack, onConfirmed, track }) {
  const fire = track || (() => {})
  const { t } = useLanguage()
  const primary = branding?.primaryColor || brand?.primaryColor || '#0f172a'
  const currency = ctx?.reseller?.currency

  const [courses, setCourses] = useState(null)   // null = loading, [] = none
  const [loadErr, setLoadErr] = useState(null)
  const [coursesId, setCoursesId] = useState(null)
  const [defaultCourseId, setDefaultCourseId] = useState(null) // grouping's chosen default, or null
  const [date, setDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 1)
    return d.toISOString().slice(0, 10)     // default = tomorrow
  })
  const [golfers, setGolfers] = useState(2)      // default party size
  const [selected, setSelected] = useState(null) // chosen slot object
  const [golfer, setGolfer] = useState({ name: '', email: '', phone: '' })

  const [stage, setStage] = useState('pick')     // pick | pay
  const [bookingForPay, setBookingForPay] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)

  // Load the grouping's bookable courses on mount; default to the first.
  useEffect(() => {
    let cancelled = false
    api.get('/tee-time/courses').then(r => {
      if (cancelled) return
      if (r.status >= 400) { setLoadErr(r.data?.message || `Error ${r.status}`); setCourses([]) }
      else {
        const list = r.data?.courses || []
        // The grouping can nominate a default course. The backend only returns
        // one that's actually in the bookable set (stale defaults come back
        // null), so we can trust it — pre-select it, else fall back to the
        // first (alphabetical) course, today's behavior.
        const def = r.data?.defaultCourseId || null
        setCourses(list)
        setDefaultCourseId(def && list.some(c => c.coursesId === def) ? def : null)
        if (list.length) {
          const hasDefault = def && list.some(c => c.coursesId === def)
          setCoursesId(hasDefault ? def : list[0].coursesId)
        }
      }
    }).catch(e => { if (!cancelled) { setLoadErr(e.message); setCourses([]) } })
    return () => { cancelled = true }
  }, [api])

  // Any change to the query (course / date / party size) invalidates the
  // previously picked slot — the ref + price no longer apply.
  useEffect(() => { setSelected(null) }, [coursesId, date, golfers])

  const minDate = new Date().toISOString().slice(0, 10)
  const selCourse = (courses || []).find(c => c.coursesId === coursesId)
  const perGolfer = selected?.pricePerGolfer != null ? Number(selected.pricePerGolfer) : null
  const subtotal = perGolfer != null ? perGolfer * Number(golfers) : null
  const formReady = !!selected && golfer.name.trim().length >= 2 && golfer.email.includes('@') && isValidPhone(golfer.phone)

  async function createBooking() {
    setErr(null); setSubmitting(true)
    try {
      const { data, status } = await api.post('/tee-time-bookings', {
        coursesId, playDate: date, teeTime: selected.teeTime, ref: selected.ref,
        numGolfers: Number(golfers), golfer,
      })
      if (status >= 400) throw new Error(data?.message || `Error ${status}`)
      setBookingForPay(data)
      fire('checkout_start', 'teetime', { numGolfers: Number(golfers) })
      setStage('pay')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (e2) { setErr(e2.message) }
    finally { setSubmitting(false) }
  }

  async function payCard(card) {
    setErr(null); setSubmitting(true)
    try {
      const r = await api.post(`/bookings/${bookingForPay.bookingId}/payments`, {
        card: { number: card.number, expiry: card.expiry, cvc: card.cvc, name: card.name },
        amount: bookingForPay.total,
        payerName: golfer.name, payerEmail: golfer.email,
      })
      if (r.status >= 400) throw new Error(r.data?.message || `Error ${r.status}`)
      onConfirmed({ ...bookingForPay })
    } catch (e2) { setErr(e2.message) }
    finally { setSubmitting(false) }
  }

  // ── Payment stage — reuse the package flow's PayScreen / Stripe wrapper ──
  if (stage === 'pay' && bookingForPay) {
    const total = bookingForPay.total
    if (bookingForPay.gateway === 'nmi' && bookingForPay.nmiTokenizationKey) {
      return (
        <NmiPayScreen
          api={api} booking={bookingForPay} total={total} currency={currency} golfer={golfer}
          onConfirmed={() => onConfirmed({ ...bookingForPay })}
          onBack={() => { setStage('pick'); setErr(null) }}
          primary={primary} branding={branding}
        />
      )
    }
    if (bookingForPay.gateway === 'stripe' && bookingForPay.stripePublishableKey) {
      return (
        <StripePayWrapper
          publishableKey={bookingForPay.stripePublishableKey}
          api={api} booking={bookingForPay} total={total} currency={currency} golfer={golfer}
          onConfirmed={() => onConfirmed({ ...bookingForPay })}
          onBack={() => { setStage('pick'); setErr(null) }}
          primary={primary} branding={branding}
        />
      )
    }
    if (bookingForPay.gateway === 'elavon_converge') {
      return <GatewayComingSoon gatewayLabel="Elavon Converge" onBack={() => { setStage('pick'); setErr(null) }} primary={primary} branding={branding} />
    }
    return (
      <PayScreen
        booking={bookingForPay} total={total} currency={currency}
        onPay={payCard} onBack={() => { setStage('pick'); setErr(null) }}
        submitting={submitting} err={err} primary={primary} branding={branding}
        heading={t('sp_tt_payment_heading', 'Pay for your tee time')}
      />
    )
  }

  // ── Pick stage ──
  const cardStyle = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, marginBottom: 18 }
  return (
    <div>
      <button type="button" onClick={onBack} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, marginBottom: 12, fontSize: 13 }}>{t('sp_back', '← Back')}</button>
      <h1 style={{ margin: '0 0 4px', fontSize: 24, ...headerFontStyle(branding) }}>{t('sp_tt_flow_title', 'Book a tee time')}</h1>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 20 }}>
        {t('sp_tt_flow_sub', 'Pick a course, date and time. Live availability, priced per golfer.')}
      </p>

      {loadErr && <div style={errorBox}>{loadErr}</div>}
      {courses === null && <Loading />}
      {courses && courses.length === 0 && !loadErr && (
        <div style={empty}>{t('sp_tt_no_courses', 'No courses are available for tee-time booking right now.')}</div>
      )}

      {courses && courses.length > 0 && (
        <>
          {/* Search controls — course, date, party size in one card */}
          <div style={cardStyle}>
            <div style={{ ...row, flexWrap: 'wrap', marginBottom: 18 }}>
              <Field label={t('sp_tt_date', 'Date')} style={{ flex: '1 1 200px', minWidth: 0, marginBottom: 0 }}>
                <input type="date" value={date} min={minDate} onChange={e => setDate(e.target.value)} style={inp} />
              </Field>
              <Field label={t('sp_tt_golfers', 'Golfers')} style={{ flex: '0 0 130px', marginBottom: 0 }}>
                <select value={golfers} onChange={e => setGolfers(Number(e.target.value))} style={inp}>
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </Field>
            </div>
            <Section title={t('sp_tt_choose_course', 'Choose a course')}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(() => {
                  const pill = (c) => {
                    const active = c.coursesId === coursesId
                    return (
                      <button key={c.coursesId} type="button" onClick={() => setCoursesId(c.coursesId)} style={{
                        padding: '9px 16px', borderRadius: 999,
                        background: active ? primary : '#fff', color: active ? '#fff' : '#0f172a',
                        border: '1px solid ' + (active ? primary : '#e2e8f0'),
                        fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        boxShadow: active ? '0 1px 3px rgba(0,0,0,0.12)' : 'none',
                      }}>
                        {c.name}{c.city ? <span style={{ opacity: 0.75, fontWeight: 400 }}> · {c.city}</span> : null}
                      </button>
                    )
                  }
                  const defCourse = defaultCourseId ? courses.find(c => c.coursesId === defaultCourseId) : null
                  // No default → flat alphabetical list (today's behavior).
                  if (!defCourse) return courses.map(pill)
                  // Default set → featured course on its own row, then an
                  // "Other courses" break, then the rest. The full-width
                  // spacer + label force line breaks inside the flex-wrap row.
                  const others = courses.filter(c => c.coursesId !== defaultCourseId)
                  return (
                    <>
                      {pill(defCourse)}
                      {others.length > 0 && (
                        <>
                          <div style={{ flexBasis: '100%', height: 0 }} />
                          <div style={{ flexBasis: '100%', display: 'flex', alignItems: 'center', gap: 10, margin: '14px 0 4px' }}>
                            <span style={{ fontSize: 12, fontWeight: 700, color: '#334155', textTransform: 'uppercase', letterSpacing: 0.6, whiteSpace: 'nowrap' }}>
                              {t('sp_tt_other_courses', 'Other courses')}
                            </span>
                            <span style={{ flex: 1, height: 1, background: '#e2e8f0' }} />
                          </div>
                          {others.map(pill)}
                        </>
                      )}
                    </>
                  )
                })()}
              </div>
            </Section>
          </div>

          {/* Available tee times */}
          <div style={cardStyle}>
            <Section title={t('sp_pick_your_tee_time', 'Pick a tee time')} subtitle={selCourse ? `${selCourse.name}${selCourse.city ? ' · ' + selCourse.city : ''} — ${friendlyDate(date)}` : null}>
              <TeeTimeSlots
                api={api} track={fire} surface="teetime" coursesId={coursesId} date={date} golfers={golfers}
                selected={selected} onSelect={setSelected}
                primary={primary} branding={branding} currency={currency} internalViewer={internalViewer}
              />
            </Section>
          </div>

          {/* Selected-slot summary + golfer form */}
          {selected && (
            <div style={{ ...cardStyle, borderColor: primary, borderWidth: 1, boxShadow: '0 2px 10px rgba(0,0,0,0.06)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', paddingBottom: 14, marginBottom: 14, borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 16, fontWeight: 700, ...headerFontStyle(branding) }}>
                    {selCourse?.name} · {formatTeeTime(selected.teeTime)}
                  </div>
                  <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
                    {friendlyDate(date)} · {golfers} {golfers === 1 ? t('sp_golfer_lower', 'golfer') : t('sp_golfers_lower', 'golfers')}
                    {perGolfer != null && <> · {fmtMoney(perGolfer, currency)}/{t('sp_golfer_lower', 'golfer')}</>}
                  </div>
                </div>
                {subtotal != null && (
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 22, fontWeight: 800, color: primary, lineHeight: 1 }}>{fmtMoney(subtotal, currency)}</div>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>{t('sp_tt_tax_note', 'Taxes calculated at checkout')}</div>
                  </div>
                )}
              </div>

              <Field label={t('sp_full_name', 'Full name')}>
                <input type="text" value={golfer.name} onChange={e => setGolfer(g => ({ ...g, name: e.target.value }))} style={inp} />
              </Field>
              <div style={row}>
                <Field label={t('sp_email', 'Email')} style={{ flex: 1 }}>
                  <input type="email" value={golfer.email} onChange={e => setGolfer(g => ({ ...g, email: e.target.value }))} style={inp} />
                </Field>
                <Field label={t('sp_phone', 'Phone')} style={{ flex: 1 }}>
                  <input type="tel" value={golfer.phone} onChange={e => setGolfer(g => ({ ...g, phone: formatPhone(e.target.value) }))} style={inp} />
                </Field>
              </div>

              {err && <div style={errorBox}>{err}</div>}

              <button type="button" disabled={!formReady || submitting} onClick={createBooking} style={{
                width: '100%', padding: 15, marginTop: 6, background: (!formReady || submitting) ? '#cbd5e1' : primary,
                color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700,
                cursor: (!formReady || submitting) ? 'not-allowed' : 'pointer',
              }}>
                {submitting
                  ? t('sp_please_wait', 'Please wait…')
                  : `${t('sp_tt_continue_to_payment', 'Continue to payment')}${subtotal != null ? ` · ${fmtMoney(subtotal, currency)}` : ''}`}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// Slot picker for the standalone tee-time flow. Hits the same tee-times
// endpoint as the package picker but with ?teeTimeOnly=1 so the backend
// exposes the per-golfer price (there's no bundle to hide it behind).
function TeeTimeSlots({ api, coursesId, date, golfers, selected, onSelect, primary, branding, currency, internalViewer, track, surface = 'teetime' }) {
  const fire = track || (() => {})
  const { t } = useLanguage()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [bucket, setBucket] = useState('morning')

  useEffect(() => {
    if (!coursesId || !date) { setData(null); return }
    let cancelled = false
    setLoading(true); setErr(null)
    const params = new URLSearchParams({ date, golfers: String(golfers || 1), teeTimeOnly: '1' })
    if (internalViewer) params.set('debug', '1')
    api.get(`/courses/${coursesId}/tee-times?${params.toString()}`).then(r => {
      if (cancelled) return
      if (r.status >= 400) setErr(r.data?.message || `Error ${r.status}`)
      else setData(r.data || { slots: [], upgrades: [] })
    }).catch(e => { if (!cancelled) setErr(e.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [api, coursesId, date, golfers, internalViewer])

  if (loading) return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#64748b' }}>
      <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid #cbd5e1', borderTopColor: primary, display: 'inline-block', animation: 'ig-spin 0.8s linear infinite' }} />
      {t('sp_looking_up_tee_times', 'Looking up available tee times…')}
      <style>{`@keyframes ig-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
  if (err) return <div style={{ ...errorBox, marginTop: 0 }}>{err}</div>
  if (!data) return null
  if (data.fallback) {
    return (
      <div style={{ padding: 12, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
        {data.message || t('sp_tt_live_unavailable', 'Live tee-time search is currently unavailable for this course.')} {t('sp_tt_enter_preferred', "Enter a preferred time and we'll confirm by email:")}
        <input type="time" value={selected?.teeTime || ''} onChange={e => onSelect({ teeTime: e.target.value, ref: null })} style={{ ...inp, width: 140, marginTop: 8 }} />
      </div>
    )
  }

  // teeTimeOnly=1 => no package cap, so every slot returns in `slots`.
  const slots = [...(data.slots || []), ...(data.upgrades || [])]
  if (slots.length === 0) {
    return <p style={{ ...muted, fontSize: 13 }}>{t('sp_tt_none_on_date', 'No tee times available at this course on this date. Try another date or course.')}</p>
  }

  const byBucket = { early: [], morning: [], afternoon: [], late: [] }
  for (const s of slots) byBucket[bucketFor(s.teeTime)].push(s)
  let activeBucket = bucket
  if (byBucket[activeBucket].length === 0) {
    const first = TEE_BUCKETS.find(b => byBucket[b.id].length > 0)
    if (first) activeBucket = first.id
  }
  const distinct = new Set(slots.map(s => s.courseName).filter(Boolean))
  const showCourseName = distinct.size > 1
  const visible = byBucket[activeBucket] || []

  return (
    <div>
      {/* Time-of-day bucket selector — matches the package flow's picker:
          label + time range + count, so both pickers read identically. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {TEE_BUCKETS.map(b => {
          const count = byBucket[b.id].length
          const isActive = b.id === activeBucket
          const isEmpty = count === 0
          return (
            <button key={b.id} type="button" disabled={isEmpty} onClick={() => !isEmpty && setBucket(b.id)} style={{
              padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
              cursor: isEmpty ? 'not-allowed' : 'pointer',
              background: isActive ? primary : '#fff',
              color: isActive ? '#fff' : (isEmpty ? '#cbd5e1' : '#0f172a'),
              border: '1px solid ' + (isActive ? primary : '#e2e8f0'),
              display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
              ...bodyFontStyle(branding),
            }}>
              <span>{t(b.labelKey, b.label)}</span>
              <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.85 }}>
                {t(b.subKey, b.sub)}
                {!isEmpty
                  ? ` · ${t(count === 1 ? 'sp_time_count_singular' : 'sp_times_count', `{count} time${count === 1 ? '' : 's'}`, { count })}`
                  : (Number(golfers) > 1
                      ? ` · ${t('sp_none_with_seats', 'none with {count} seats', { count: golfers })}`
                      : ` · ${t('sp_none', 'none')}`)}
              </span>
            </button>
          )
        })}
      </div>

      {/* Tee-time chips — single-line pills like the package picker, with the
          per-golfer price as a small inline badge (this flow always shows price). */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {visible.map(s => {
          const isSel = selected?.ref === s.ref && selected?.teeTime === s.teeTime
          const price = s.pricePerGolfer != null ? fmtMoney(s.pricePerGolfer) : null
          return (
            <button key={(s.ref || '') + s.teeTime} type="button" onClick={() => { fire('round_select', surface, { coursesId, date }); onSelect(s) }} style={{
              padding: '8px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 500,
              background: isSel ? primary : '#fff', color: isSel ? '#fff' : '#0f172a',
              border: '1px solid ' + (isSel ? primary : '#e2e8f0'),
              display: 'inline-flex', alignItems: 'center', gap: 6,
              ...bodyFontStyle(branding),
            }}>
              <span style={{ fontWeight: 600 }}>{formatTeeTime(s.teeTime)}</span>
              {price && <span style={{ fontSize: 11, color: isSel ? 'rgba(255,255,255,0.85)' : '#64748b' }}>{price}</span>}
              {showCourseName && s.courseName && <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.8, color: isSel ? '#fff' : '#64748b' }}>{s.courseName}</span>}
            </button>
          )
        })}
      </div>
    </div>
  )
}

// Friendly "Sat, Jul 25" style date for the tee-time summary line.
function friendlyDate(iso) {
  if (!iso) return ''
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-CA', { weekday: 'short', month: 'short', day: 'numeric' })
}

// Fully spelled-out "Monday, September 7, 2026" — used on the checkout summary +
// policy agreements so the customer can't later dispute the month/year.
function longDate(iso) {
  if (!iso) return ''
  const d = new Date(String(iso).slice(0, 10) + 'T00:00:00')
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-CA', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
}

// ─── Build-your-own trip flow ──────────────────────────────────────────────
// Customer assembles a trip from scratch: dates + party -> hotel -> round(s) ->
// live price (server-computed: cost + reseller markup + InteGolf fee + tax) ->
// pay. Package-less booking via /build-your-own/bookings; payment reuses the
// same pay screens as the package + tee-time flows.
function BuildYourOwnFlow({ ctx, api, brand, branding, internalViewer, onBack, onConfirmed, track }) {
  const fire = track || (() => {})
  const { t } = useLanguage()
  const primary = branding?.primaryColor || brand?.primaryColor || '#0f172a'
  const currency = ctx?.reseller?.currency

  const [courses, setCourses] = useState(null)
  const [checkIn, setCheckIn] = useState(() => { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10) })
  const [nights, setNights] = useState(2)
  const [golfers, setGolfers] = useState(2)
  const [rooms, setRooms] = useState(1)
  const [hotel, setHotel] = useState(null)
  const [rounds, setRounds] = useState([{ coursesId: null, date: '', selected: null }])
  const [golfer, setGolfer] = useState({ name: '', email: '', phone: '' })
  const [agreed, setAgreed] = useState({}) // cancellation policy agreement, keyed by content

  const [price, setPrice] = useState(null)
  const [priceLoading, setPriceLoading] = useState(false)
  const [stage, setStage] = useState('build')
  const [bookingForPay, setBookingForPay] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)

  const checkOut = useMemo(() => {
    const d = new Date(checkIn + 'T00:00:00'); d.setDate(d.getDate() + (Number(nights) || 1))
    return d.toISOString().slice(0, 10)
  }, [checkIn, nights])

  useEffect(() => {
    let cancelled = false
    api.get('/tee-time/courses').then(r => { if (!cancelled) setCourses(r.status >= 400 ? [] : (r.data?.courses || [])) })
      .catch(() => { if (!cancelled) setCourses([]) })
    return () => { cancelled = true }
  }, [api])

  // Selection payload for both the price preview and the booking.
  const hotelPayload = hotel ? { costToken: hotel.costToken, rateKey: hotel.rateKey, name: hotel.name, externalId: hotel.externalId, source: hotel.source, hotelRef: hotel.hotelRef || null, priceCheckHref: hotel.priceCheckHref } : null
  const roundsPayload = rounds.filter(r => r.coursesId && r.selected).map(r => ({
    coursesId: r.coursesId, playDate: r.date || checkIn, teeTime: r.selected.teeTime, ref: r.selected.ref,
    courseName: (courses || []).find(c => c.coursesId === r.coursesId)?.name || null,
  }))

  // Live price whenever the selection changes (debounced).
  const priceKey = JSON.stringify({ g: golfers, rm: rooms, h: hotelPayload?.costToken || null, r: roundsPayload.map(r => [r.coursesId, r.teeTime, r.ref]) })
  useEffect(() => {
    // Only price once BOTH a hotel and a round are chosen — a hotel-only price
    // would expose standalone hotel pricing (not allowed outside a package).
    if (!hotelPayload || roundsPayload.length === 0) { setPrice(null); return }
    let cancelled = false
    setPriceLoading(true)
    const timer = setTimeout(() => {
      api.post('/build-your-own/price', { numGolfers: Number(golfers), numRooms: Number(rooms), hotel: hotelPayload, rounds: roundsPayload })
        .then(r => { if (!cancelled) setPrice(r.status >= 400 ? null : r.data) })
        .catch(() => { if (!cancelled) setPrice(null) })
        .finally(() => { if (!cancelled) setPriceLoading(false) })
    }, 350)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [api, priceKey]) // eslint-disable-line react-hooks/exhaustive-deps

  const allRoundsComplete = rounds.every(r => r.coursesId && r.selected)
  // A BYO trip is stay-AND-play: require a hotel + at least one round. This also
  // ensures we never show a standalone hotel price.
  const hasBoth = !!hotelPayload && roundsPayload.length > 0
  const golferOk = golfer.name.trim().length >= 2 && golfer.email.includes('@') && isValidPhone(golfer.phone)

  // Cancellation policies the customer must agree to (mirrors the package flow).
  // Keys are content-based, so changing a hotel room or a tee time un-agrees it.
  const policies = []
  if (hotel) {
    const roomLabel = [hotel.roomType, hotel.bedConfig].filter(Boolean).join(' · ')
    policies.push({
      key: `hotel_${hotel.rateKey || hotel.name}`,
      label: `${t('sp_byo_policy_hotel', 'Hotel')} — ${hotel.name}`,
      sub: roomLabel ? (Number(rooms) > 1 ? t('sp_rooms_label', '{count} Rooms: {room}', { count: rooms, room: roomLabel }) : t('sp_room_label', 'Room: {room}', { room: roomLabel })) : null,
      text: hotel.refundable
        ? `${t('sp_byo_free_cancel', 'Free cancellation')}${hotel.cancellationDeadline ? ` ${t('sp_until', 'until')} ${longDate(hotel.cancellationDeadline)}` : ''}.`
        : t('sp_byo_nonref', 'Non-refundable. If you cancel, no refund will be issued for the hotel portion.'),
    })
  }
  rounds.filter(r => r.coursesId && r.selected).forEach(r => {
    const cname = (courses || []).find(c => c.coursesId === r.coursesId)?.name || t('sp_byo_round_generic', 'Round')
    policies.push({
      key: `round_${r.coursesId}_${r.date || checkIn}_${r.selected.teeTime}`,
      label: `${cname} — ${longDate(r.date || checkIn)}`,
      text: t('sp_cancellation_default_round', "Subject to the course's standard policy — usually 24-48 hour notice required."),
    })
  })
  const allAgreed = policies.length > 0 && policies.every(p => agreed[p.key])

  const formReady = hasBoth && allRoundsComplete && golferOk && !!checkIn && !!price && !priceLoading && allAgreed

  function patchRound(i, patch) { setRounds(rs => rs.map((r, j) => j === i ? { ...r, ...patch } : r)) }

  async function createBooking() {
    setErr(null); setSubmitting(true)
    try {
      const { data, status } = await api.post('/build-your-own/bookings', {
        golfer, checkInDate: checkIn, checkOutDate: checkOut,
        numGolfers: Number(golfers), numRooms: Number(rooms),
        hotel: hotelPayload, rounds: roundsPayload,
        agreedPolicies: Object.keys(agreed).filter(k => agreed[k]),
      })
      if (status >= 400) throw new Error(data?.message || `Error ${status}`)
      fire('checkout_start', 'byo', { numGolfers: Number(golfers), rounds: roundsPayload.length, hasHotel: !!hotelPayload })
      setBookingForPay(data); setStage('pay')
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch (e2) { setErr(e2.message) }
    finally { setSubmitting(false) }
  }

  async function payCard(card) {
    setErr(null); setSubmitting(true)
    try {
      const r = await api.post(`/bookings/${bookingForPay.bookingId}/payments`, {
        card: { number: card.number, expiry: card.expiry, cvc: card.cvc, name: card.name },
        amount: bookingForPay.total, payerName: golfer.name, payerEmail: golfer.email,
      })
      if (r.status >= 400) throw new Error(r.data?.message || `Error ${r.status}`)
      onConfirmed({ ...bookingForPay })
    } catch (e2) { setErr(e2.message) }
    finally { setSubmitting(false) }
  }

  // ── Payment stage (reuse the shared pay screens) ──
  if (stage === 'pay' && bookingForPay) {
    const total = bookingForPay.total
    const summary = (
      <OrderSummary
        title={t('sp_byo_card_title', 'Build your own trip')}
        lines={[
          hotel?.name ? `🏨 ${hotel.name}` : null,
          hotel?.name ? `${t('sp_checkin', 'Check-in')}: ${longDate(checkIn)}` : null,
          hotel?.name ? `${t('sp_checkout', 'Check-out')}: ${longDate(checkOut)}` : null,
          `${golfers} ${Number(golfers) === 1 ? t('sp_golfer', 'golfer') : t('sp_golfers', 'golfers')} · ${rooms} ${Number(rooms) === 1 ? t('sp_room', 'room') : t('sp_rooms', 'rooms')}`,
          ...roundsPayload.map(r => `⛳ ${golfers} ${Number(golfers) === 1 ? t('sp_byo_round_word', 'round') : t('sp_byo_rounds_word', 'rounds')} @ ${r.courseName || t('sp_byo_round_generic', 'Round')} · ${longDate(r.playDate)}${r.teeTime ? ` - ${formatTeeTime(r.teeTime)} ${t('sp_byo_teetime', 'Tee-Time')}` : ''}`),
        ]}
        subtotal={bookingForPay.pricing?.subtotal}
        tax={bookingForPay.pricing?.tax}
        taxRate={bookingForPay.pricing?.taxRate}
        total={total}
        currency={currency}
        branding={branding}
      />
    )
    if (bookingForPay.gateway === 'nmi' && bookingForPay.nmiTokenizationKey) {
      return <NmiPayScreen api={api} booking={bookingForPay} total={total} currency={currency} golfer={golfer} summary={summary}
        onConfirmed={() => onConfirmed({ ...bookingForPay })} onBack={() => { setStage('build'); setErr(null) }} primary={primary} branding={branding} />
    }
    if (bookingForPay.gateway === 'stripe' && bookingForPay.stripePublishableKey) {
      return <StripePayWrapper publishableKey={bookingForPay.stripePublishableKey} api={api} booking={bookingForPay} total={total} currency={currency} golfer={golfer} summary={summary}
        onConfirmed={() => onConfirmed({ ...bookingForPay })} onBack={() => { setStage('build'); setErr(null) }} primary={primary} branding={branding} />
    }
    if (bookingForPay.gateway === 'elavon_converge') {
      return <GatewayComingSoon gatewayLabel="Elavon Converge" onBack={() => { setStage('build'); setErr(null) }} primary={primary} branding={branding} />
    }
    return <PayScreen booking={bookingForPay} total={total} currency={currency} onPay={payCard} onBack={() => { setStage('build'); setErr(null) }}
      submitting={submitting} err={err} primary={primary} branding={branding} heading={t('sp_byo_pay_heading', 'Pay for your trip')} summary={summary} />
  }

  // ── Build stage ──
  const cardStyle = { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 18, marginBottom: 18 }
  const lbl = { fontSize: 12, fontWeight: 600, color: '#475569', display: 'block', marginBottom: 4 }
  const inp2 = { padding: '9px 11px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, width: '100%', boxSizing: 'border-box' }
  return (
    <div>
      <button type="button" onClick={onBack} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, marginBottom: 12, fontSize: 13 }}>{t('sp_back', '← Back')}</button>
      <h1 style={{ margin: '0 0 4px', fontSize: 24, ...headerFontStyle(branding) }}>{t('sp_byo_card_title', 'Build your own trip')}</h1>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 20 }}>{t('sp_byo_card_sub', 'Pick your hotel and rounds - pay only for what you choose.')}</p>

      {/* Trip basics */}
      <div style={cardStyle}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 12 }}>
          <div><label style={lbl}>{t('sp_byo_checkin', 'Check-in')}</label><input type="date" min={new Date().toISOString().slice(0, 10)} value={checkIn} onChange={e => { setCheckIn(e.target.value); setHotel(null); setPrice(null) }} style={inp2} /></div>
          <div><label style={lbl}>{t('sp_byo_nights', 'Nights')}</label><input type="number" min={1} max={30} value={nights} onChange={e => { setNights(e.target.value); setHotel(null); setPrice(null) }} style={inp2} /></div>
          <div><label style={lbl}>{t('sp_byo_golfers', 'Golfers')}</label><input type="number" min={1} max={32} value={golfers} onChange={e => setGolfers(e.target.value)} style={inp2} /></div>
          <div><label style={lbl}>{t('sp_byo_rooms', 'Rooms')}</label><input type="number" min={1} max={16} value={rooms} onChange={e => { setRooms(e.target.value); setHotel(null); setPrice(null) }} style={inp2} /></div>
        </div>
      </div>

      {/* Hotel */}
      <div style={cardStyle}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, ...headerFontStyle(branding) }}>{t('sp_byo_hotel', 'Choose your hotel')}</div>
        <HotelPicker api={api} track={fire} surface="byo" checkin={checkIn} nights={Number(nights) || 1} numRooms={Number(rooms) || 1}
          maxNightly={undefined} packageId={undefined} selected={hotel}
          onSelect={(h, r) => setHotel(h ? {
            externalId: h.propertyId, source: r?.provider || h.provider || 'expedia', hotelRef: r?.hotelRef || null, name: h.name, starRating: h.starRating, image: h.image,
            rateKey: r?.rateKey, priceCheckHref: r?.priceCheckHref, costToken: r?.costToken, roomType: r?.roomType,
            bedConfig: r?.bedConfig, refundable: r?.refundable, cancellationDeadline: r?.cancellationDeadline,
          } : null)}
          primary={primary} branding={branding} />
      </div>

      {/* Rounds */}
      <div style={cardStyle}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, ...headerFontStyle(branding) }}>{t('sp_byo_rounds', 'Add your rounds')}</div>
        {rounds.map((rd, i) => (
          <div key={i} style={{ border: '1px solid #eef2f7', borderRadius: 10, padding: 12, marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <span style={{ fontSize: 12, fontWeight: 700, color: '#64748b' }}>{t('sp_byo_round_n', 'Round {n}', { n: i + 1 })}</span>
              {rounds.length > 1 && <button type="button" onClick={() => setRounds(rs => rs.filter((_, j) => j !== i))} style={{ border: 'none', background: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 12 }}>{t('sp_remove', 'Remove')}</button>}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 150px', gap: 8, marginBottom: 8 }}>
              <select value={rd.coursesId || ''} onChange={e => patchRound(i, { coursesId: e.target.value ? Number(e.target.value) : null, selected: null })} style={inp2}>
                <option value="">{t('sp_byo_pick_course', 'Select a course…')}</option>
                {(courses || []).map(c => <option key={c.coursesId} value={c.coursesId}>{c.name}{c.city ? ` — ${c.city}` : ''}</option>)}
              </select>
              <input type="date" min={checkIn} value={rd.date || checkIn} onChange={e => patchRound(i, { date: e.target.value, selected: null })} style={inp2} />
            </div>
            {rd.coursesId && (
              <TeeTimePicker api={api} track={fire} surface="byo" packageId={undefined} coursesId={rd.coursesId} date={rd.date || checkIn} golfers={Number(golfers) || 1}
                selected={rd.selected} onSelect={(slot) => patchRound(i, { selected: slot })} primary={primary} branding={branding} internalViewer={internalViewer} />
            )}
          </div>
        ))}
        <button type="button" onClick={() => setRounds(rs => {
            // Default the new round to the day AFTER the previous round (most
            // trips play a different course each day), clamped to the stay.
            const last = rs[rs.length - 1]
            const base = (last?.date || checkIn)
            const d = new Date(base + 'T00:00:00'); d.setDate(d.getDate() + 1)
            let next = d.toISOString().slice(0, 10)
            if (checkOut && next > checkOut) next = checkOut
            return [...rs, { coursesId: null, date: next, selected: null }]
          })}
          style={{ padding: '7px 12px', background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>
          + {t('sp_byo_add_round', 'Add a round')}
        </button>
      </div>

      {/* Golfer */}
      <div style={cardStyle}>
        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, ...headerFontStyle(branding) }}>{t('sp_byo_your_details', 'Your details')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 10 }}>
          <div><label style={lbl}>{t('sp_name', 'Name')}</label><input value={golfer.name} onChange={e => setGolfer(g => ({ ...g, name: e.target.value }))} style={inp2} /></div>
          <div><label style={lbl}>{t('sp_email', 'Email')}</label><input type="email" value={golfer.email} onChange={e => setGolfer(g => ({ ...g, email: e.target.value }))} style={inp2} /></div>
          <div><label style={lbl}>{t('sp_phone', 'Phone')}</label><input value={golfer.phone} onChange={e => setGolfer(g => ({ ...g, phone: formatPhone(e.target.value) }))} style={inp2} /></div>
        </div>
      </div>

      {/* Cancellation policies — review + agree to each (mirrors the package flow) */}
      {policies.length > 0 && (
        <div style={cardStyle}>
          <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10, ...headerFontStyle(branding) }}>
            {t('sp_cancellation_policies_review_agree', 'Cancellation policies — please review and agree to each')}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {policies.map(p => (
              <label key={p.key} style={{ background: agreed[p.key] ? '#f0fdf4' : '#fff', border: '1px solid ' + (agreed[p.key] ? '#86efac' : '#e2e8f0'), borderRadius: 8, padding: 12, display: 'flex', gap: 12, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!agreed[p.key]} onChange={e => setAgreed(a => ({ ...a, [p.key]: e.target.checked }))} style={{ marginTop: 3, flexShrink: 0 }} />
                <div style={{ flex: 1, fontSize: 13 }}>
                  <div style={{ fontWeight: 600 }}>{p.label}</div>
                  {p.sub && <div style={{ color: '#0f172a', marginTop: 2, fontWeight: 500 }}>{p.sub}</div>}
                  <div style={{ color: '#475569', marginTop: 2 }}>{p.text}</div>
                  <div style={{ color: '#64748b', marginTop: 4, fontSize: 12, fontStyle: 'italic' }}>{t('sp_i_accept_policy', 'I have read and accept this policy.')}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      )}

      {err && <div style={errorBox}>{err}</div>}
      {hasBoth && !allAgreed && policies.length > 0 && (
        <div style={{ fontSize: 12.5, color: '#b45309', marginBottom: 8 }}>{t('sp_byo_agree_hint', 'Please agree to the cancellation policies above to continue.')}</div>
      )}

      {/* Price + continue */}
      <div style={{ ...cardStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', position: 'sticky', bottom: 0 }}>
        <div>
          {!hasBoth ? (
            <div style={{ fontSize: 13, color: '#64748b', maxWidth: 360, lineHeight: 1.4 }}>
              {t('sp_byo_need_both', 'Pick a hotel and at least one round to see your price.')}
            </div>
          ) : priceLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#475569', fontSize: 14, fontWeight: 600 }}>
              <span style={{ width: 16, height: 16, borderRadius: '50%', border: '2px solid #cbd5e1', borderTopColor: primary, display: 'inline-block', animation: 'ig-spin 0.8s linear infinite' }} />
              {t('sp_byo_checking_price', 'Checking prices…')}
              <style>{`@keyframes ig-spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : price ? (
            <>
              <div style={{ fontSize: 12, color: '#64748b' }}>{t('sp_byo_your_price', 'Your price')}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#0f172a' }}>{fmtMoney(price.total, currency)}</div>
              <div style={{ fontSize: 12, color: '#64748b' }}>{fmtMoney(price.perGolfer, currency)}/golfer · {t('sp_byo_incl_tax', 'incl. tax')}</div>
            </>
          ) : (
            <div style={{ fontSize: 13, color: '#64748b' }}>{t('sp_byo_price_retry', 'Price unavailable — adjust your selection and try again.')}</div>
          )}
        </div>
        <button type="button" disabled={!formReady || submitting} onClick={createBooking} style={{
          padding: '12px 22px', fontSize: 15, fontWeight: 700, borderRadius: 10, border: 'none',
          background: (!formReady || submitting) ? '#94a3b8' : primary, color: '#fff', cursor: (!formReady || submitting) ? 'default' : 'pointer',
        }}>{submitting ? t('sp_byo_working', 'Working…') : t('sp_byo_continue', 'Continue to payment')} →</button>
      </div>
    </div>
  )
}

// ─── Booking flow ────────────────────────────────────────────────────────

function BookingFlow({ detail, ctx, api, brand, branding, onBack, onConfirmed, resumeState, resumeError, internalViewer, track }) {
  const fire = track || (() => {})
  const { t, langId } = useLanguage()
  // Availability check for resumed trips — populated on review-stage mount.
  // null = not yet checked. Each entry: { roundIndex, available, alternatives }.
  const [availability, setAvailability] = useState(null)
  const [availabilityErr, setAvailabilityErr] = useState(null)
  const [checkingAvail, setCheckingAvail] = useState(false)
  // Initial form. When resumeState is present, hydrate from the saved
  // 'building' booking (customer is coming back via the
  // /customer-portal "Continue this trip" link).
  const [form, setForm] = useState(() => {
    // Read numGolfers first (explicit per-package party size), fall back
    // to max/min only for back-compat with older payloads. Without this
    // the embed would silently size everything for 2 golfers on packages
    // where the reseller picked 4+ — addons, totals, tee-time slot count.
    const packageGolfers =
      detail?.package?.numGolfers ||
      detail?.package?.maxGolfers ||
      detail?.package?.minGolfers ||
      2
    if (resumeState?.form) {
      const rs = resumeState.form
      return {
        checkInDate: rs.checkInDate || '',
        numGolfers:  rs.numGolfers || packageGolfers,
        numRooms:    rs.numRooms || detail?.package?.roomCount || 1,
        rounds:      Array.isArray(rs.rounds) ? rs.rounds : [],
        hotel:       rs.hotel || null,
        addons:      rs.addons || [],
        golfer:      rs.golfer || { name: '', email: '', phone: '' },
      }
    }
    return {
      checkInDate: '',
      // Group size + rooms come from the package — fixed by the reseller,
      // not editable by the customer.
      numGolfers: packageGolfers,
      numRooms: detail?.package?.roomCount || 1,
      rounds: [], hotel: null, addons: [],
      golfer: { name: '', email: '', phone: '' },
    }
  })
  // Sync party size + rooms once `detail` loads (or when the customer
  // navigates between packages). The useState initializer above runs
  // BEFORE detail finishes loading, so without this effect form.numGolfers
  // would stay at the 2-golfer fallback forever, ignoring the package's
  // configured party size.
  useEffect(() => {
    if (!detail?.package) return
    const desiredGolfers =
      detail.package.numGolfers ||
      detail.package.maxGolfers ||
      detail.package.minGolfers ||
      null
    const desiredRooms = detail.package.roomCount || null
    setForm(f => {
      const next = { ...f }
      let changed = false
      // Don't trample a resumed customer's saved choice (resumeState path).
      if (desiredGolfers && Number(f.numGolfers) !== Number(desiredGolfers) && !resumeState?.form) {
        next.numGolfers = desiredGolfers; changed = true
      }
      if (desiredRooms && Number(f.numRooms) !== Number(desiredRooms) && !resumeState?.form) {
        next.numRooms = desiredRooms; changed = true
      }
      return changed ? next : f
    })
  }, [detail?.package?.id, detail?.package?.numGolfers, detail?.package?.maxGolfers, detail?.package?.minGolfers, detail?.package?.roomCount, resumeState])
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)
  // Two-stage submit: 'building' (current form) -> 'review' (read-only
  // summary + per-component agreement checkboxes) -> POST. Per Pass D
  // Q12, customer must tick every component policy before paying.
  // Resume mode lands directly on 'review' since the form is already
  // populated from the saved booking.
  const [stage, setStage] = useState(resumeState ? 'review' : 'building')
  const [agreed, setAgreed] = useState({}) // { hotel: bool, round_0: bool, ... }
  const [bookingForPay, setBookingForPay] = useState(null) // { bookingId, magicToken, ... } after POST /bookings
  // Promo code (reseller-created). Validated live against the server before pay;
  // the discount is applied pre-tax, exactly as the server recomputes it.
  const [promoInput, setPromoInput] = useState('')
  const [appliedPromo, setAppliedPromo] = useState(null) // { code, benefitType, discount, addonType, addonLabel }
  const [promoErr, setPromoErr] = useState(null)
  const [promoBusy, setPromoBusy] = useState(false)

  // Split-pay (Phase B): 'full' pays the whole total on one card; 'split' lets
  // the leader divide the trip among golfers, invite them to pay their share,
  // and pay their own. Only offered when the package allows it (+ Moneris).
  const [paymentPlan, setPaymentPlan] = useState('full') // 'full' | 'split'
  const [splitSetupDone, setSplitSetupDone] = useState(false)
  const [leaderShare, setLeaderShare] = useState(null)

  // Wizard step inside the building stage. Mirrors the Stay & Play
  // /embed/stay-play multi-step flow: dates -> hotel -> tee times ->
  // contact + add-ons. The Review-and-agree screen is its own stage.
  // Step dots render at the top so customer always sees progress.
  const [wizardStep, setWizardStep] = useState(1)
  const TOTAL_STEPS = 4

  // When a resume lands on the review stage, verify each saved tee
  // time is still bookable. Surfaces a banner + per-round badges so
  // the customer isn't blindsided by a stale-slot booking failure
  // at payment time. Runs once per review-stage entry from resume.
  useEffect(() => {
    if (!resumeState || stage !== 'review' || availability !== null || checkingAvail) return
    setCheckingAvail(true); setAvailabilityErr(null)
    api.get(`/bookings/${resumeState.bookingId}/availability?token=${encodeURIComponent(new URLSearchParams(window.location.search).get('resumeToken') || '')}`)
      .then(r => {
        if (r.status >= 400) {
          setAvailabilityErr(r.data?.message || 'Could not verify tee-time availability')
        } else {
          setAvailability(r.data.rounds)
        }
      })
      .catch(e => setAvailabilityErr(e.message))
      .finally(() => setCheckingAvail(false))
  }, [resumeState, stage, availability, checkingAvail, api])

  // Margin-cap preview. The browser can't compute the discount (it never knows
  // the hotel/golf COSTS, only the fixed package price), so ask the server
  // whenever the selection is complete. Display-only — the charge recomputes
  // authoritatively at booking. Returns null unless a cap actually applies.
  const [capPreview, setCapPreview] = useState(null)
  const [capLoading, setCapLoading] = useState(false)
  // Safety valve: never let a slow/cold-cache preview hard-block checkout. After
  // this many ms we stop gating Continue on the preview — the authoritative
  // recompute at booking still applies the cap, so proceeding early only risks
  // briefly showing the un-discounted total (the customer is then charged less).
  const CAP_PREVIEW_MAX_WAIT_MS = 6000
  const [capMaxWaitReached, setCapMaxWaitReached] = useState(false)
  useEffect(() => {
    const pkgSlug = detail?.package?.slug
    const capEnabled = !!detail?.package?.maxRevenueCapEnabled
    const nights = Number(detail?.package?.nights) || 1
    const ready = pkgSlug && form.hotel && Array.isArray(form.rounds) && form.rounds.length > 0 && form.checkInDate
    // Only packages with a cap trigger a preview (and the "searching" gate).
    if (!ready || !capEnabled) { setCapPreview(null); setCapLoading(false); setCapMaxWaitReached(false); return }
    const co = new Date(new Date(form.checkInDate).getTime() + nights * 86400000).toISOString().slice(0, 10)
    let alive = true
    setCapLoading(true)          // show the "searching for best price" indicator immediately
    setCapMaxWaitReached(false)  // reset the safety valve for this selection
    const hardStop = setTimeout(() => { if (alive) setCapMaxWaitReached(true) }, CAP_PREVIEW_MAX_WAIT_MS)
    const timer = setTimeout(() => {
      api.post(`/packages/${pkgSlug}/price-preview`, {
        hotel: form.hotel, rounds: form.rounds,
        numGolfers: Number(form.numGolfers) || 1, numRooms: Number(form.numRooms) || 1,
        checkInDate: form.checkInDate, checkOutDate: co,
      }).then(r => { if (!alive) return; setCapPreview(r.status < 400 && r.data?.capApplied ? r.data : null); setCapLoading(false) })
        .catch(() => { if (!alive) return; setCapPreview(null); setCapLoading(false) })
    }, 350)
    return () => { alive = false; clearTimeout(timer); clearTimeout(hardStop) }
  }, [detail, form.hotel, form.rounds, form.numGolfers, form.numRooms, form.checkInDate, api])

  function applyAlternative(roundIndex, alt) {
    setForm(f => ({
      ...f,
      rounds: f.rounds.map((r, i) => i === roundIndex
        ? {
            ...r,
            teeTime: alt.teeTime + ':00',
            ref: alt.ref || null,
            // Upgrade-tier alternatives carry a price-delta that flows
            // into the trip total via the existing roundsUpgrade reducer.
            upgradePerGolfer: alt.inBudget ? null : alt.upgradePerGolfer,
            upgradeTotal:     alt.inBudget ? null : alt.upgradeTotal,
          }
        : r),
    }))
    // Optimistically clear the warning for this round.
    setAvailability(prev => prev?.map(p =>
      p.roundIndex === roundIndex ? { ...p, available: true, alternatives: [] } : p
    ))
  }

  // Auto-init / re-sync rounds based on the package's RoundsPerGolfer +
  // course list + the customer's current check-in date. Three triggers:
  //   1. Initial population (rounds.length === 0)  — happens first time
  //      check-in is set.
  //   2. Check-in changes after rounds were populated — re-base each
  //      round's playDate to (newCheckInDate + i). Tee-time picks are
  //      cleared since they're date-specific.
  //   3. Course list / roundsPerGolfer change — handled by initial pop.
  // Must come BEFORE any early-return so hook order is stable.
  useEffect(() => {
    if (!detail?.package) return
    const totalRounds = Number(detail.package.roundsPerGolfer || 1)
    const courses = detail.courses || []
    if (!form.checkInDate || courses.length === 0) return

    // Path 1: first-time population. Required courses always get a slot
    // first (one round each, in package SortOrder); remaining slots
    // (totalRounds - requiredCount) get filled by non-required courses
    // rotating through. If the reseller has configured MORE required
    // courses than rounds in the package, we'd still pin every required
    // one — the customer can drop hours from a 2-round package into a
    // 3-required package later, but at least nothing required is hidden.
    if (form.rounds.length === 0) {
      const required = courses.filter(c => c.isRequired)
      const optional = courses.filter(c => !c.isRequired)
      const initialRounds = []
      // Required first. `required: true` PINS the round to this course (shown
      // as locked static text). This is a per-round designation, NOT derived
      // from the selected course — so choosing the required course again on a
      // free round doesn't make it "required" on a second day.
      for (const c of required) {
        initialRounds.push({
          coursesId: c.coursesId,
          courseName: c.courseName,
          required: true,
          playDate: addDays(form.checkInDate, initialRounds.length),
          teeTime: '', ref: null,
          cancellationPolicy: null, upgradePerGolfer: null, upgradeTotal: null,
        })
      }
      // Fill the remaining slots with non-required courses (rotating
      // through the available list — usually only one, often more on
      // multi-course packages).
      const remaining = Math.max(0, totalRounds - initialRounds.length)
      const fillSource = optional.length > 0 ? optional : courses
      for (let i = 0; i < remaining; i++) {
        const c = fillSource[i % fillSource.length]
        initialRounds.push({
          coursesId: c.coursesId,
          courseName: c.courseName,
          required: false,
          playDate: addDays(form.checkInDate, initialRounds.length),
          teeTime: '', ref: null,
          cancellationPolicy: null, upgradePerGolfer: null, upgradeTotal: null,
        })
      }
      setForm(f => ({ ...f, rounds: initialRounds }))
      return
    }

    // Path 2: re-sync existing rounds when check-in shifted. Detect by
    // comparing round 0's playDate to checkInDate; if they don't match,
    // roll the whole sequence forward.
    const expectedRound0 = form.checkInDate
    if (form.rounds[0]?.playDate !== expectedRound0) {
      setForm(f => ({
        ...f,
        rounds: f.rounds.map((r, i) => ({
          ...r,
          playDate: addDays(form.checkInDate, i),
          // Tee-time picks are date-specific — clear them so the
          // customer re-picks against the new date's availability.
          teeTime: '',
          ref: null,
          upgradePerGolfer: null,
          upgradeTotal: null,
        })),
      }))
    }
  }, [form.checkInDate, detail?.courses, detail?.package?.roundsPerGolfer])

  if (!detail || !detail.package) return <Loading />

  const checkOut = form.checkInDate ? addDays(form.checkInDate, detail.package.nights) : ''

  // ─── Customer-facing totals ────────────────────────────────────────────
  // Reseller package model: pkg.Price IS the bundle total (hotel + rounds
  // baked in). Customer pays Price + add-ons + taxes. Hotel/golf line items
  // shown in the editor's worst-case breakdown are NOT exposed here — they
  // already live inside Price from the customer's perspective.
  const subtotal = Number(detail.package.price) || 0
  const totalAddons = form.addons.reduce((sum, a) => sum + Number(a.priceTotal || 0), 0)
  // Hotel upgrade delta: if customer picked an above-budget hotel, add
  // the cost difference (vs the package's max nightly) for all nights.
  const hotelUpgrade = Number(form.hotel?.upgradeTotal) || 0
  // Round upgrades: each picked tee time may be a "premium" slot above
  // the per-course max — we add per-round upgradeTotal (already
  // multiplied by golfers backend-side).
  // roundsUpgrade only counts in-package rounds. Extras have their own
  // line below; the slot's upgrade fee is folded into extraRoundsCost
  // when the course is set to "Bill extras at full price".
  const allowedRounds = Number(detail.package.roundsPerGolfer || 1)
  const roundsUpgrade = form.rounds.reduce((sum, r, i) => {
    if (i >= allowedRounds) return sum
    return sum + (Number(r.upgradeTotal) || 0)
  }, 0)
  // Extra rounds: any round beyond the package allowance counts as a
  // paid add-on. Pricing rule depends on the course's BillExtrasFullPrice
  // flag:
  //   - false (default cap behaviour): customer pays MaxPricePerRound
  //     × golfers, regardless of the slot's actual price. Reseller
  //     absorbs the difference (positive or negative).
  //   - true ("Extras at market price"): customer pays the slot's
  //     ACTUAL price × golfers. Server exposes pricePerGolfer on each
  //     slot for these courses (see exposePrices in resellerEmbedController);
  //     the picker's onSelect stores it as r.actualTotal so the math
  //     can use it directly. Falls back to (max + upgradeTotal) if the
  //     slot was picked before pricePerGolfer was exposed (legacy data
  //     on a half-deployed env).
  const extraRoundsCost = form.rounds.reduce((sum, r, i) => {
    if (i < allowedRounds) return sum
    const course = (detail.courses || []).find(c => c.coursesId === r.coursesId)
    const maxPerRound = course?.maxPricePerRound != null
      ? Number(course.maxPricePerRound)
      : Number(detail.package.defaultMaxGolfPrice || 0)
    const golfers = Number(form.numGolfers || 1)
    if (course?.billExtrasFullPrice) {
      if (r.actualTotal != null) return sum + Number(r.actualTotal)
      // Legacy/fallback: max + upgrade gives actual when slot > max,
      // but UNDER-counts when slot < max (extras-at-market should pay
      // less in that case). Best we can do without the slot price.
      return sum + (maxPerRound * golfers) + (Number(r.upgradeTotal) || 0)
    }
    return sum + (maxPerRound * golfers)
  }, 0)
  const preTax = subtotal + totalAddons + hotelUpgrade + roundsUpgrade + extraRoundsCost
  // Promo discount (reseller-created code): applied PRE-TAX, reseller absorbs it.
  // Recomputed live from the code's raw params against the current pre-tax base,
  // mirroring the server's discountForSubtotal exactly — so if the customer edits
  // their order after applying, the shown total still equals the charged total.
  const promoDiscount = (() => {
    if (!appliedPromo) return 0
    const bt = appliedPromo.benefitType
    if (bt === 'addon') return 0
    if (appliedPromo.minSpend != null && preTax < Number(appliedPromo.minSpend)) return 0 // dropped below min spend
    let d = 0
    if (bt === 'percent') {
      d = preTax * ((Number(appliedPromo.percentOff) || 0) / 100)
      if (appliedPromo.maxDiscount != null) d = Math.min(d, Number(appliedPromo.maxDiscount))
    } else if (bt === 'amount') {
      d = Number(appliedPromo.amountOff) || 0
    }
    return Math.min(Math.round(d * 100) / 100, preTax)
  })()
  const taxable = Math.max(0, preTax - promoDiscount)
  // Taxes computed against the discounted base; compound rates stack on running base.
  const taxRates = ctx.taxRates || []
  const taxLines = []
  let totalTax = 0
  let runningCompoundBase = taxable
  for (const t of taxRates) {
    if (!t.rate || taxable <= 0) continue
    const base = t.baseMode === 'compound' ? runningCompoundBase : taxable
    const amt = Math.round(base * t.rate * 100) / 100
    if (amt <= 0) continue
    taxLines.push({ name: t.name, rate: t.rate, baseMode: t.baseMode, amt })
    totalTax += amt
    if (t.baseMode === 'compound') runningCompoundBase += amt
  }
  const total = taxable + totalTax
  // When the margin cap applies (server preview), the customer pays the capped
  // total. Falls back to the full client total when there's no cap.
  const capApplied = !!capPreview?.capApplied
  const displayTotal = capApplied ? Number(capPreview.cappedTotal) : total
  // The cap discount is PRE-TAX (reduces the package base); tax then follows the
  // reduced base. Scale the itemised tax lines so the breakdown reconciles.
  const capDiscountPreTax = capApplied ? Number(capPreview.discount) : 0
  const cappedSubtotal = Math.max(0, taxable - capDiscountPreTax)
  const taxScale = (capApplied && taxable > 0) ? cappedSubtotal / taxable : 1
  const scaledTax = (amt) => Math.round((capApplied ? amt * taxScale : amt) * 100) / 100

  function setField(k, v) { setForm(f => ({ ...f, [k]: v })) }
  function setGolfer(k, v) { setForm(f => ({ ...f, golfer: { ...f.golfer, [k]: v } })) }

  function promoReasonMsg(reason, minSpend) {
    if (reason === 'min_spend') return t('sp_promo_err_min_spend', 'Spend at least {amount} to use this code.', { amount: fmtMoney(minSpend, ctx.reseller.currency) })
    const map = {
      not_found: 'sp_promo_err_not_found', inactive: 'sp_promo_err_inactive', expired: 'sp_promo_err_expired',
      not_started: 'sp_promo_err_not_started', not_applicable: 'sp_promo_err_not_applicable',
      used_up: 'sp_promo_err_used_up', already_used: 'sp_promo_err_already_used',
      wrong_customer: 'sp_promo_err_wrong_customer',
    }
    return t(map[reason] || 'sp_promo_err_not_found', 'That code is not valid.')
  }
  async function applyPromo() {
    const code = promoInput.trim()
    if (!code || promoBusy) return
    setPromoBusy(true); setPromoErr(null)
    try {
      const { data, status } = await api.post('/promo/validate', {
        code, packageSlug: detail.package.slug, subtotal: preTax,
        golferEmail: form.golfer.email || null, langId,
      })
      if (status >= 400) throw new Error(data?.message || `Error ${status}`)
      if (data.ok) { setAppliedPromo(data); setPromoErr(null) }
      else { setAppliedPromo(null); setPromoErr(promoReasonMsg(data.reason, data.minSpend)) }
    } catch (e) { setAppliedPromo(null); setPromoErr(e.message) }
    finally { setPromoBusy(false) }
  }
  function removePromo() { setAppliedPromo(null); setPromoErr(null); setPromoInput('') }
  function setRound(i, k, v) { setForm(f => ({ ...f, rounds: f.rounds.map((r, idx) => idx === i ? { ...r, [k]: v } : r) })) }
  function addRound() {
    if (!detail.courses?.length) return
    const c = detail.courses[form.rounds.length % detail.courses.length]
    setForm(f => ({ ...f, rounds: [...f.rounds, {
      coursesId: c.coursesId,
      courseName: c.courseName,
      // Customer-added extras are never a pinned/required round.
      required: false,
      // Default the new round to the day AFTER the last one (or
      // check-in if this is the first). Keeps the natural day-by-day
      // progression instead of stacking everyone on day 1.
      playDate: addDays(form.checkInDate, f.rounds.length),
      teeTime: '',
      ref: null,
      cancellationPolicy: null,
      upgradePerGolfer: null,
      upgradeTotal: null,
    }] }))
  }
  function removeRound(i) { setForm(f => ({ ...f, rounds: f.rounds.filter((_, idx) => idx !== i) })) }
  function toggleAddon(a) {
    const exists = form.addons.find(x => x.addonId === a.ID_NO)
    if (exists) setForm(f => ({ ...f, addons: f.addons.filter(x => x.addonId !== a.ID_NO) }))
    else setForm(f => ({ ...f, addons: [...f.addons, { addonId: a.ID_NO, addonName: a.Name, priceTotal: addonPriceTotal(a, form.numGolfers) }] }))
  }

  // Stage 1: form -> review. Validates inputs only; doesn't hit the
  // server. Customer must agree to per-component policies on the review
  // screen before we post.
  function goReview(e) {
    if (e) e.preventDefault()
    setErr(null)
    if (!form.checkInDate) return setErr(t('sp_pick_checkin_date', 'Pick a check-in date.'))
    if (!form.golfer.name || !form.golfer.email) return setErr(t('sp_enter_name_and_email', 'Enter your name and email.'))
    if (!isValidPhone(form.golfer.phone)) return setErr(t('sp_enter_10_digit_phone', 'Enter a 10-digit phone number.'))
    if (form.rounds.length === 0) return setErr(t('sp_add_at_least_one_round', 'Add at least one round of golf.'))
    for (let i = 0; i < form.rounds.length; i++) {
      const r = form.rounds[i]
      if (!r.teeTime) return setErr(t('sp_pick_tee_time_for_round', 'Pick a tee time for round {num}.', { num: i + 1 }))
    }
    if (!form.hotel?.externalId) return setErr(t('sp_please_select_hotel', 'Please select a hotel.'))
    setAgreed({}) // reset agreement state on each entry
    fire('checkout_start', 'package', { rounds: form.rounds.length, hasHotel: !!form.hotel?.externalId })
    setStage('review')
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Stage 2: review screen submit. Creates the booking row + advances
  // to the 'pay' stage where the customer enters their card. Booking
  // hasn't been charged yet at this point — the payment screen runs
  // the actual pre-auth which kicks off the booking sequencer.
  async function submit(e) {
    e.preventDefault()
    setErr(null)
    setSubmitting(true)
    try {
      const { data, status } = await api.post('/bookings', {
        packageSlug: detail.package.slug,
        golfer: form.golfer,
        checkInDate: form.checkInDate, checkOutDate: checkOut,
        numGolfers: Number(form.numGolfers), numRooms: Number(form.numRooms),
        hotel: form.hotel,
        rounds: form.rounds,
        addons: form.addons,
        // Audit trail: every policy the customer agreed to. Backend
        // stores on the booking row so we can show "you agreed to
        // these terms" if they ever dispute.
        agreedPolicies: Object.keys(agreed).filter(k => agreed[k]),
        totalCustomerPaid: total,
        promoCode: appliedPromo?.code || undefined,
      })
      if (status >= 400) throw new Error(data?.message || `Error ${status}`)
      // Move to payment stage with the booking ID + magic token.
      setBookingForPay(data)
      setStage('pay')
    } catch (e2) {
      setErr(e2.message)
    } finally { setSubmitting(false) }
  }

  // Stage 3: card capture + pre-auth. The raw card fields are POSTed over
  // HTTPS to our API, which runs the Moneris pre-auth server-side (the same
  // proven path as the Stay & Play /pay checkout). The card is never stored.
  // In dev (RESELLER_FAKE_PAYMENTS=1 on the API) the backend short-circuits
  // to a synthetic auth before the card is used, so the flow still completes.
  async function payCard(card) {
    setErr(null); setSubmitting(true)
    // Charge the SERVER's authoritative total, which includes the margin-cap
    // discount (computed server-side; the client can't derive it).
    const payTotal = bookingForPay?.pricing?.total ?? total
    try {
      const r = await api.post(`/bookings/${bookingForPay.bookingId}/payments`, {
        card: {
          number: card.number,
          expiry: card.expiry,
          cvc: card.cvc,
          name: card.name,
        },
        amount: payTotal,
        payerName: form.golfer.name,
        payerEmail: form.golfer.email,
      })
      if (r.status >= 400) throw new Error(r.data?.message || `Error ${r.status}`)
      onConfirmed({ ...bookingForPay, total: payTotal })
    } catch (e2) {
      setErr(e2.message)
    } finally { setSubmitting(false) }
  }

  // Split-pay: register the golfers (leader + others) and email each their
  // share link. The leader's own row is created when THEY pay (payLeaderShare).
  async function setupSplit(payers) {
    setErr(null); setSubmitting(true)
    try {
      const r = await api.post(`/bookings/${bookingForPay.bookingId}/split-payers`, {
        token: bookingForPay.magicToken,
        payers,
      })
      if (r.status >= 400) throw new Error(r.data?.message || `Error ${r.status}`)
      const leader = payers.find(p => p.isLeader)
      setLeaderShare(leader ? Number(leader.amount) : (bookingForPay?.pricing?.total ?? total))
      setSplitSetupDone(true)
    } catch (e2) {
      setErr(e2.message)
    } finally { setSubmitting(false) }
  }

  // Leader pays only their own share; the booking then waits for the others.
  async function payLeaderShare(card) {
    setErr(null); setSubmitting(true)
    try {
      const r = await api.post(`/bookings/${bookingForPay.bookingId}/payments`, {
        card: { number: card.number, expiry: card.expiry, cvc: card.cvc, name: card.name },
        amount: leaderShare,
        payerName: form.golfer.name,
        payerEmail: form.golfer.email,
      })
      if (r.status >= 400) throw new Error(r.data?.message || `Error ${r.status}`)
      setStage('waiting')
    } catch (e2) {
      setErr(e2.message)
    } finally { setSubmitting(false) }
  }

  const primary = branding?.primaryColor || brand?.primaryColor || '#0f172a'
  const canSplit = !!detail?.package?.allowSplitPayments && bookingForPay?.gateway !== 'stripe'

  // Build the policy list shown on the review screen. One row per
  // bookable component (hotel + each round). Each row carries a
  // unique key + label + the policy text the customer must accept.
  const policies = []
  if (form.hotel) {
    // Room type the customer picked — shown on the policy card so they re-confirm
    // the exact room when they agree (between the hotel name and the policy text).
    const hotelRoomLabel = [form.hotel.roomType, form.hotel.bedConfig].filter(Boolean).join(' · ')
    policies.push({
      key: 'hotel',
      label: `Hotel — ${form.hotel.name}`,
      sub: hotelRoomLabel
        ? (Number(form.numRooms) > 1
            ? t('sp_rooms_label', '{count} Rooms: {room}', { count: form.numRooms, room: hotelRoomLabel })
            : t('sp_room_label', 'Room: {room}', { room: hotelRoomLabel }))
        : null,
      text: form.hotel.refundable
        ? `Free cancellation${form.hotel.cancellationDeadline ? ` until ${form.hotel.cancellationDeadline.slice(0, 10)}` : ''}.`
        : 'Non-refundable. If you cancel, no refund will be issued for the hotel portion.',
    })
  }
  form.rounds.forEach((r, i) => {
    policies.push({
      key: `round_${i}`,
      label: `Round ${i + 1} — ${r.courseName} on ${r.playDate} at ${formatTeeTime(r.teeTime)}`,
      text: r.cancellationPolicy || t('sp_cancellation_default_round', "Subject to the course's standard policy — usually 24-48 hour notice required."),
    })
  })
  const allAgreed = policies.length > 0 && policies.every(p => agreed[p.key])

  if (stage === 'pay' && bookingForPay) {
    // Charge/display the SERVER's authoritative total (includes the margin-cap
    // discount). capInfo drives the "Book now, save X%" saving banner.
    const payTotal = bookingForPay.pricing?.total ?? total
    const capInfo = bookingForPay.pricing?.marginCap || null
    const savingBanner = capInfo?.applied ? (
      <div style={{ padding: '10px 14px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 10, marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13.5, fontWeight: 700, color: '#166534' }}>
          {t('sp_maxrev_save_line', 'Book now — save {pct}%', { pct: capInfo.savingsPct })}
        </span>
        <span style={{ fontSize: 12.5, color: '#15803d' }}>
          {capInfo.preTotal != null && <span style={{ textDecoration: 'line-through', color: '#94a3b8', marginRight: 8 }}>{t('sp_maxrev_was', 'Was {amount}', { amount: fmtMoney(capInfo.preTotal, ctx.reseller.currency) })}</span>}
          {t('sp_maxrev_you_save', 'You save {amount}', { amount: fmtMoney(Math.round((capInfo.preTotal - payTotal) * 100) / 100, ctx.reseller.currency) })}
        </span>
      </div>
    ) : null
    // Order summary shown above the card fields (what they're buying + tax).
    const pkgSummary = (
      <OrderSummary
        title={detail.package?.name}
        lines={[
          form.hotel?.name ? `🏨 ${form.hotel.name}` : null,
          (form.hotel?.name && form.checkInDate) ? `${t('sp_checkin', 'Check-in')}: ${longDate(form.checkInDate)}` : null,
          (form.hotel?.name && form.checkInDate) ? `${t('sp_checkout', 'Check-out')}: ${longDate(checkOut)}` : null,
          `${form.numGolfers} ${Number(form.numGolfers) === 1 ? t('sp_golfer', 'golfer') : t('sp_golfers', 'golfers')}${detail.package?.nights ? ` · ${detail.package.nights} ${detail.package.nights === 1 ? t('sp_night', 'night') : t('sp_nights', 'nights')}` : ''}`,
          ...(form.rounds || []).filter(r => r.coursesId).map(r => `⛳ ${form.numGolfers} ${Number(form.numGolfers) === 1 ? t('sp_byo_round_word', 'round') : t('sp_byo_rounds_word', 'rounds')} @ ${r.courseName || t('sp_byo_round_generic', 'Round')}${r.playDate ? ` · ${longDate(r.playDate)}` : ''}${r.teeTime ? ` - ${formatTeeTime(r.teeTime)} ${t('sp_byo_teetime', 'Tee-Time')}` : ''}`),
        ]}
        subtotal={bookingForPay.pricing?.subtotal}
        savings={capInfo?.applied ? capInfo.discount : 0}
        savingsLabel={t('sp_maxrev_discount', 'Book-now discount')}
        tax={bookingForPay.pricing?.tax}
        taxRate={bookingForPay.pricing?.taxRate}
        total={payTotal}
        currency={ctx.reseller.currency}
        branding={branding}
      />
    )
    // Mode B (Stripe): the browser tokenizes the card with the reseller's
    // publishable key via Stripe Elements, then sends the paymentMethodId.
    // Mode A (Moneris): the raw-card form posts to our server. Chosen by the
    // gateway the booking was created under.
    if (bookingForPay.gateway === 'nmi' && bookingForPay.nmiTokenizationKey) {
      return (
        <>
          {savingBanner}
          <NmiPayScreen
            api={api} booking={bookingForPay} total={payTotal}
            currency={ctx.reseller.currency} golfer={form.golfer} summary={pkgSummary}
            onConfirmed={() => onConfirmed({ ...bookingForPay, total: payTotal })}
            onBack={() => setStage('review')}
            primary={primary} branding={branding}
          />
        </>
      )
    }
    if (bookingForPay.gateway === 'stripe' && bookingForPay.stripePublishableKey) {
      return (
        <>
          {savingBanner}
          <StripePayWrapper
            publishableKey={bookingForPay.stripePublishableKey}
            api={api}
            booking={bookingForPay}
            total={payTotal}
            currency={ctx.reseller.currency}
            golfer={form.golfer}
            summary={pkgSummary}
            onConfirmed={() => onConfirmed({ ...bookingForPay, total: payTotal })}
            onBack={() => setStage('review')}
            primary={primary}
            branding={branding}
          />
        </>
      )
    }
    if (bookingForPay.gateway === 'elavon_converge') {
      return <GatewayComingSoon gatewayLabel="Elavon Converge" onBack={() => setStage('review')} primary={primary} branding={branding} />
    }
    // Split-pay: enter golfers + shares, then the leader pays only their share.
    if (paymentPlan === 'split' && !splitSetupDone) {
      return (
        <>
          {savingBanner}
          <SplitSetupScreen
            total={payTotal}
            currency={ctx.reseller.currency}
            numGolfers={Number(form.numGolfers) || 1}
            leaderGolfer={form.golfer}
            onSubmit={setupSplit}
            onBack={() => { setPaymentPlan('full'); setErr(null) }}
            submitting={submitting}
            err={err}
            primary={primary}
            branding={branding}
          />
        </>
      )
    }
    if (paymentPlan === 'split' && splitSetupDone) {
      return (
        <PayScreen
          booking={bookingForPay}
          total={leaderShare}
          currency={ctx.reseller.currency}
          onPay={payLeaderShare}
          onBack={() => setSplitSetupDone(false)}
          submitting={submitting}
          err={err}
          primary={primary}
          branding={branding}
          heading={t('sp_pay_your_share', 'Pay your share')}
        />
      )
    }
    return (
      <>
        {savingBanner}
        {canSplit && (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 13, color: '#475569' }}>{t('sp_split_prompt', 'Golfing with friends? Split the cost and invite them to pay their share.')}</span>
            <button type="button" onClick={() => { setPaymentPlan('split'); setErr(null) }} style={{ ...secondaryBtn, whiteSpace: 'nowrap' }}>{t('sp_split_cta', 'Split with friends →')}</button>
          </div>
        )}
        <PayScreen
          booking={bookingForPay}
          total={payTotal}
          currency={ctx.reseller.currency}
          onPay={payCard}
          onBack={() => setStage('review')}
          submitting={submitting}
          err={err}
          primary={primary}
          branding={branding}
          summary={pkgSummary}
        />
      </>
    )
  }

  if (stage === 'waiting' && bookingForPay) {
    return (
      <SplitWaitingScreen
        api={api}
        bookingId={bookingForPay.bookingId}
        magicToken={bookingForPay.magicToken}
        resellerSlug={ctx.reseller.slug}
        currency={ctx.reseller.currency}
        onConfirmed={() => onConfirmed({ ...bookingForPay, total })}
        primary={primary}
        branding={branding}
      />
    )
  }

  if (stage === 'review') {
    return (
      <form onSubmit={submit}>
        <button type="button" onClick={() => setStage('building')} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, marginBottom: 12, fontSize: 13 }}>← Back to edit</button>
        <h1 style={{ margin: '0 0 8px', fontSize: 26 }}>Review and confirm</h1>
        <p style={{ color: '#64748b', marginTop: 0, marginBottom: 20, fontSize: 14, lineHeight: 1.6 }}>
          Please review your trip and agree to each provider's cancellation policy below. Once you confirm and pay, we'll lock in your hotel and tee times.
        </p>

        {resumeState && (
          <ResumeAvailabilityBanner
            checking={checkingAvail}
            availability={availability}
            err={availabilityErr}
            onPickAlternative={applyAlternative}
          />
        )}
        {resumeError && !resumeState && (
          <div style={{ ...errorBox, marginBottom: 16 }}>{resumeError}</div>
        )}

        {err && <div style={errorBox}>{err}</div>}

        <Section title={t('sp_trip_summary', 'Trip summary')}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, fontSize: 13, lineHeight: 1.7 }}>
            <div><strong>{detail.package.name}</strong></div>
            <div style={{ color: '#64748b' }}>
              {form.checkInDate} → {checkOut} · {form.numGolfers} golfer{form.numGolfers !== 1 ? 's' : ''} · {form.numRooms} room{form.numRooms !== 1 ? 's' : ''}
            </div>
            <div style={{ marginTop: 8 }}>
              <strong>Hotel:</strong> {form.hotel?.name || '—'}
            </div>
            <div style={{ marginTop: 4 }}>
              <strong>Rounds:</strong>
              <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                {form.rounds.map((r, i) => (
                  <li key={i}>{r.courseName} — {r.playDate} at {formatTeeTime(r.teeTime)}{r.upgradeTotal ? ` (premium +${fmtMoney(r.upgradeTotal)})` : ''}</li>
                ))}
              </ul>
            </div>
            {form.addons.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <strong>Add-ons:</strong> {form.addons.map(a => a.addonName).join(', ')}
              </div>
            )}
          </div>
        </Section>

        <Section title={t('sp_cancellation_policies_review_agree', 'Cancellation policies — please review and agree to each')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {policies.map(p => (
              <label key={p.key} style={{ background: agreed[p.key] ? '#f0fdf4' : '#fff', border: '1px solid ' + (agreed[p.key] ? '#86efac' : '#e2e8f0'), borderRadius: 8, padding: 12, display: 'flex', gap: 12, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!agreed[p.key]}
                  onChange={e => setAgreed(a => ({ ...a, [p.key]: e.target.checked }))}
                  style={{ marginTop: 3, flexShrink: 0 }}
                />
                <div style={{ flex: 1, fontSize: 13 }}>
                  <div style={{ fontWeight: 600 }}>{p.label}</div>
                  {p.sub && <div style={{ color: '#0f172a', marginTop: 2, fontWeight: 500 }}>{p.sub}</div>}
                  <div style={{ color: '#475569', marginTop: 2 }}>{p.text}</div>
                  <div style={{ color: '#64748b', marginTop: 4, fontSize: 12, fontStyle: 'italic' }}>I have read and accept this policy.</div>
                </div>
              </label>
            ))}
          </div>
        </Section>

        <Section title={t('sp_your_total', 'Your total')}>
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
            <Line label={`${detail.package.name}`} value={subtotal} />
            {hotelUpgrade > 0 && <Line label={`Hotel upgrade (${form.hotel?.name || ''})`} value={hotelUpgrade} />}
            {roundsUpgrade > 0 && <Line label="Premium tee time upgrades" value={roundsUpgrade} />}
            {extraRoundsCost > 0 && <Line label="Extra rounds (beyond package)" value={extraRoundsCost} />}
            {totalAddons > 0 && <Line label="Add-ons" value={totalAddons} />}
            {capApplied && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 8, padding: '8px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6 }}>
                <span style={{ color: '#166534', fontWeight: 700 }}>{t('sp_maxrev_save_line', 'Book now — save {pct}%', { pct: capPreview.savingsPct })}</span>
                <span style={{ color: '#166534', fontWeight: 700 }}>−{fmtMoney(capDiscountPreTax, ctx.reseller.currency)}</span>
              </div>
            )}
            {taxLines.map((t, i) => (
              <Line key={i} label={`${t.name} (${(t.rate * 100).toFixed(3).replace(/\.?0+$/, '')}%)`} value={scaledTax(t.amt)} />
            ))}
            <hr style={{ border: 0, borderTop: '1px solid #e2e8f0', margin: '12px 0' }} />
            <Line label={t('sp_total', 'Total')} value={displayTotal} bold currency={ctx.reseller.currency} />
            {capApplied && (
              <div style={{ textAlign: 'right', fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                <span style={{ textDecoration: 'line-through' }}>{fmtMoney(total, ctx.reseller.currency)}</span>
              </div>
            )}
          </div>
        </Section>

        {(() => {
          // Block submission while we're still verifying tee-time
          // availability on a resumed trip, OR if any round is still
          // flagged unavailable. Customer must pick alternatives (or
          // go back to edit) before they can pay.
          const verifying = !!resumeState && (checkingAvail || availability === null)
          const hasUnavailable = !!availability && availability.some(r => !r.available)
          const blocked = submitting || !allAgreed || verifying || hasUnavailable
          let label
          if (submitting) label = 'Processing…'
          else if (!allAgreed) label = 'Agree to all policies above to continue'
          else if (verifying) label = 'Checking tee-time availability…'
          else if (hasUnavailable) label = 'Resolve unavailable tee times above to continue'
          else label = `Confirm + Pay (${fmtMoney(displayTotal, ctx.reseller.currency)})`
          return (
            <button
              type="submit"
              disabled={blocked}
              style={{
                width: '100%',
                padding: 16,
                background: blocked ? '#cbd5e1' : primary,
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontSize: 15,
                fontWeight: 700,
                cursor: blocked ? 'not-allowed' : 'pointer',
              }}
            >{label}</button>
          )
        })()}
      </form>
    )
  }

  // Per-step validation — gate the Continue button so customers can't
  // skip ahead with empty fields.
  function canAdvance() {
    if (wizardStep === 1) return !!form.checkInDate
    if (wizardStep === 2) return !!form.hotel?.externalId
    if (wizardStep === 3) return form.rounds.length > 0 && form.rounds.every(r => r.teeTime)
    if (wizardStep === 4) {
      if (!(form.golfer.name && form.golfer.email && isValidPhone(form.golfer.phone))) return false
      // Wait for the margin-cap price preview so the customer can't advance
      // before we've shown them the discounted total — but only up to the safety
      // valve, so a slow/cold-cache preview never hangs checkout (the cap is still
      // enforced authoritatively at booking).
      if (detail.package.maxRevenueCapEnabled && capLoading && !capMaxWaitReached) return false
      return true
    }
    return true
  }
  function nextStep() {
    setErr(null)
    if (!canAdvance()) {
      if (wizardStep === 1) return setErr(t('sp_pick_checkin_date', 'Pick a check-in date.'))
      if (wizardStep === 2) return setErr(t('sp_select_hotel', 'Select a hotel.'))
      if (wizardStep === 3) return setErr(t('sp_pick_tee_time_each_round', 'Pick a tee time for each round.'))
      if (wizardStep === 4 && detail.package.maxRevenueCapEnabled && capLoading && form.golfer.name && form.golfer.email && isValidPhone(form.golfer.phone)) {
        return setErr(t('sp_maxrev_searching', 'Searching for the best price…'))
      }
      if (wizardStep === 4) return setErr(t('sp_enter_name_and_email', 'Enter your name and email.'))
    }
    if (wizardStep < TOTAL_STEPS) setWizardStep(wizardStep + 1)
    else goReview()
  }
  function prevStep() { if (wizardStep > 1) setWizardStep(wizardStep - 1); setErr(null) }

  return (
    <form onSubmit={(e) => { e.preventDefault(); nextStep() }}>
      <button type="button" onClick={onBack} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, marginBottom: 12, fontSize: 13 }}>{t('sp_back_to_packages', '← Back to packages')}</button>
      <h1 style={{ margin: '0 0 4px', fontSize: 24, ...headerFontStyle(branding) }}>{(langId === 2 && detail.package?.nameFr) ? detail.package.nameFr : detail.package?.name}</h1>
      {(() => {
        const d = (langId === 2 && detail.package?.descriptionFr) ? detail.package.descriptionFr : detail.package?.description
        return d ? <div className="hiw-preview" style={{ color: '#64748b', marginTop: 0, marginBottom: 16, fontSize: 13, lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: mdToHtml(d) }} /> : null
      })()}

      {/* Step dots — match the S&P /embed/stay-play multi-step pattern */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 20 }}>
        {Array.from({ length: TOTAL_STEPS }).map((_, i) => {
          const idx = i + 1
          const done = idx < wizardStep, active = idx === wizardStep
          return (
            <div key={i} style={{
              flex: 1, height: 4, borderRadius: 2,
              background: done || active ? primary : '#e2e8f0',
              opacity: done ? 0.5 : 1,
            }} />
          )
        })}
      </div>

      {err && <div style={errorBox}>{err}</div>}

      {/* ─── Step 1: dates ──────────────────────────────────────── */}
      {wizardStep === 1 && (
      <Section title={t('sp_when_do_you_want_to_arrive', 'When do you want to arrive?')}>
        <div style={{ ...row, flexWrap: 'wrap' }}>
          <Field label={t('sp_checkin_night_package', 'Check-in ({nights}-night package)', { nights: detail.package.nights })} style={{ flex: '1 1 150px', minWidth: 0 }}>
            <input type="date" required value={form.checkInDate} onChange={e => setField('checkInDate', e.target.value)} style={inp} min={today()} />
          </Field>
          <Field label={t('sp_checkout', 'Check-out')} style={{ flex: '1 1 150px', minWidth: 0 }}>
            <input type="date" value={checkOut} readOnly style={{ ...inp, background: '#f1f5f9', color: '#64748b' }} />
          </Field>
        </div>
        {/* "X golfers in Y rooms" info text removed per reseller request
            (2026-06-18) — package description already tells the customer
            the party size, so the auto-block was just repeating it. */}
      </Section>
      )}

      {/* ─── Step 3: tee times ───────────────────────────────────── */}
      {wizardStep === 3 && (
      <Section title={t('sp_pick_your_tee_times', 'Pick your tee times')}>
        {Number(form.numGolfers) === 1 && (
          <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 8, fontSize: 12.5, color: '#92400e', lineHeight: 1.5 }}>
            {t('sp_solo_golfer_tee_notice', 'Heads up: some courses don’t allow a single golfer to book an empty tee time online — you may be asked to call the pro shop. Booking 2 or more golfers, or picking a time that already has players, avoids this.')}
          </div>
        )}
        {form.rounds.length === 0
          ? <div style={muted}>{form.checkInDate ? 'Add a round below.' : 'Pick a check-in date first.'}</div>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {form.rounds.map((r, i) => {
                // A round is "locked" when its course is flagged Required on
                // the package — the reseller has decided every customer must
                // play that course, so we render it as static text (no
                // dropdown) and suppress the Remove button. The date stays
                // editable so the customer picks WHICH day they play it.
                const courseMeta = detail.courses.find(c => c.coursesId === Number(r.coursesId))
                // A round beyond the package's included allowance is an "extra"
                // the customer added — always removable (so an accidental
                // +Add round can be undone).
                const isExtra = i >= allowedRounds
                // A round is "locked" (pinned to a required course, shown as
                // static text) only when it is a DESIGNATED required round —
                // tracked by the round's own `required` flag set at init. We do
                // NOT derive this from the currently-selected course, otherwise
                // picking the required course on a free/extra round would wrongly
                // lock it, making that course "required" on a second day.
                // Fallback for older resumed bookings created before the flag
                // existed: the previous course-derived rule.
                const isLocked = r.required != null
                  ? !!r.required
                  : (!isExtra && !!courseMeta?.isRequired)
                return (
                <div key={i} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    {isLocked ? (
                      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 12px', background: isExtra ? '#fffbeb' : '#f0fdf4', border: '1px solid ' + (isExtra ? '#fcd34d' : '#86efac'), borderRadius: 6, fontSize: 14, color: isExtra ? '#92400e' : '#15803d', fontWeight: 600 }}>
                        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: isExtra ? '#92400e' : '#15803d', background: isExtra ? '#fef3c7' : '#dcfce7', padding: '2px 6px', borderRadius: 4 }}>{isExtra ? t('sp_extra_round_badge', 'EXTRA ROUND') : t('sp_required_badge', 'REQUIRED')}</span>
                        {r.courseName}
                      </div>
                    ) : (
                      <>
                      {isExtra && (
                        <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, color: '#92400e', background: '#fef3c7', padding: '4px 8px', borderRadius: 4 }}>{t('sp_extra_round_badge', 'EXTRA ROUND')}</span>
                      )}
                      <select
                        value={r.coursesId}
                        onChange={e => {
                          const c = detail.courses.find(c => c.coursesId === Number(e.target.value));
                          setRound(i, 'coursesId', Number(e.target.value));
                          if (c) setRound(i, 'courseName', c.courseName);
                          // Clear picked-slot fields — they're tied to the
                          // previous course/date. Without this, stale
                          // actualTotal / upgradeTotal from the old slot
                          // would feed into the math after the course swap.
                          setRound(i, 'teeTime', '');
                          setRound(i, 'ref', null);
                          setRound(i, 'upgradePerGolfer', null);
                          setRound(i, 'upgradeTotal', null);
                          setRound(i, 'actualPerGolfer', null);
                          setRound(i, 'actualTotal', null);
                        }}
                        style={selectS}
                      >
                        {/* All package courses are selectable here, including
                            Required ones. The Required flag only GUARANTEES one
                            round on that course (it's pinned to its own locked
                            round above); it does NOT stop the customer from
                            choosing it again on another day — e.g. play the host
                            course multiple times in the trip. (Forcing rounds on
                            *different* courses would be a separate "min unique
                            courses" rule, not this.) */}
                        {detail.courses.map(c => (
                          <option key={c.coursesId} value={c.coursesId}>{c.courseName}</option>
                        ))}
                      </select>
                      </>
                    )}
                    <input
                      type="date"
                      value={r.playDate}
                      onChange={e => {
                        setRound(i, 'playDate', e.target.value);
                        setRound(i, 'teeTime', '');
                        setRound(i, 'ref', null);
                        setRound(i, 'upgradePerGolfer', null);
                        setRound(i, 'upgradeTotal', null);
                        setRound(i, 'actualPerGolfer', null);
                        setRound(i, 'actualTotal', null);
                      }}
                      style={{ ...inp, width: 160 }}
                      min={form.checkInDate}
                      max={checkOut}
                    />
                    {(isExtra || !isLocked) && (
                      <button type="button" onClick={() => removeRound(i)} style={{ ...ghostBtn, marginLeft: 'auto' }}>{t('sp_remove_round', 'Remove round')}</button>
                    )}
                  </div>
                  {/* Distance from the selected hotel to this round's
                      course. Only renders when both have usable coords.
                      Helps the customer reason about morning-of drive
                      time when picking tee times. */}
                  {(() => {
                    const course = detail.courses.find(c => c.coursesId === Number(r.coursesId))
                    if (!form.hotel || form.hotel.lat == null || !course || course.latitude == null) return null
                    const km = roundDistanceKm(haversineKm(form.hotel.lat, form.hotel.lng, course.latitude, course.longitude))
                    return (
                      <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', background: '#f1f5f9', color: '#334155', borderRadius: 999, fontSize: 12, fontWeight: 500 }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                        {t('sp_distance_from', '{distance} km from {name}', { distance: km, name: form.hotel.name })}
                        <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 2 }}>{t('sp_crow_flies', '(crow flies)')}</span>
                      </div>
                    )
                  })()}
                  {(() => {
                    // v159: decide whether/how to label the badge on slots
                    // above the per-course max:
                    //   in-package + hardCap  → hide badge (reseller absorbs)
                    //   extra + !billExtrasFullPrice → hide badge (extras
                    //       billed at MaxPricePerRound, not slot price)
                    //   extra + billExtrasFullPrice → show FULL round cost
                    //       (max × golfers + upgradeTotal), not just the
                    //       delta — the customer pays the whole round at
                    //       market price, not a delta over a bundle
                    //   in-package + !hardCap → show DELTA (upgradeTotal),
                    //       since the bundle already covers the base
                    const allowed = Number(detail.package.roundsPerGolfer || 1)
                    const course = (detail.courses || []).find(c => c.coursesId === Number(r.coursesId))
                    const isExtra = i >= allowed
                    const hideUpgradeBadge = isExtra
                      ? !course?.billExtrasFullPrice
                      : !!course?.hardCap
                    const extrasMarketMode = isExtra && !!course?.billExtrasFullPrice
                    // Base used to translate upgrade-delta into full round
                    // cost on the badge: maxPerRound × golfers.
                    const extrasMarketBase = extrasMarketMode
                      ? (course?.maxPricePerRound != null
                          ? Number(course.maxPricePerRound)
                          : Number(detail.package.defaultMaxGolfPrice || 0)) * Number(form.numGolfers || 1)
                      : 0
                    return (
                      <TeeTimePicker
                        api={api}
                        track={fire}
                        surface="package"
                        packageId={detail.package.id}
                        coursesId={r.coursesId}
                        date={r.playDate}
                        golfers={form.numGolfers}
                        selected={r}
                        internalViewer={internalViewer}
                        hideUpgradeBadge={hideUpgradeBadge}
                        extrasMarketMode={extrasMarketMode}
                        extrasMarketBase={extrasMarketBase}
                        onSelect={(slot, isUpgrade) => {
                          setRound(i, 'teeTime', slot?.teeTime || '');
                          setRound(i, 'ref', slot?.ref || null);
                          setRound(i, 'cancellationPolicy', slot?.cancellationPolicy || null);
                          // When the badge is hidden, the customer wasn't
                          // shown a premium so we don't bill one — store
                          // upgrade as null no matter which tier the slot
                          // came from. The math layer below knows that
                          // extras with billExtrasFullPrice still need the
                          // upgrade total, so we preserve it in that case.
                          const recordUpgrade = isUpgrade && !hideUpgradeBadge
                          setRound(i, 'upgradePerGolfer', recordUpgrade ? slot?.upgradePerGolfer : null);
                          setRound(i, 'upgradeTotal', recordUpgrade ? slot?.upgradeTotal : null);
                          // For extras-at-market rounds the server exposes
                          // pricePerGolfer + totalPrice on every slot —
                          // capture the actual total so the running total
                          // can charge the slot's real price (even when
                          // the slot is BELOW max, where upgradeTotal is 0).
                          setRound(i, 'actualPerGolfer', slot?.pricePerGolfer != null ? Number(slot.pricePerGolfer) : null);
                          setRound(i, 'actualTotal',     slot?.totalPrice     != null ? Number(slot.totalPrice)     : null);
                        }}
                        primary={primary}
                        branding={branding}
                      />
                    )
                  })()}
                </div>
                )
              })}
            </div>
          )
        }
        {form.checkInDate && <button type="button" onClick={addRound} style={{ ...secondaryBtn, marginTop: 8 }}>{t('sp_add_round', '+ Add round')}</button>}
      </Section>
      )}

      {/* ─── Step 2: hotel ───────────────────────────────────────── */}
      {wizardStep === 2 && (
      <Section title={t('sp_choose_your_hotel', 'Choose your hotel')} subtitle={
        Number(form.numRooms) > 1
          ? t('sp_nights_rooms_included', '{nights} nights and {rooms} rooms included in your package.', { nights: detail.package.nights, rooms: Number(form.numRooms) })
          : t(detail.package.nights === 1 ? 'sp_night_included_singular' : 'sp_nights_included', '{nights} nights included in your package.', { nights: detail.package.nights })
      }>
        <HotelPicker
          api={api}
          track={fire}
          surface="package"
          checkin={form.checkInDate}
          nights={detail.package.nights}
          numRooms={Number(form.numRooms) || 1}
          maxNightly={detail.package.maxHotelPricePerNight}
          packageId={detail.package.id}
          selected={form.hotel}
          onSelect={(h, r) => setField('hotel', h ? {
            // Identity + display only. Customer never sees absolute
            // price; we re-quote via priceCheckHref at booking time.
            externalId:           h.propertyId,
            source:               r?.provider || h.provider || 'expedia',
            hotelRef:             r?.hotelRef || null,
            name:                 h.name,
            address:              h.address?.line1 || h.address?.city || '',
            starRating:           h.starRating,
            image:                h.image,
            // Coords are kept on the form so subsequent steps (tee-time
            // picker) can show distance from the chosen hotel to each
            // course without a refetch. Accepts both shapes the API
            // emits (live Expedia: {lat,lng}, cache fallback: {latitude,longitude}).
            lat: h.location?.lat ?? h.location?.latitude ?? null,
            lng: h.location?.lng ?? h.location?.longitude ?? null,
            rateKey:              r?.rateKey,
            priceCheckHref:       r?.priceCheckHref,
            // Opaque encrypted per-room cost — relayed back so the server can
            // trust the hotel cost for the margin cap (never readable/forgeable).
            costToken:            r?.costToken,
            roomType:             r?.roomType,
            bedConfig:            r?.bedConfig,
            refundable:           r?.refundable,
            cancellationDeadline: r?.cancellationDeadline,
            // Upgrade delta only — exposed for the cost summary later.
            // null for in-budget picks (no extra customer charge).
            upgradeNightly:       r?.upgradeNightly ?? null,
            upgradeTotal:         r?.upgradeTotal ?? null,
          } : null)}
          primary={primary}
          branding={branding}
        />
      </Section>
      )}

      {/* ─── Step 4: contact + add-ons + total ────────────────────── */}
      {wizardStep === 4 && (
      <>
      {detail.addons?.length > 0 && (
        <Section title={t('sp_addons', 'Add-ons')}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {detail.addons.map(a => {
              const selected = !!form.addons.find(x => x.addonId === a.ID_NO)
              return (
                <div key={a.ID_NO} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: 12, background: '#fff', border: '1px solid ' + (selected ? primary : '#e2e8f0'), borderRadius: 8 }}>
                  <Toggle
                    checked={selected || !a.IsOptional}
                    onChange={() => toggleAddon(a)}
                    disabled={!a.IsOptional}
                    activeColor={primary}
                  />
                  <div style={{ flex: 1 }}>
                    <strong style={{ fontSize: 14 }}>{a.Name}{!a.IsOptional && <span style={{ marginLeft: 6, fontSize: 11, color: '#dc2626' }}>(required)</span>}</strong>
                    {a.Description && <div style={{ fontSize: 12, color: '#64748b' }}>{a.Description}</div>}
                  </div>
                  <span style={{ fontSize: 13, color: '#475569' }}>
                    {a.PricePerGolfer != null && <>{fmtMoney(a.PricePerGolfer)} × {form.numGolfers} = </>}
                    <strong>{fmtMoney(addonPriceTotal(a, form.numGolfers))}</strong>
                  </span>
                </div>
              )
            })}
          </div>
        </Section>
      )}

      <Section title={t('sp_your_details', 'Your details')}>
        <Field label={t('sp_full_name', 'Full name')}><input required type="text" value={form.golfer.name} onChange={e => setGolfer('name', e.target.value)} style={inp} /></Field>
        <div style={row}>
          <Field label={t('sp_email', 'Email')} style={{ flex: 1 }}><input required type="email" value={form.golfer.email} onChange={e => setGolfer('email', e.target.value)} style={inp} /></Field>
          <Field label={t('sp_phone', 'Phone')} style={{ flex: 1 }}>
            <input
              required
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="+1 (902) 555-1234"
              value={form.golfer.phone}
              onChange={e => setGolfer('phone', formatPhone(e.target.value))}
              onBlur={e => setGolfer('phone', formatPhone(e.target.value))}
              style={inp}
            />
          </Field>
        </div>
      </Section>

      <Section title={t('sp_your_total', 'Your total')}>
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
          <Line label={t('sp_package_total_breakdown', '{name} ({nights} nights, {rounds} rounds per golfer, {golfers} golfers)', { name: detail.package.name, nights: detail.package.nights, rounds: detail.package.roundsPerGolfer, golfers: form.numGolfers })} value={subtotal} />
          {hotelUpgrade > 0 && <Line label={t('sp_hotel_upgrade', 'Hotel upgrade ({name})', { name: form.hotel?.name || '' })} value={hotelUpgrade} />}
          {roundsUpgrade > 0 && <Line label={t('sp_premium_tee_time_upgrades', 'Premium tee time upgrades')} value={roundsUpgrade} />}
          {extraRoundsCost > 0 && <Line label={t('sp_extra_rounds_beyond_package', 'Extra rounds (beyond package)')} value={extraRoundsCost} />}
          {totalAddons > 0 && <Line label={t('sp_addons', 'Add-ons')} value={totalAddons} />}
          {capLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, padding: '8px 10px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, color: '#64748b', fontSize: 13 }}>
              <Spinner /> <span>{t('sp_maxrev_searching', 'Searching for the best price…')}</span>
            </div>
          ) : capApplied ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 8, padding: '8px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6 }}>
              <span style={{ color: '#166534', fontWeight: 700 }}>{t('sp_maxrev_save_line', 'Book now — save {pct}%', { pct: capPreview.savingsPct })}</span>
              <span style={{ color: '#166534', fontWeight: 700 }}>−{fmtMoney(capDiscountPreTax, ctx.reseller.currency)}</span>
            </div>
          ) : null}
          {taxLines.map((t, i) => (
            <Line
              key={i}
              label={`${t.name} (${(t.rate * 100).toFixed(3).replace(/\.?0+$/, '')}%${t.baseMode === 'compound' ? ' compound' : ''})`}
              value={scaledTax(t.amt)}
            />
          ))}
          <hr style={{ border: 0, borderTop: '1px solid #e2e8f0', margin: '12px 0' }} />
          <Line label={t('sp_total', 'Total')} value={displayTotal} bold currency={ctx.reseller.currency} />
          {capApplied && (
            <div style={{ textAlign: 'right', fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
              <span style={{ textDecoration: 'line-through' }}>{fmtMoney(total, ctx.reseller.currency)}</span>
            </div>
          )}
        </div>
      </Section>

      {detail.refundTiers?.length > 0 && (
        <Section title={t('sp_refund_policy', 'Refund policy')}>
          <ul style={{ fontSize: 13, color: '#475569', margin: 0, paddingLeft: 18 }}>
            {detail.refundTiers.map((tier, i) => <li key={i}>{tierLabel(tier, t)}: {t('sp_refund_tier_percent', '{percent}% refund', { percent: Number(tier.RefundPercent) })}</li>)}
          </ul>
          <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 8 }}>{t('sp_refund_policy_disclaimer', 'Course + hotel-rate policies may further restrict; final schedule shown at booking.')}</p>
        </Section>
      )}
      </>
      )}

      {/* ─── Running total: persistent summary above the wizard nav so
           customers always see what their trip will cost as they make
           choices. Shows the bundled package price + any upgrade deltas
           they've picked (premium tee times, above-budget hotel). No
           per-round itemisation -- the bundle stays the customer's
           mental model. */}
      {wizardStep < TOTAL_STEPS && (
        <div style={{
          marginTop: 20, padding: 14,
          background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8,
          fontSize: 13, color: '#475569',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span>{t('sp_package_price', 'Package price')}</span>
            <span style={{ color: '#0f172a' }}>{fmtMoney(subtotal, ctx.reseller.currency)}</span>
          </div>
          {hotelUpgrade > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 4 }}>
              <span>{t('sp_hotel_upgrade_short', 'Hotel upgrade')}</span>
              <span style={{ color: '#b45309' }}>+{fmtMoney(hotelUpgrade, ctx.reseller.currency)}</span>
            </div>
          )}
          {roundsUpgrade > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 4 }}>
              <span>{t('sp_premium_tee_times', 'Premium tee times')}</span>
              <span style={{ color: '#b45309' }}>+{fmtMoney(roundsUpgrade, ctx.reseller.currency)}</span>
            </div>
          )}
          {extraRoundsCost > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 4 }}>
              <span>{t('sp_extra_rounds_beyond_package', 'Extra rounds (beyond package)')}</span>
              <span style={{ color: '#b45309' }}>+{fmtMoney(extraRoundsCost, ctx.reseller.currency)}</span>
            </div>
          )}
          {totalAddons > 0 && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 4 }}>
              <span>{t('sp_addons', 'Add-ons')}</span>
              <span style={{ color: '#0f172a' }}>+{fmtMoney(totalAddons, ctx.reseller.currency)}</span>
            </div>
          )}

          {/* ─── Promo code ─────────────────────────────────────────── */}
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px dashed #e2e8f0' }}>
            {appliedPromo ? (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                  <span style={{ color: '#166534', fontWeight: 600 }}>
                    {appliedPromo.benefitType === 'addon'
                      ? `${t('sp_promo_embed_included', 'Included')}: ${appliedPromo.addonLabel}`
                      : `${t('sp_promo_embed_discount', 'Discount')} (${appliedPromo.code})`}
                  </span>
                  {appliedPromo.benefitType !== 'addon' && (
                    <span style={{ color: '#166534', fontWeight: 600 }}>−{fmtMoney(promoDiscount, ctx.reseller.currency)}</span>
                  )}
                </div>
                <button type="button" onClick={removePromo} style={{ background: 'none', border: 'none', color: '#b91c1c', fontSize: 12, cursor: 'pointer', padding: '2px 0' }}>
                  {t('sp_promo_embed_remove', 'Remove')}
                </button>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 4 }}>{t('sp_promo_embed_label', 'Promo code')}</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={promoInput} onChange={e => setPromoInput(e.target.value.toUpperCase())}
                    placeholder={t('sp_promo_embed_placeholder', 'Enter a code')}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); applyPromo() } }}
                    style={{ flex: 1, padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, fontFamily: 'monospace', textTransform: 'uppercase' }} />
                  <button type="button" onClick={applyPromo} disabled={!promoInput.trim() || promoBusy}
                    style={{ padding: '8px 14px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 700, cursor: (!promoInput.trim() || promoBusy) ? 'not-allowed' : 'pointer', opacity: (!promoInput.trim() || promoBusy) ? 0.5 : 1 }}>
                    {promoBusy ? t('loading_ellipsis', 'Loading…') : t('sp_promo_embed_apply', 'Apply')}
                  </button>
                </div>
                {promoErr && <div style={{ color: '#b91c1c', fontSize: 12, marginTop: 4 }}>{promoErr}</div>}
              </div>
            )}
          </div>

          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
            marginTop: 8, paddingTop: 8, borderTop: '1px solid #e2e8f0',
            fontSize: 15, fontWeight: 700, color: '#0f172a',
          }}>
            <span>{totalTax > 0 ? t('sp_trip_total_incl_tax', 'Trip total (incl. tax)') : t('sp_trip_total', 'Trip total')}</span>
            <span>{fmtMoney(total, ctx.reseller.currency)}</span>
          </div>
          <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
            {[
              t(form.numGolfers === 1 ? 'sp_golfer_count_singular' : 'sp_golfers_count', `{count} golfer${form.numGolfers !== 1 ? 's' : ''}`, { count: form.numGolfers }),
              t(detail.package.nights === 1 ? 'sp_night_count_singular' : 'sp_nights_count', `{count} night${detail.package.nights !== 1 ? 's' : ''}`, { count: detail.package.nights }),
              t(form.rounds.length === 1 ? 'sp_round_count_singular' : 'sp_rounds_count', `{count} round${form.rounds.length !== 1 ? 's' : ''}`, { count: form.rounds.length }),
            ].join(' · ')}
          </div>
        </div>
      )}

      {/* ─── Wizard nav ─────────────────────────────────────────── */}
      <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
        {wizardStep > 1 && (
          <button type="button" onClick={prevStep} style={secondaryBtn}>
            {t('sp_back', '← Back')}
          </button>
        )}
        <button
          type="submit"
          style={{
            flex: 1,
            padding: 14,
            background: canAdvance() ? primary : '#cbd5e1',
            color: '#fff',
            border: 'none',
            borderRadius: 8,
            fontSize: 15,
            fontWeight: 700,
            cursor: canAdvance() ? 'pointer' : 'not-allowed',
          }}
        >
          {wizardStep < TOTAL_STEPS
            ? t('sp_continue', 'Continue →')
            : (detail.package.maxRevenueCapEnabled && capLoading && !capMaxWaitReached)
              ? t('sp_maxrev_searching', 'Searching for the best price…')
              : t('sp_review_and_agree', 'Review and agree → ({amount})', { amount: fmtMoney(displayTotal, ctx.reseller.currency) })}
        </button>
      </div>
      <HowItWorks data={ctx.howItWorks} primary={primary} />
      {wizardStep === TOTAL_STEPS && (
        <p style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', marginTop: 8 }}>
          {t('sp_next_review_no_charge', "Next: review your trip + agree to each provider's cancellation policy. No charge yet.")}
        </p>
      )}
    </form>
  )
}

// ─── "How it works" disclosure (EPIC #106 P1) ───────────────────────────
// Shown under the Continue button when the reseller enabled it on their
// brand profile. Body is bilingual Markdown authored by the reseller; we
// render the one matching the embed's current language (falling back to the
// other if blank), sanitized with DOMPurify before injection.
function HowItWorks({ data, primary }) {
  const { t, langId } = useLanguage()
  const [open, setOpen] = useState(false)
  if (!data || !data.enabled) return null
  const body = ((langId === 2 ? data.fr : data.en) || data.en || data.fr || '').trim()
  if (!body) return null
  const html = DOMPurify.sanitize(marked.parse(body, { breaks: true }))
  return (
    <div style={{ marginTop: 12, textAlign: 'center' }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', color: primary || '#0f172a', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 4, textDecoration: 'underline' }}
      >
        {t('sp_how_it_works', 'How it works?')} {open ? '▲' : '▼'}
      </button>
      {open && (
        <div
          style={{ marginTop: 8, padding: 16, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, lineHeight: 1.6, color: '#334155', textAlign: 'left' }}
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
    </div>
  )
}

// ─── Pay screen ─────────────────────────────────────────────────────────
//
// Card capture form. The raw card fields are sent to /bookings/:id/payments,
// which runs the real Moneris pre-auth server-side (production) or synthesizes
// a successful auth (dev with RESELLER_FAKE_PAYMENTS=1) and kicks off the
// booking sequencer. The card is authorized now and captured after the hotel
// + tee times are locked in.
// Split-pay: the leader enters each golfer + their share (even by default,
// editable, must total the trip price), then we email each golfer a pay link.
function SplitSetupScreen({ total, currency, numGolfers, leaderGolfer, onSubmit, onBack, submitting, err, primary, branding }) {
  const { t } = useLanguage()
  const [rows, setRows] = useState(() => {
    const n = Math.max(2, Number(numGolfers) || 2)
    const arr = [{ name: leaderGolfer?.name || '', email: leaderGolfer?.email || '', amount: '', isLeader: true }]
    for (let i = 1; i < n; i++) arr.push({ name: '', email: '', amount: '', isLeader: false })
    return arr
  })

  function setRow(i, k, v) { setRows(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r))) }
  function addRow() { setRows([...rows, { name: '', email: '', amount: '', isLeader: false }]) }
  function removeRow(i) { if (rows[i].isLeader) return; setRows(rows.filter((_, idx) => idx !== i)) }
  function evenSplit() {
    const n = rows.length
    const cents = Math.round((Number(total) || 0) * 100)
    const base = Math.floor(cents / n)
    const rem = cents - base * n // remainder pennies go to the leader
    setRows(rows.map((r, i) => ({ ...r, amount: (((i === 0 ? base + rem : base)) / 100).toFixed(2) })))
  }

  const sum = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0)
  const totalNum = Number(total) || 0
  const balanced = Math.abs(sum - totalNum) < 0.01
  const allValid = rows.every(r => r.name.trim() && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(r.email) && Number(r.amount) > 0)
  const ready = balanced && allValid && rows.length >= 2

  function submit(e) {
    e.preventDefault()
    if (!ready) return
    onSubmit(rows.map(r => ({ name: r.name.trim(), email: r.email.trim(), amount: Number(r.amount), isLeader: !!r.isLeader })))
  }

  const amtInp = { ...inp, width: 100, textAlign: 'right' }
  return (
    <form onSubmit={submit}>
      <button type="button" onClick={onBack} disabled={submitting} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, marginBottom: 12, fontSize: 13 }}>← {t('sp_pay_in_full_instead', 'Pay in full instead')}</button>
      <h1 style={{ margin: '0 0 4px', fontSize: 24, ...headerFontStyle(branding) }}>{t('sp_split_with_friends', 'Split with friends')}</h1>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 16 }}>
        {t('sp_split_intro', "Enter each golfer and their share. We'll email everyone a secure link to pay their portion — the trip is booked once everyone has paid.")}
      </p>
      {err && <div style={errorBox}>{err}</div>}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((r, i) => (
          <div key={i} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 12, fontWeight: 600, color: r.isLeader ? primary : '#64748b' }}>{r.isLeader ? t('sp_you', 'You') : t('sp_golfer_n', 'Golfer {n}', { n: i + 1 })}</span>
              {!r.isLeader && <button type="button" onClick={() => removeRow(i)} style={{ background: 'none', border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 18, lineHeight: 1 }} aria-label="Remove">×</button>}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: '1 1 140px', minWidth: 0 }}>
                <input type="text" placeholder={t('sp_name', 'Name')} value={r.name} onChange={e => setRow(i, 'name', e.target.value)} style={inp} />
              </div>
              <div style={{ flex: '1 1 180px', minWidth: 0 }}>
                <input type="email" placeholder={t('sp_email', 'Email')} value={r.email} onChange={e => setRow(i, 'email', e.target.value)} style={inp} />
              </div>
              <div>
                <input type="text" inputMode="decimal" placeholder="0.00" value={r.amount} onChange={e => setRow(i, 'amount', e.target.value.replace(/[^\d.]/g, ''))} style={amtInp} />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
        <button type="button" onClick={addRow} style={secondaryBtn}>+ {t('sp_add_golfer', 'Add golfer')}</button>
        <button type="button" onClick={evenSplit} style={secondaryBtn}>{t('sp_split_evenly', 'Split evenly')}</button>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, padding: '10px 14px', background: balanced ? '#ecfdf5' : '#fff7ed', border: '1px solid ' + (balanced ? '#a7f3d0' : '#fed7aa'), borderRadius: 8 }}>
        <span style={{ fontSize: 13, color: '#475569' }}>{t('sp_shares_total', 'Shares total')}</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: balanced ? '#065f46' : '#9a3412' }}>
          {fmtMoney(sum, currency)} / {fmtMoney(totalNum, currency)} {balanced ? '✓' : `(${t('sp_must_match', 'must match')})`}
        </span>
      </div>

      <button type="submit" disabled={!ready || submitting} style={{ width: '100%', padding: 16, marginTop: 16, background: !ready || submitting ? '#cbd5e1' : primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: !ready || submitting ? 'not-allowed' : 'pointer' }}>
        {submitting ? t('sp_sending', 'Sending…') : t('sp_send_invites_pay_share', 'Send invites & pay my share →')}
      </button>
    </form>
  )
}

// After the leader pays their share, this polls each golfer's status and lets
// the leader copy each pay link (in case an email is lost). When everyone has
// paid, the booking advances and onConfirmed fires.
function SplitWaitingScreen({ api, bookingId, magicToken, resellerSlug, currency, onConfirmed, primary, branding }) {
  const { t } = useLanguage()
  const [data, setData] = useState(null)
  const [now, setNow] = useState(Date.now())
  const [copiedId, setCopiedId] = useState(null)
  const [revealed, setRevealed] = useState({})
  const [extending, setExtending] = useState(false)
  const [extendErr, setExtendErr] = useState(null)

  async function refresh() {
    const r = await api.get(`/bookings/${bookingId}/payment-status?token=${encodeURIComponent(magicToken)}`)
    if (r.status >= 400) return
    setData(r.data)
    if (['payments_complete', 'booking_in_progress', 'confirmed', 'partially_confirmed'].includes(r.data.bookingStatus)) onConfirmed()
  }

  useEffect(() => {
    let cancelled = false
    refresh()
    const pollId = setInterval(() => { if (!cancelled) refresh() }, 5000)
    const tickId = setInterval(() => setNow(Date.now()), 1000)
    return () => { cancelled = true; clearInterval(pollId); clearInterval(tickId) }
  }, [api, bookingId, magicToken]) // eslint-disable-line react-hooks/exhaustive-deps

  async function extendHold() {
    setExtending(true); setExtendErr(null)
    const r = await api.post(`/bookings/${bookingId}/extend-hold`, { token: magicToken })
    setExtending(false)
    if (r.status >= 400) { setExtendErr(r.data?.message || t('sp_extend_failed', 'Could not extend the hold.')); return }
    refresh()
  }

  const payLink = (tok) => `${window.location.origin}/r/${resellerSlug}/embed/reseller-pay-share?b=${bookingId}&t=${tok}`
  function copy(p) {
    try { navigator.clipboard?.writeText(payLink(p.magicToken)) } catch { /* noop */ }
    setCopiedId(p.id); setTimeout(() => setCopiedId(null), 1500)
  }

  const payments = data?.payments || []
  const isPaid = (s) => ['authorized', 'captured'].includes(s)
  const paidCount = payments.filter(p => isPaid(p.status)).length
  const allPaid = payments.length > 0 && paidCount === payments.length

  const deadline = data?.rateHoldExpiresAt ? new Date(data.rateHoldExpiresAt).getTime() : null
  const secLeft = deadline != null ? Math.max(0, Math.ceil((deadline - now) / 1000)) : null
  const expired = secLeft != null && secLeft <= 0
  const mmss = secLeft != null ? `${Math.floor(secLeft / 60)}:${String(secLeft % 60).padStart(2, '0')}` : null
  const extRemaining = data?.extensionsRemaining ?? 0

  return (
    <div>
      <h1 style={{ margin: '0 0 4px', fontSize: 24, ...headerFontStyle(branding) }}>{t('sp_waiting_for_group', 'Waiting for the group')}</h1>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 16 }}>
        {t('sp_your_share_paid_waiting', 'Your share is paid. We\'ll lock in the hotel + tee times and email everyone once all shares are in.')}
      </p>

      {/* Rate-hold countdown + extend. Hidden once everyone has paid. */}
      {!allPaid && deadline != null && (
        <div style={{ padding: '12px 14px', borderRadius: 10, marginBottom: 14, background: expired ? '#fff7ed' : '#f8fafc', border: '1px solid ' + (expired ? '#fed7aa' : '#e2e8f0') }}>
          {expired ? (
            <div style={{ fontSize: 13, color: '#9a3412', fontWeight: 600 }}>⏱ {t('sp_hold_expired', 'The quoted rates may no longer be held — the remaining golfers should pay now.')}</div>
          ) : (
            <div style={{ fontSize: 14, color: '#0f172a' }}>⏱ {t('sp_rates_held_for', 'Rates held for')} <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{mmss}</strong></div>
          )}
          {extRemaining > 0 ? (
            <div style={{ marginTop: 8 }}>
              <button type="button" onClick={extendHold} disabled={extending} style={{ ...secondaryBtn, fontSize: 13 }}>
                {extending ? t('sp_checking_availability', 'Checking availability…') : t('sp_recheck_extend', 'Re-check & extend 15 min')}
              </button>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{t('sp_extends_left', '{n} extension(s) left', { n: extRemaining })} · {t('sp_extend_note', "we'll confirm the hotel + tee times are still available first")}</div>
            </div>
          ) : (
            <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>{t('sp_max_hold', 'Maximum hold reached — the remaining golfers need to pay now.')}</div>
          )}
          {extendErr && <div style={{ ...errorBox, marginTop: 8, marginBottom: 0, fontSize: 12 }}>{extendErr}</div>}
        </div>
      )}

      {data && (
        <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a', marginBottom: 12 }}>
          {paidCount} {t('of', 'of')} {payments.length} {t('sp_golfers_paid', 'golfers paid')}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {payments.map(p => {
          const paid = isPaid(p.status)
          return (
            <div key={p.id} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{p.payerName}{p.isSplitLeader ? ` (${t('sp_you', 'You')})` : ''}</div>
                  <div style={{ color: '#64748b', fontSize: 12 }}>{fmtMoney(p.amount, p.currency || currency)}</div>
                </div>
                <span style={{ flexShrink: 0, padding: '3px 10px', borderRadius: 999, fontSize: 11, fontWeight: 600, background: paid ? '#d1fae5' : '#fef3c7', color: paid ? '#065f46' : '#92400e' }}>
                  {paid ? t('sp_paid', 'Paid') : t('sp_awaiting', 'Awaiting')}
                </span>
              </div>
              {!p.isSplitLeader && !paid && p.magicToken && (
                <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px dashed #e2e8f0', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                  <button type="button" onClick={() => copy(p)} style={{ ...secondaryBtn, fontSize: 12 }}>{copiedId === p.id ? t('sp_copied', 'Copied!') : t('sp_copy_link', 'Copy pay link')}</button>
                  <button type="button" onClick={() => setRevealed(v => ({ ...v, [p.id]: !v[p.id] }))} style={{ background: 'none', border: 'none', color: primary, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>{revealed[p.id] ? t('sp_hide', 'Hide') : t('sp_show_link', 'Show link')}</button>
                  {revealed[p.id] && <input readOnly value={payLink(p.magicToken)} onFocus={e => e.target.select()} style={{ ...inp, flex: '1 1 100%', fontSize: 11, marginTop: 4 }} />}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </div>
  )
}

// Order summary shown above the card fields on every pay screen so the customer
// sees exactly what they're buying + the price + tax before entering a card.
function OrderSummary({ title, lines = [], subtotal, savings, savingsLabel, tax, taxRate, total, currency, branding }) {
  const { t } = useLanguage()
  const m = (n) => fmtMoney(n, currency)
  const taxPct = taxRate ? ` (${(taxRate * 100).toFixed(taxRate * 100 % 1 ? 1 : 0)}%)` : ''
  const Row = ({ label, val, color, bold }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: bold ? 15 : 13, color: color || '#475569', fontWeight: bold ? 800 : 400, marginTop: bold ? 8 : 4 }}>
      <span>{label}</span><span>{val}</span>
    </div>
  )
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16, marginBottom: 16 }}>
      {title && <div style={{ fontSize: 15, fontWeight: 700, color: '#0f172a', ...headerFontStyle(branding) }}>{title}</div>}
      {lines.filter(Boolean).map((l, i) => <div key={i} style={{ fontSize: 12.5, color: '#64748b', marginTop: 2 }}>{l}</div>)}
      <div style={{ borderTop: '1px solid #eef2f7', margin: '12px 0 4px' }} />
      {subtotal != null && <Row label={t('sp_pay_subtotal', 'Subtotal')} val={m(subtotal)} />}
      {savings > 0 && <Row label={savingsLabel || t('sp_pay_discount', 'Discount')} val={'− ' + m(savings)} color="#16a34a" />}
      {tax != null && <Row label={t('sp_pay_tax', 'Tax') + taxPct} val={m(tax)} />}
      <div style={{ borderTop: '1px solid #e2e8f0', marginTop: 8, paddingTop: 4 }} />
      <Row label={t('sp_pay_total', 'Total')} val={m(total)} bold color="#0f172a" />
    </div>
  )
}

function PayScreen({ booking, total, currency, onPay, onBack, submitting, err, primary, branding, heading, summary }) {
  const { t } = useLanguage()
  const [card, setCard] = useState({ number: '', expiry: '', cvc: '', name: '' })

  function submit(e) {
    e.preventDefault()
    onPay(card) // { number, expiry, cvc, name }
  }

  // Format card number as 4-digit groups, expiry as MM / YY.
  function setNum(v) {
    const digits = v.replace(/\D/g, '').slice(0, 19)
    setCard(c => ({ ...c, number: digits.replace(/(.{4})/g, '$1 ').trim() }))
  }
  function setExp(v) {
    const digits = v.replace(/\D/g, '').slice(0, 4)
    setCard(c => ({ ...c, expiry: digits.length >= 3 ? digits.slice(0, 2) + ' / ' + digits.slice(2) : digits }))
  }
  function setCvc(v) { setCard(c => ({ ...c, cvc: v.replace(/\D/g, '').slice(0, 4) })) }

  const ready = card.number.replace(/\s/g, '').length >= 13
                && card.expiry.replace(/\D/g, '').length === 4
                && card.cvc.length >= 3
                && card.name.trim().length >= 2

  return (
    <form onSubmit={submit}>
      <button type="button" onClick={onBack} disabled={submitting} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, marginBottom: 12, fontSize: 13 }}>{t('sp_back', '← Back')}</button>
      <h1 style={{ margin: '0 0 4px', fontSize: 24, ...headerFontStyle(branding) }}>{heading || t('sp_store_payment', 'Payment')}</h1>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 16 }}>
        {t('sp_pay_auth_note', "Card is authorized now (not charged). We'll capture the charge once we've locked in your hotel and tee times. If anything fails, the authorization is voided.")}
      </p>

      {err && <div style={errorBox}>{err}</div>}

      {summary}

      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 18 }}>
        <Field label={t('sp_store_cardholder', 'Cardholder name')}>
          <input type="text" required value={card.name} onChange={e => setCard(c => ({ ...c, name: e.target.value }))} style={inp} />
        </Field>
        <Field label={t('sp_store_card_number', 'Card number')}>
          <input type="text" required inputMode="numeric" autoComplete="cc-number" placeholder="1234 5678 9012 3456" value={card.number} onChange={e => setNum(e.target.value)} style={{ ...inp, fontFamily: 'monospace', letterSpacing: 1 }} />
        </Field>
        <div style={row}>
          <Field label={t('sp_store_card_expiry', 'Expiry (MM/YY)')} style={{ flex: 1 }}>
            <input type="text" required inputMode="numeric" autoComplete="cc-exp" placeholder="08 / 28" value={card.expiry} onChange={e => setExp(e.target.value)} style={{ ...inp, fontFamily: 'monospace' }} />
          </Field>
          <Field label={t('sp_store_card_cvc', 'CVC')} style={{ flex: 1 }}>
            <input type="text" required inputMode="numeric" autoComplete="cc-csc" placeholder="123" value={card.cvc} onChange={e => setCvc(e.target.value)} style={{ ...inp, fontFamily: 'monospace' }} />
          </Field>
        </div>
        <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8' }}>
          {t('sp_pay_authorizing_amount', 'Authorizing {amount}.', { amount: fmtMoney(total, currency) })}
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting || !ready}
        style={{
          width: '100%',
          padding: 16,
          marginTop: 16,
          background: submitting || !ready ? '#cbd5e1' : primary,
          color: '#fff',
          border: 'none',
          borderRadius: 8,
          fontSize: 15,
          fontWeight: 700,
          cursor: submitting || !ready ? 'not-allowed' : 'pointer',
        }}
      >
        {submitting ? t('sp_pay_authorizing', 'Authorizing…') : t('sp_pay_authorize_amount', 'Authorize {amount}', { amount: fmtMoney(total, currency) })}
      </button>
    </form>
  )
}

// ─── Stripe pay screen (Mode B) ─────────────────────────────────────────
//
// The card never touches our server. Stripe Elements tokenizes it in the
// browser with the reseller's PUBLISHABLE key; we send only the
// paymentMethodId, and the server pre-authorizes on the reseller's own Stripe
// account. If the card needs a 3DS/SCA challenge, it runs inline via
// stripe.handleCardAction, then confirm-stripe finalizes server-side.
// NMI (Payroc-branded) Mode B pay screen. Card data is tokenized entirely in
// the browser by Collect.js (secure iframes hosted by NMI) — we only ever see
// the resulting payment_token, which we POST to /payments. The token is
// pre-authorized on the reseller's own NMI account server-side.
function NmiPayScreen({ api, booking, total, currency, golfer, onConfirmed, onBack, primary, branding, summary }) {
  const { t } = useLanguage()
  const [name, setName] = useState(golfer?.name || '')
  const [ready, setReady] = useState(false) // Collect.js secure fields mounted
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  const onTokenRef = useRef(null) // configure() callback calls the latest handler

  useEffect(() => {
    const base = String(booking.nmiBaseUrl || 'https://secure.nmi.com').replace(/\/+$/, '')
    let cancelled = false
    function configure() {
      if (!window.CollectJS) return
      window.CollectJS.configure({
        variant: 'inline',
        fields: {
          ccnumber: { selector: '#nmi-ccnumber', placeholder: 'Card number' },
          ccexp:    { selector: '#nmi-ccexp', placeholder: 'MM / YY' },
          cvv:      { selector: '#nmi-cvv', placeholder: 'CVV' },
        },
        fieldsAvailableCallback: () => { if (!cancelled) setReady(true) },
        validationCallback: (field, ok, message) => { if (!ok && message && !cancelled) setErr(message) },
        callback: (resp) => { const h = onTokenRef.current; if (h) h(resp) },
      })
    }
    const existing = document.getElementById('nmi-collectjs')
    if (window.CollectJS) { configure() }
    else if (!existing) {
      const s = document.createElement('script')
      s.id = 'nmi-collectjs'
      s.src = base + '/token/Collect.js'
      s.async = true
      s.setAttribute('data-tokenization-key', booking.nmiTokenizationKey || '')
      s.onload = () => { if (!cancelled) configure() }
      s.onerror = () => { if (!cancelled) setErr(t('sp_pay_err_card_form_load', 'Could not load the secure card form. Please refresh and try again.')) }
      document.body.appendChild(s)
    } else {
      existing.addEventListener('load', configure)
      if (window.CollectJS) configure()
    }
    return () => { cancelled = true }
  }, [booking.nmiBaseUrl, booking.nmiTokenizationKey])

  function submit(e) {
    e.preventDefault()
    setErr(null); setBusy(true)
    // Collect.js hands the token back via the configure() callback; wire the
    // latest POST handler, then trigger tokenization.
    onTokenRef.current = async (resp) => {
      try {
        if (!resp || !resp.token) throw new Error(t('sp_pay_err_card_incomplete', 'Card details are incomplete — please re-check and try again.'))
        const r = await api.post(`/bookings/${booking.bookingId}/payments`, {
          paymentToken: resp.token,
          amount: total,
          payerName: golfer?.name,
          payerEmail: golfer?.email,
        })
        if (r.status >= 400 || r.data?.ok === false) throw new Error(r.data?.message || t('sp_pay_failed', 'Payment failed ({status})', { status: r.status }))
        onConfirmed()
      } catch (e2) {
        setErr(e2.message); setBusy(false)
      }
    }
    if (window.CollectJS) window.CollectJS.startPaymentRequest()
    else { setErr(t('sp_pay_err_card_loading', 'Card form is still loading — one moment.')); setBusy(false) }
  }

  const collectField = { ...inp, padding: '11px 12px', height: 44 }
  const canPay = ready && name.trim().length >= 2 && !busy
  return (
    <form onSubmit={submit}>
      <button type="button" onClick={onBack} disabled={busy} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, marginBottom: 12, fontSize: 13 }}>← {t('sp_pay_back_review', 'Back to review')}</button>
      <h1 style={{ margin: '0 0 4px', fontSize: 24, ...headerFontStyle(branding) }}>{t('sp_store_payment', 'Payment')}</h1>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 16 }}>
        {t('sp_pay_auth_note', "Card is authorized now (not charged). We'll capture the charge once we've locked in your hotel and tee times. If anything fails, the authorization is voided.")}
      </p>
      {err && <div style={errorBox}>{err}</div>}
      {summary}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 18 }}>
        <Field label={t('sp_store_cardholder', 'Cardholder name')}>
          <input type="text" required value={name} onChange={e => setName(e.target.value)} style={inp} />
        </Field>
        <Field label={t('sp_store_card_number', 'Card number')}><div id="nmi-ccnumber" style={collectField} /></Field>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><Field label={t('sp_store_card_expiry_short', 'Expiry')}><div id="nmi-ccexp" style={collectField} /></Field></div>
          <div style={{ flex: 1 }}><Field label={t('sp_store_card_cvv', 'CVV')}><div id="nmi-cvv" style={collectField} /></Field></div>
        </div>
        <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8' }}>
          {t('sp_pay_authorizing_amount', 'Authorizing {amount}.', { amount: fmtMoney(total, currency) })} {t('sp_store_secured_nmi', 'Secured by NMI.')}
        </div>
      </div>
      <button type="submit" disabled={!canPay} style={{ marginTop: 16, width: '100%', padding: '13px 16px', background: canPay ? primary : '#cbd5e1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: canPay ? 'pointer' : 'not-allowed' }}>
        {busy ? t('sp_pay_authorizing', 'Authorizing…') : t('sp_pay_authorize_amount', 'Authorize {amount}', { amount: fmtMoney(total, currency) })}
      </button>
    </form>
  )
}

function StripePayWrapper({ publishableKey, ...rest }) {
  const stripePromise = useMemo(() => loadStripe(publishableKey), [publishableKey])
  return (
    <Elements stripe={stripePromise}>
      <StripePayScreen {...rest} />
    </Elements>
  )
}

function StripePayScreen({ api, booking, total, currency, golfer, onConfirmed, onBack, primary, branding, summary }) {
  const { t } = useLanguage()
  const stripe = useStripe()
  const elements = useElements()
  const [name, setName] = useState(golfer?.name || '')
  const [cardReady, setCardReady] = useState(false)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)

  async function submit(e) {
    e.preventDefault()
    if (!stripe || !elements) { setErr(t('sp_pay_err_form_loading', 'Payment form is still loading — one moment.')); return }
    setErr(null); setBusy(true)
    try {
      const card = elements.getElement(CardElement)
      const { error: pmErr, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card', card, billing_details: { name, email: golfer?.email || undefined },
      })
      if (pmErr) throw new Error(pmErr.message)
      const r = await api.post(`/bookings/${booking.bookingId}/payments`, {
        paymentMethodId: paymentMethod.id,
        amount: total,
        payerName: golfer?.name,
        payerEmail: golfer?.email,
      })
      // 3DS / SCA challenge required — run it inline, then finalize server-side.
      if (r.status === 202 && r.data?.requiresAction && r.data?.clientSecret) {
        const { error: actErr } = await stripe.handleCardAction(r.data.clientSecret)
        if (actErr) throw new Error(actErr.message)
        const c = await api.post(`/bookings/${booking.bookingId}/payments/confirm-stripe`, { paymentId: r.data.paymentId })
        if (c.status >= 400 || c.data?.ok === false) throw new Error(c.data?.message || t('sp_pay_err_finalize', 'Could not finalize the authorization.'))
        onConfirmed(); return
      }
      if (r.status >= 400 || r.data?.ok === false) throw new Error(r.data?.message || t('sp_pay_failed', 'Payment failed ({status})', { status: r.status }))
      onConfirmed()
    } catch (e2) {
      setErr(e2.message)
    } finally { setBusy(false) }
  }

  const canPay = !!stripe && cardReady && name.trim().length >= 2 && !busy
  return (
    <form onSubmit={submit}>
      <button type="button" onClick={onBack} disabled={busy} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, marginBottom: 12, fontSize: 13 }}>← {t('sp_pay_back_review', 'Back to review')}</button>
      <h1 style={{ margin: '0 0 4px', fontSize: 24, ...headerFontStyle(branding) }}>{t('sp_store_payment', 'Payment')}</h1>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 16 }}>
        {t('sp_pay_auth_note', "Card is authorized now (not charged). We'll capture the charge once we've locked in your hotel and tee times. If anything fails, the authorization is voided.")}
      </p>
      {err && <div style={errorBox}>{err}</div>}
      {summary}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 18 }}>
        <Field label={t('sp_store_cardholder', 'Cardholder name')}>
          <input type="text" required value={name} onChange={e => setName(e.target.value)} style={inp} />
        </Field>
        <Field label={t('sp_store_card_details', 'Card details')}>
          <div style={{ ...inp, padding: '13px 12px' }}>
            <CardElement
              options={{ style: { base: { fontSize: '15px', color: '#0f172a', '::placeholder': { color: '#94a3b8' } }, invalid: { color: '#b91c1c' } } }}
              onChange={ev => { setCardReady(ev.complete); setErr(ev.error ? ev.error.message : null) }}
            />
          </div>
        </Field>
        <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8' }}>
          {t('sp_pay_authorizing_amount', 'Authorizing {amount}.', { amount: fmtMoney(total, currency) })} {t('sp_store_secured_stripe', 'Secured by Stripe.')}
        </div>
      </div>
      <button
        type="submit"
        disabled={!canPay}
        style={{
          width: '100%', padding: 16, marginTop: 16,
          background: !canPay ? '#cbd5e1' : primary, color: '#fff',
          border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700,
          cursor: !canPay ? 'not-allowed' : 'pointer',
        }}
      >
        {busy ? t('sp_pay_authorizing', 'Authorizing…') : t('sp_pay_authorize_amount', 'Authorize {amount}', { amount: fmtMoney(total, currency) })}
      </button>
    </form>
  )
}

// ─── Confirmed ──────────────────────────────────────────────────────────

function ConfirmedScreen({ booking, brand, reseller }) {
  const { t } = useLanguage()
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 12, color: brand?.primaryColor || '#00d19f' }}>✓</div>
      <h1 style={{ margin: '0 0 8px', fontSize: 24 }}>{t('sp_pay_confirmed_title', 'Booking confirmed')}</h1>
      <p style={{ color: '#64748b', marginBottom: 24 }}>
        {t('sp_pay_confirmed_body', "We've emailed your confirmation. Booking #{id}.", { id: booking.bookingId })}
      </p>
      <a href={booking.myTripUrl} style={{ display: 'inline-block', padding: '12px 28px', background: brand?.primaryColor || '#0f172a', color: '#fff', textDecoration: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600 }}>
        {t('sp_pay_manage_trip', 'Manage my trip')}
      </a>
    </div>
  )
}

// ─── EPIC #116 — Golf-bundle storefront ────────────────────────────────
//
// A bundle store sells prepaid-round passes. Flow: browse the bundle cards
// (shared PreviewWidget, mode="bundles") → checkout (buyer info + optional
// gift) → confirmed. There's no hotel / tee-time selection — the golfer pays
// for rounds now and books them later from "My Rounds" (separate phase).

const DAY_ABBR_EMBED = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
function bundleDaysText(mask) {
  const s = String(mask || '1111111')
  if (!/^[01]{7}$/.test(s) || s === '1111111') return null
  if (s === '1111100') return 'Mon–Fri'
  if (s === '0000011') return 'Weekends'
  const on = []
  for (let i = 0; i < 7; i++) if (s[i] === '1') on.push(DAY_ABBR_EMBED[i])
  return on.join(', ') || null
}
function bundleExpiryText(d) {
  if (!d) return null
  const dt = new Date(String(d).slice(0, 10) + 'T00:00:00')
  if (Number.isNaN(dt.getTime())) return null
  return dt.toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' })
}

function BundleStore({ ctx, api, brand, branding, reseller }) {
  const { t, langId } = useLanguage()
  const [view, setView] = useState('browse') // browse | checkout | confirmed
  const [detail, setDetail] = useState(null)
  const [loadingDetail, setLoadingDetail] = useState(false)
  const [result, setResult] = useState(null)
  const currency = ctx?.reseller?.currency || 'CAD'
  // Resolve each bundle's description to the language the golfer is browsing
  // in (French falls back to English when no French copy exists).
  const bundles = (ctx.bundles || []).map(b => ({
    ...b,
    Description: (langId === 2 && b.DescriptionFr) ? b.DescriptionFr : b.Description,
  }))
  const groupingDesc = (langId === 2 && ctx.grouping?.descriptionFr)
    ? ctx.grouping.descriptionFr
    : ctx.grouping?.description

  function selectBundle(bnd) {
    setLoadingDetail(true); setDetail(null); setView('checkout')
    api.get(`/bundles/${bnd.Slug}`).then(r => {
      setDetail(r.status >= 400 ? { error: r.data?.message || 'Unavailable' } : r.data)
    }).catch(e => setDetail({ error: e.message })).finally(() => setLoadingDetail(false))
  }

  if (view === 'confirmed' && result) {
    return <BundleConfirmed result={result} brand={brand} branding={branding} reseller={reseller}
              onDone={() => { setResult(null); setDetail(null); setView('browse') }} />
  }
  if (view === 'checkout') {
    return <BundleCheckout detail={detail} loading={loadingDetail} api={api} branding={branding} currency={currency}
              taxRates={ctx.taxRates || []}
              checkoutLive={ctx.bundleCheckoutLive === true}
              onBack={() => { setDetail(null); setView('browse') }}
              onPurchased={(res) => { setResult(res); setView('confirmed') }} />
  }
  return (
    <>
      {groupingDesc && <p style={{ color: '#475569', marginTop: 0, marginBottom: 24, fontSize: 15, lineHeight: 1.5, ...bodyFontStyle(branding) }}>{groupingDesc}</p>}
      {(!bundles || bundles.length === 0)
        ? <div style={empty}>{t('sp_store_no_bundles', 'No bundles are available right now.')}</div>
        : <PreviewWidget mode="bundles" packages={bundles} branding={branding} onPackageClick={selectBundle} suppressPoweredBy />}
    </>
  )
}

function BackLink({ onBack }) {
  const { t } = useLanguage()
  return (
    <button onClick={onBack} style={{ ...secondaryBtn, marginBottom: 16 }}>
      ← {t('sp_store_back', 'Back to bundles')}
    </button>
  )
}

function BundleCheckout({ detail, loading, api, branding, currency, taxRates, checkoutLive, onBack, onPurchased }) {
  const { t, langId } = useLanguage()
  const primary = branding?.primaryColor || '#0f172a'
  const [buyer, setBuyer] = useState({ name: '', email: '', phone: '' })
  const [isGift, setIsGift] = useState(false)
  const [gift, setGift] = useState({ recipientName: '', recipientEmail: '', message: '' })
  const [step, setStep] = useState('details') // details | pay
  const [err, setErr] = useState(null)

  if (loading || !detail) return <div style={{ padding: 40, textAlign: 'center' }}><Spinner /></div>
  if (detail.error) return <><BackLink onBack={onBack} /><div style={errorBox}>{detail.error}</div></>

  const b = detail.bundle
  const cur = detail.currency || currency
  const desc = (langId === 2 && b.DescriptionFr) ? b.DescriptionFr : b.Description
  // Bundles always add tax at checkout (mirrors the S&P checkout math so what
  // the buyer sees matches what we store). The per-store "defer to redemption"
  // option was removed — a bundle sells specific rounds, a taxable supply.
  const subtotal = Number(b.Price)
  const taxLines = []
  let totalTax = 0
  {
    let runningCompoundBase = subtotal
    for (const tr of (taxRates || [])) {
      if (!tr.rate || subtotal <= 0) continue
      const base = tr.baseMode === 'compound' ? runningCompoundBase : subtotal
      const amt = Math.round(base * tr.rate * 100) / 100
      if (amt <= 0) continue
      taxLines.push({ name: tr.name, rate: tr.rate, amt })
      totalTax += amt
      if (tr.baseMode === 'compound') runningCompoundBase += amt
    }
    totalTax = Math.round(totalTax * 100) / 100
  }
  const grandTotal = Math.round((subtotal + totalTax) * 100) / 100
  const days = bundleDaysText(b.DaysAllowed)
  const timeWin = (b.TimeWindowStart || b.TimeWindowEnd)
    ? `${b.TimeWindowStart ? formatTeeTime(b.TimeWindowStart) : ''}${b.TimeWindowStart && b.TimeWindowEnd ? '–' : ''}${b.TimeWindowEnd ? formatTeeTime(b.TimeWindowEnd) : ''}`
    : null
  const expiry = bundleExpiryText(b.ExpiryDate)

  // Which card form to render + its PUBLIC tokenization material.
  const gateway = detail.gateway || 'moneris'

  // Step 1 -> Step 2: validate the buyer/gift details, then collect the card.
  function goToPay() {
    setErr(null)
    if (!buyer.name.trim() || !buyer.email.trim()) return setErr(t('sp_store_err_buyer', 'Enter your name and email.'))
    if (!isValidPhone(buyer.phone)) return setErr(t('sp_store_err_phone', 'Enter a valid phone number.'))
    if (isGift && (!gift.recipientName.trim() || !gift.recipientEmail.trim())) return setErr(t('sp_store_err_gift', "Enter the recipient's name and email."))
    setStep('pay')
  }

  // Shared purchase body — buyer + gift always identical; each gateway adds its
  // own tokenized payment field.
  function purchaseBody(extra) {
    return {
      bundleSlug: b.Slug,
      buyer: { name: buyer.name.trim(), email: buyer.email.trim(), phone: buyer.phone || null },
      gift: isGift
        ? { isGift: true, recipientName: gift.recipientName.trim(), recipientEmail: gift.recipientEmail.trim(), message: gift.message || null }
        : { isGift: false },
      ...extra,
    }
  }

  // Mode A / Mode B Moneris — raw card posted straight to Moneris (never stored).
  async function chargeMoneris(card) {
    const r = await api.post('/bundle-purchases', purchaseBody({
      card: { number: String(card.number || '').replace(/\s/g, ''), expiry: card.expiry, cvc: card.cvc },
    }))
    if (r.status >= 400 || r.data?.ok === false) throw new Error(r.data?.message || `Payment failed (${r.status})`)
    onPurchased(r.data)
  }

  // Mode B NMI — Collect.js payment_token.
  async function chargeNmi(token) {
    const r = await api.post('/bundle-purchases', purchaseBody({ paymentToken: token }))
    if (r.status >= 400 || r.data?.ok === false) throw new Error(r.data?.message || `Payment failed (${r.status})`)
    onPurchased(r.data)
  }

  // Mode B Stripe — Elements paymentMethodId, with inline 3DS/SCA finalize.
  async function chargeStripe({ paymentMethodId, stripe }) {
    const r = await api.post('/bundle-purchases', purchaseBody({ paymentMethodId }))
    if (r.status === 202 && r.data?.requiresAction && r.data?.clientSecret) {
      const { error } = await stripe.handleCardAction(r.data.clientSecret)
      if (error) throw new Error(error.message)
      const c = await api.post('/bundle-purchases/confirm-stripe', purchaseBody({ intentId: r.data.intentId }))
      if (c.status >= 400 || c.data?.ok === false) throw new Error(c.data?.message || 'Could not finalize the payment.')
      onPurchased(c.data); return
    }
    if (r.status >= 400 || r.data?.ok === false) throw new Error(r.data?.message || `Payment failed (${r.status})`)
    onPurchased(r.data)
  }

  // Mode B Elavon Converge — the browser charges via Checkout.js. Step 1: mint a
  // one-time session token server-side (carries the authoritative amount). Step 2:
  // after ConvergeEmbeddedPayment.pay() approves, verify the ssl_txn_id server-side.
  async function startElavonSession() {
    const r = await api.post('/bundle-elavon-session', purchaseBody({}))
    if (r.status >= 400 || !r.data?.token) throw new Error(r.data?.message || 'Could not start Elavon payment.')
    return r.data // { token, scriptHost }
  }
  async function chargeElavon(elavonTxnId) {
    const r = await api.post('/bundle-purchases', purchaseBody({ elavonTxnId }))
    if (r.status >= 400 || r.data?.ok === false) throw new Error(r.data?.message || `Payment failed (${r.status})`)
    onPurchased(r.data)
  }

  return (
    <>
      <BackLink onBack={onBack} />

      {/* Order summary */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <h2 style={{ margin: 0, fontSize: 20, color: primary, ...headerFontStyle(branding) }}>{b.Name}</h2>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: primary, lineHeight: 1 }}>{fmtMoney(b.Price, cur)}</div>
            <div style={{ fontSize: 11, color: '#94a3b8' }}>{t('sp_store_prepaid', 'prepaid')}</div>
          </div>
        </div>
        <div style={{ fontSize: 13, color: '#475569', margin: '8px 0 0' }}>
          {t('sp_store_rounds_n', '{n} prepaid rounds', { n: b.RoundsCount })}
        </div>
        {desc && <div className="hiw-preview" style={{ fontSize: 13, color: '#475569', margin: '10px 0 0', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: mdToHtml(desc) }} />}
        {detail.courses?.length > 0 && (
          <div style={{ marginTop: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#64748b', marginBottom: 6 }}>{t('sp_store_plays_at', 'Plays at')}</div>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: '#334155' }}>
              {detail.courses.map(c => (
                <li key={c.coursesId}>{c.courseName}{c.city ? <span style={{ color: '#94a3b8' }}> — {c.city}</span> : null}{c.minRounds > 0 ? <span style={{ color: '#94a3b8' }}> · {t('sp_store_min_n', 'min {n}', { n: c.minRounds })}</span> : null}</li>
              ))}
            </ul>
          </div>
        )}
        {(days || timeWin || expiry) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 14 }}>
            {days && <Chip primary={primary}>{days}</Chip>}
            {timeWin && <Chip primary={primary}>{timeWin}</Chip>}
            {expiry && <Chip primary={primary}>{t('sp_store_valid_until', 'Valid until {d}', { d: expiry })}</Chip>}
          </div>
        )}

        {/* Tax is always added at checkout; itemize it when the reseller has
            tax rates configured (otherwise the price is simply the total). */}
        {totalTax > 0 && (
          <div style={{ marginTop: 16, borderTop: '1px solid #f1f5f9', paddingTop: 10 }}>
            <Line label={t('sp_store_subtotal', 'Subtotal')} value={subtotal} />
            {taxLines.map((tl, i) => (
              <Line key={i} label={`${tl.name} (${(tl.rate * 100).toFixed(3).replace(/\.?0+$/, '')}%)`} value={tl.amt} />
            ))}
            <Line label={t('sp_store_total', 'Total')} value={grandTotal} bold />
          </div>
        )}
      </div>

      {!checkoutLive ? (
        <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 24, textAlign: 'center' }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🔜</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#0f172a', marginBottom: 6 }}>
            {t('sp_store_not_live_title', 'Online purchasing is coming soon')}
          </div>
          <div style={{ fontSize: 13, color: '#64748b', lineHeight: 1.5, maxWidth: 420, margin: '0 auto' }}>
            {t('sp_store_not_live_body', 'This bundle isn’t available to buy online just yet. Please check back soon or contact us to purchase.')}
          </div>
        </div>
      ) : step === 'pay' ? (
        <BundlePayStep
          gateway={gateway}
          amount={grandTotal}
          currency={cur}
          primary={primary}
          branding={branding}
          buyerName={buyer.name}
          stripePublishableKey={detail.stripePublishableKey}
          nmiTokenizationKey={detail.nmiTokenizationKey}
          nmiBaseUrl={detail.nmiBaseUrl}
          onBack={() => { setErr(null); setStep('details') }}
          onChargeMoneris={chargeMoneris}
          onChargeNmi={chargeNmi}
          onChargeStripe={chargeStripe}
          onStartElavonSession={startElavonSession}
          onChargeElavon={chargeElavon}
        />
      ) : (<>
      {err && <div style={errorBox}>{err}</div>}

      <Section title={t('sp_store_your_details', 'Your details')}>
        <Field label={t('sp_name_label', 'Name')}>
          <input style={inp} value={buyer.name} onChange={e => setBuyer({ ...buyer, name: e.target.value })} placeholder={t('sp_store_name_ph', 'Your full name')} />
        </Field>
        <div style={row}>
          <Field label={t('sp_email_label', 'Email')} style={{ flex: 1 }}>
            <input style={inp} type="email" value={buyer.email} onChange={e => setBuyer({ ...buyer, email: e.target.value })} placeholder="you@email.com" />
          </Field>
          <Field label={t('sp_store_phone', 'Phone')} style={{ flex: 1 }}>
            <input style={inp} value={buyer.phone} onChange={e => setBuyer({ ...buyer, phone: formatPhone(e.target.value) })} placeholder="(902) 555 - 0100" />
          </Field>
        </div>
      </Section>

      {/* Gift toggle */}
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 14, marginBottom: 20 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', margin: 0 }}>
          <input type="checkbox" checked={isGift} onChange={e => setIsGift(e.target.checked)} style={{ width: 16, height: 16 }} />
          <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{t('sp_store_buy_as_gift', 'Buy as a gift')}</span>
        </label>
        {isGift && (
          <div style={{ marginTop: 12 }}>
            <div style={row}>
              <Field label={t('sp_store_recipient_name', "Recipient's name")} style={{ flex: 1 }}>
                <input style={inp} value={gift.recipientName} onChange={e => setGift({ ...gift, recipientName: e.target.value })} />
              </Field>
              <Field label={t('sp_store_recipient_email', "Recipient's email")} style={{ flex: 1 }}>
                <input style={inp} type="email" value={gift.recipientEmail} onChange={e => setGift({ ...gift, recipientEmail: e.target.value })} />
              </Field>
            </div>
            <Field label={t('sp_store_gift_message', 'Message (optional)')}>
              <textarea style={{ ...inp, minHeight: 64, resize: 'vertical' }} value={gift.message} onChange={e => setGift({ ...gift, message: e.target.value })} placeholder={t('sp_store_gift_message_ph', 'Happy birthday — enjoy a round on me!')} />
            </Field>
          </div>
        )}
      </div>

      <button onClick={goToPay}
        style={{ width: '100%', padding: '14px 20px', background: primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 700, cursor: 'pointer' }}>
        {t('sp_store_continue_pay', 'Continue to payment — {price}', { price: fmtMoney(grandTotal, cur) })}
      </button>
      </>)}
    </>
  )
}

// ─── Bundle payment step — dispatches to the resolved gateway's card form ───
//
// A bundle is a one-shot SALE (prepaid rounds, no downstream fulfillment) so
// the card is CHARGED now, not pre-authorized. Card data is tokenized in the
// browser (Stripe Elements / NMI Collect.js) or, for platform Moneris, posted
// straight to Moneris — it never touches our server.
// Elavon Converge customer checkout (Checkout.js) is code-complete but UNVERIFIED
// against a live/sandbox Converge account — flip this to true only after the
// one-time Elavon sandbox test passes. Until then Converge stores show
// "coming soon" instead of an untested card form.
const ELAVON_CONVERGE_CHECKOUT_LIVE = false

export function BundlePayStep({ gateway, amount, currency, primary, branding, buyerName,
                          stripePublishableKey, nmiTokenizationKey, nmiBaseUrl,
                          onBack, onChargeMoneris, onChargeNmi, onChargeStripe,
                          onStartElavonSession, onChargeElavon }) {
  if (gateway === 'stripe') {
    if (!stripePublishableKey) return <PayConfigError onBack={onBack} />
    return (
      <BundleStripeWrapper publishableKey={stripePublishableKey}
        amount={amount} currency={currency} primary={primary} branding={branding}
        buyerName={buyerName} onBack={onBack} onCharge={onChargeStripe} />
    )
  }
  if (gateway === 'nmi') {
    if (!nmiTokenizationKey) return <PayConfigError onBack={onBack} />
    return <BundleNmiPay amount={amount} currency={currency} primary={primary} branding={branding}
      buyerName={buyerName} nmiTokenizationKey={nmiTokenizationKey} nmiBaseUrl={nmiBaseUrl}
      onBack={onBack} onCharge={onChargeNmi} />
  }
  if (gateway === 'elavon_converge') {
    if (!ELAVON_CONVERGE_CHECKOUT_LIVE) return <GatewayComingSoon gatewayLabel="Elavon Converge" onBack={onBack} primary={primary} branding={branding} />
    return <BundleElavonPay amount={amount} currency={currency} primary={primary} branding={branding}
      buyerName={buyerName} onBack={onBack} onStartSession={onStartElavonSession} onCharge={onChargeElavon} />
  }
  // Default: Mode A / Mode B Moneris (raw card).
  return <BundleMonerisPay amount={amount} currency={currency} primary={primary} branding={branding}
    buyerName={buyerName} onBack={onBack} onCharge={onChargeMoneris} />
}

// A gateway that's configured but whose live customer checkout isn't finalized
// yet (e.g. Elavon Converge, pending sandbox verification). Shows a clean
// message instead of a broken/wrong card form.
function GatewayComingSoon({ gatewayLabel, onBack, primary, branding }) {
  const { t } = useLanguage()
  return (
    <div>
      <button type="button" onClick={onBack} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, marginBottom: 12, fontSize: 13 }}>← {t('sp_back', 'Back')}</button>
      <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 12, padding: 32, textAlign: 'center' }}>
        <div style={{ fontSize: 30, marginBottom: 10 }}>🔜</div>
        <h2 style={{ margin: '0 0 8px', fontSize: 20, color: primary || '#0f172a', ...headerFontStyle(branding) }}>
          {t('sp_gateway_soon_title', '{gateway} checkout is coming soon', { gateway: gatewayLabel })}
        </h2>
        <p style={{ margin: 0, color: '#64748b', fontSize: 13, lineHeight: 1.6, maxWidth: 440, marginLeft: 'auto', marginRight: 'auto' }}>
          {t('sp_gateway_soon_body', 'Online payment through {gateway} is being finalized. Please check back shortly or contact us to complete your purchase.', { gateway: gatewayLabel })}
        </p>
      </div>
    </div>
  )
}

function PayConfigError({ onBack }) {
  const { t } = useLanguage()
  return (
    <>
      <button type="button" onClick={onBack} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, marginBottom: 12, fontSize: 13 }}>← {t('sp_store_back_details', 'Back to details')}</button>
      <div style={errorBox}>{t('sp_store_pay_unavailable', 'Online payment isn’t available for this store yet. Please contact us to purchase.')}</div>
    </>
  )
}

// Shared "charged now" copy + back link + submit button for the bundle pay forms.
function bundlePayCopy(t) {
  return {
    heading: t('sp_store_payment', 'Payment'),
    note: t('sp_store_charge_now', 'Your card is charged now to activate your prepaid rounds.'),
    back: t('sp_store_back_details', 'Back to details'),
  }
}

// Load Elavon Converge Checkout.js from the (reseller-specific) script host and
// resolve once ConvergeEmbeddedPayment is available.
function ensureConvergeCheckoutJs(scriptHost) {
  return new Promise((resolve, reject) => {
    if (window.ConvergeEmbeddedPayment) return resolve()
    const existing = document.getElementById('elavon-checkoutjs')
    const done = () => (window.ConvergeEmbeddedPayment ? resolve() : reject(new Error('Payment form failed to initialise.')))
    if (existing) { existing.addEventListener('load', done); existing.addEventListener('error', () => reject(new Error('Could not load the secure payment form.'))); if (window.ConvergeEmbeddedPayment) resolve(); return }
    const s = document.createElement('script')
    s.id = 'elavon-checkoutjs'
    s.src = String(scriptHost || '').replace(/\/+$/, '')
    s.async = true
    s.onload = done
    s.onerror = () => reject(new Error('Could not load the secure payment form.'))
    document.body.appendChild(s)
  })
}

// Elavon Converge (Mode B) bundle pay screen. Unlike Stripe/NMI (tokenize then
// charge server-side), Converge is session-token based: the server mints an
// ssl_txn_auth_token for this amount, the browser runs Checkout.js (which opens
// its own secure card lightbox), and on approval we hand the ssl_txn_id back to
// the server to verify + record. The PAN never touches our server.
// ⚠ UNVERIFIED against a live Converge account — gated off until sandbox test.
function BundleElavonPay({ amount, currency, primary, branding, buyerName, onBack, onStartSession, onCharge }) {
  const { t } = useLanguage()
  const copy = bundlePayCopy(t)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  async function submit(e) {
    e.preventDefault()
    setErr(null); setBusy(true)
    try {
      const { token, scriptHost } = await onStartSession()   // mint session server-side
      if (!token) throw new Error('Could not start the payment session.')
      await ensureConvergeCheckoutJs(scriptHost)
      window.ConvergeEmbeddedPayment.pay(
        { ssl_txn_auth_token: token },
        {
          onError:     (e2) => { setErr((e2 && (e2.errorMessage || e2.ssl_result_message)) || 'Payment error — please try again.'); setBusy(false) },
          onDeclined:  (resp) => { setErr((resp && resp.ssl_result_message) || 'Card declined.'); setBusy(false) },
          onCancelled: () => { setBusy(false) },
          onApproval:  async (resp) => {
            try {
              const txnId = resp && resp.ssl_txn_id
              if (!txnId) throw new Error('No transaction id returned by Elavon.')
              await onCharge(txnId)   // server verifies + records; view switches away
            } catch (e3) { setErr(e3.message); setBusy(false) }
          },
        }
      )
    } catch (e2) { setErr(e2.message); setBusy(false) }
  }

  return (
    <form onSubmit={submit}>
      <button type="button" onClick={onBack} disabled={busy} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, marginBottom: 12, fontSize: 13 }}>← {copy.back}</button>
      <h2 style={{ margin: '0 0 4px', fontSize: 20, color: primary, ...headerFontStyle(branding) }}>{copy.heading}</h2>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 16 }}>{copy.note}</p>
      {err && <div style={errorBox}>{err}</div>}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 18, fontSize: 13, color: '#64748b' }}>
        {t('sp_store_elavon_note', 'You’ll enter your card securely in the Elavon payment window.')}
      </div>
      <button type="submit" disabled={busy}
        style={{ width: '100%', padding: 16, marginTop: 16, background: busy ? '#cbd5e1' : primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: busy ? 'not-allowed' : 'pointer' }}>
        {busy ? t('sp_store_processing', 'Processing…') : t('sp_store_pay_now', 'Pay {price}', { price: fmtMoney(amount, currency) })}
      </button>
    </form>
  )
}

function BundleMonerisPay({ amount, currency, primary, branding, buyerName, onBack, onCharge }) {
  const { t } = useLanguage()
  const copy = bundlePayCopy(t)
  const [card, setCard] = useState({ number: '', expiry: '', cvc: '', name: buyerName || '' })
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState(null)

  function setNum(v) {
    const digits = v.replace(/\D/g, '').slice(0, 19)
    setCard(c => ({ ...c, number: digits.replace(/(.{4})/g, '$1 ').trim() }))
  }
  function setExp(v) {
    const digits = v.replace(/\D/g, '').slice(0, 4)
    setCard(c => ({ ...c, expiry: digits.length >= 3 ? digits.slice(0, 2) + ' / ' + digits.slice(2) : digits }))
  }
  function setCvc(v) { setCard(c => ({ ...c, cvc: v.replace(/\D/g, '').slice(0, 4) })) }

  const ready = card.number.replace(/\s/g, '').length >= 13
    && card.expiry.replace(/\D/g, '').length === 4
    && card.cvc.length >= 3 && card.name.trim().length >= 2 && !busy

  async function submit(e) {
    e.preventDefault(); setErr(null); setBusy(true)
    try { await onCharge(card) } // resolves on success (view switches away)
    catch (e2) { setErr(e2.message); setBusy(false) }
  }

  return (
    <form onSubmit={submit}>
      <button type="button" onClick={onBack} disabled={busy} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, marginBottom: 12, fontSize: 13 }}>← {copy.back}</button>
      <h2 style={{ margin: '0 0 4px', fontSize: 20, color: primary, ...headerFontStyle(branding) }}>{copy.heading}</h2>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 16 }}>{copy.note}</p>
      {err && <div style={errorBox}>{err}</div>}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 18 }}>
        <Field label={t('sp_store_cardholder', 'Cardholder name')}>
          <input type="text" required value={card.name} onChange={e => setCard(c => ({ ...c, name: e.target.value }))} style={inp} />
        </Field>
        <Field label={t('sp_store_card_number', 'Card number')}>
          <input type="text" required inputMode="numeric" autoComplete="cc-number" placeholder="1234 5678 9012 3456" value={card.number} onChange={e => setNum(e.target.value)} style={{ ...inp, fontFamily: 'monospace', letterSpacing: 1 }} />
        </Field>
        <div style={row}>
          <Field label={t('sp_store_card_expiry', 'Expiry (MM/YY)')} style={{ flex: 1 }}>
            <input type="text" required inputMode="numeric" autoComplete="cc-exp" placeholder="08 / 28" value={card.expiry} onChange={e => setExp(e.target.value)} style={{ ...inp, fontFamily: 'monospace' }} />
          </Field>
          <Field label={t('sp_store_card_cvc', 'CVC')} style={{ flex: 1 }}>
            <input type="text" required inputMode="numeric" autoComplete="cc-csc" placeholder="123" value={card.cvc} onChange={e => setCvc(e.target.value)} style={{ ...inp, fontFamily: 'monospace' }} />
          </Field>
        </div>
      </div>
      <button type="submit" disabled={!ready}
        style={{ width: '100%', padding: 16, marginTop: 16, background: !ready ? '#cbd5e1' : primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: !ready ? 'not-allowed' : 'pointer' }}>
        {busy ? t('sp_store_processing', 'Processing…') : t('sp_store_pay_now', 'Pay {price}', { price: fmtMoney(amount, currency) })}
      </button>
    </form>
  )
}

function BundleNmiPay({ amount, currency, primary, branding, buyerName, nmiTokenizationKey, nmiBaseUrl, onBack, onCharge }) {
  const { t } = useLanguage()
  const copy = bundlePayCopy(t)
  const [name, setName] = useState(buyerName || '')
  const [ready, setReady] = useState(false)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  const onTokenRef = useRef(null)

  useEffect(() => {
    const base = String(nmiBaseUrl || 'https://secure.nmi.com').replace(/\/+$/, '')
    let cancelled = false
    function configure() {
      if (!window.CollectJS) return
      window.CollectJS.configure({
        variant: 'inline',
        fields: {
          ccnumber: { selector: '#bundle-nmi-ccnumber', placeholder: 'Card number' },
          ccexp:    { selector: '#bundle-nmi-ccexp', placeholder: 'MM / YY' },
          cvv:      { selector: '#bundle-nmi-cvv', placeholder: 'CVV' },
        },
        fieldsAvailableCallback: () => { if (!cancelled) setReady(true) },
        validationCallback: (field, ok, message) => { if (!ok && message && !cancelled) setErr(message) },
        callback: (resp) => { const h = onTokenRef.current; if (h) h(resp) },
      })
    }
    const existing = document.getElementById('nmi-collectjs')
    if (window.CollectJS) { configure() }
    else if (!existing) {
      const s = document.createElement('script')
      s.id = 'nmi-collectjs'
      s.src = base + '/token/Collect.js'
      s.async = true
      s.setAttribute('data-tokenization-key', nmiTokenizationKey || '')
      s.onload = () => { if (!cancelled) configure() }
      s.onerror = () => { if (!cancelled) setErr('Could not load the secure card form. Please refresh and try again.') }
      document.body.appendChild(s)
    } else {
      existing.addEventListener('load', configure)
      if (window.CollectJS) configure()
    }
    return () => { cancelled = true }
  }, [nmiBaseUrl, nmiTokenizationKey])

  function submit(e) {
    e.preventDefault(); setErr(null); setBusy(true)
    onTokenRef.current = async (resp) => {
      try {
        if (!resp || !resp.token) throw new Error('Card details are incomplete — please re-check and try again.')
        await onCharge(resp.token) // resolves on success (view switches away)
      } catch (e2) { setErr(e2.message); setBusy(false) }
    }
    if (window.CollectJS) window.CollectJS.startPaymentRequest()
    else { setErr('Card form is still loading — one moment.'); setBusy(false) }
  }

  const collectField = { ...inp, padding: '11px 12px', height: 44 }
  const canPay = ready && name.trim().length >= 2 && !busy
  return (
    <form onSubmit={submit}>
      <button type="button" onClick={onBack} disabled={busy} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, marginBottom: 12, fontSize: 13 }}>← {copy.back}</button>
      <h2 style={{ margin: '0 0 4px', fontSize: 20, color: primary, ...headerFontStyle(branding) }}>{copy.heading}</h2>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 16 }}>{copy.note}</p>
      {err && <div style={errorBox}>{err}</div>}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 18 }}>
        <Field label={t('sp_store_cardholder', 'Cardholder name')}>
          <input type="text" required value={name} onChange={e => setName(e.target.value)} style={inp} />
        </Field>
        <Field label={t('sp_store_card_number', 'Card number')}><div id="bundle-nmi-ccnumber" style={collectField} /></Field>
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}><Field label={t('sp_store_card_expiry_short', 'Expiry')}><div id="bundle-nmi-ccexp" style={collectField} /></Field></div>
          <div style={{ flex: 1 }}><Field label={t('sp_store_card_cvv', 'CVV')}><div id="bundle-nmi-cvv" style={collectField} /></Field></div>
        </div>
        <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8' }}>{t('sp_store_secured_nmi', 'Secured by NMI.')}</div>
      </div>
      <button type="submit" disabled={!canPay}
        style={{ marginTop: 16, width: '100%', padding: '13px 16px', background: canPay ? primary : '#cbd5e1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: canPay ? 'pointer' : 'not-allowed' }}>
        {busy ? t('sp_store_processing', 'Processing…') : t('sp_store_pay_now', 'Pay {price}', { price: fmtMoney(amount, currency) })}
      </button>
    </form>
  )
}

function BundleStripeWrapper({ publishableKey, ...rest }) {
  const stripePromise = useMemo(() => loadStripe(publishableKey), [publishableKey])
  return (
    <Elements stripe={stripePromise}>
      <BundleStripePay {...rest} />
    </Elements>
  )
}

function BundleStripePay({ amount, currency, primary, branding, buyerName, onBack, onCharge }) {
  const { t } = useLanguage()
  const copy = bundlePayCopy(t)
  const stripe = useStripe()
  const elements = useElements()
  const [name, setName] = useState(buyerName || '')
  const [cardReady, setCardReady] = useState(false)
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)

  async function submit(e) {
    e.preventDefault()
    if (!stripe || !elements) { setErr('Payment form is still loading — one moment.'); return }
    setErr(null); setBusy(true)
    try {
      const card = elements.getElement(CardElement)
      const { error: pmErr, paymentMethod } = await stripe.createPaymentMethod({ type: 'card', card, billing_details: { name } })
      if (pmErr) throw new Error(pmErr.message)
      await onCharge({ paymentMethodId: paymentMethod.id, stripe }) // resolves on success (view switches away)
    } catch (e2) { setErr(e2.message); setBusy(false) }
  }

  const canPay = !!stripe && cardReady && name.trim().length >= 2 && !busy
  return (
    <form onSubmit={submit}>
      <button type="button" onClick={onBack} disabled={busy} style={{ background: 'none', border: 'none', color: '#64748b', cursor: 'pointer', padding: 0, marginBottom: 12, fontSize: 13 }}>← {copy.back}</button>
      <h2 style={{ margin: '0 0 4px', fontSize: 20, color: primary, ...headerFontStyle(branding) }}>{copy.heading}</h2>
      <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 16 }}>{copy.note}</p>
      {err && <div style={errorBox}>{err}</div>}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, padding: 18 }}>
        <Field label={t('sp_store_cardholder', 'Cardholder name')}>
          <input type="text" required value={name} onChange={e => setName(e.target.value)} style={inp} />
        </Field>
        <Field label={t('sp_store_card_details', 'Card details')}>
          <div style={{ ...inp, padding: '13px 12px' }}>
            <CardElement
              options={{ style: { base: { fontSize: '15px', color: '#0f172a', '::placeholder': { color: '#94a3b8' } }, invalid: { color: '#b91c1c' } } }}
              onChange={ev => { setCardReady(ev.complete); setErr(ev.error ? ev.error.message : null) }}
            />
          </div>
        </Field>
        <div style={{ marginTop: 8, fontSize: 11, color: '#94a3b8' }}>{t('sp_store_secured_stripe', 'Secured by Stripe.')}</div>
      </div>
      <button type="submit" disabled={!canPay}
        style={{ width: '100%', padding: 16, marginTop: 16, background: !canPay ? '#cbd5e1' : primary, color: '#fff', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: !canPay ? 'not-allowed' : 'pointer' }}>
        {busy ? t('sp_store_processing', 'Processing…') : t('sp_store_pay_now', 'Pay {price}', { price: fmtMoney(amount, currency) })}
      </button>
    </form>
  )
}

function Chip({ children, primary }) {
  return <span style={{ fontSize: 11, padding: '3px 10px', background: primary + '15', color: primary, border: '1px solid ' + primary + '55', borderRadius: 12 }}>{children}</span>
}

function BundleConfirmed({ result, brand, branding, reseller, onDone }) {
  const { t } = useLanguage()
  const primary = branding?.primaryColor || brand?.primaryColor || '#0f172a'
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 40, textAlign: 'center' }}>
      <div style={{ fontSize: 48, marginBottom: 12, color: primary }}>{result.isGift ? '🎁' : '✓'}</div>
      <h1 style={{ margin: '0 0 8px', fontSize: 24, ...headerFontStyle(branding) }}>
        {result.isGift ? t('sp_store_gift_sent', 'Gift sent!') : t('sp_store_all_set', "You're all set!")}
      </h1>
      <p style={{ color: '#64748b', marginBottom: 8 }}>
        {result.isGift
          ? t('sp_store_gift_sent_body', 'We emailed the claim link to your recipient. They can attach the rounds to their account anytime.')
          : t('sp_store_confirmed_body', '{n} prepaid rounds are ready. We emailed your receipt.', { n: result.roundsTotal })}
      </p>
      <p style={{ color: '#94a3b8', fontSize: 13, marginBottom: 24 }}>
        {t('sp_store_order_line', 'Order #{id} · {amount}', { id: result.purchaseId, amount: fmtMoney(result.total, result.currency) })}
      </p>
      {!result.isGift && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          {/* Book right away (no login) — scoped to just this pass. */}
          {result.bookUrl && (
            <a href={result.bookUrl} style={{ display: 'inline-block', padding: '12px 28px', background: primary, color: '#fff', textDecoration: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700 }}>
              {t('sp_store_book_rounds', 'Book Your Rounds')}
            </a>
          )}
          {/* View the full account (magic link = passwordless sign-in). */}
          {(result.accountUrl || result.walletUrl) && (
            <a href={result.accountUrl || result.walletUrl} style={{ ...secondaryBtn, padding: '12px 20px', textDecoration: 'none' }}>
              {t('sp_store_view_rounds', 'View My Rounds')}
            </a>
          )}
        </div>
      )}
      <div style={{ marginTop: 14 }}>
        <button onClick={onDone} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: 13 }}>{t('sp_store_buy_another', 'Buy another')}</button>
      </div>
    </div>
  )
}

// ─── Hotel picker (Pass C) ─────────────────────────────────────────────
//
// Hits /api/public/reseller-embed/:iframeKey/hotels with the customer's
// selected check-in date and the package's max-nightly budget. Renders
// hotel cards with image, name, star rating, address, and a Select
// button. Selection writes a populated hotel object into the parent
// form so the booking POST has externalId + rateKey + total.
// Reflects whether the iframe/viewport is at phone width. In the embed the
// iframe is 100% wide, so this tracks the device in portrait. Used to stack
// flex rows that are too cramped on a phone (matches the <=640px breakpoint
// the package tiles use).
function useIsNarrow(bp = 640) {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(`(max-width:${bp}px)`).matches
  )
  useEffect(() => {
    const mq = window.matchMedia(`(max-width:${bp}px)`)
    const on = e => setNarrow(e.matches)
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on)
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on) }
  }, [bp])
  return narrow
}

function HotelPicker({ api, checkin, nights, numRooms = 1, maxNightly, packageId, selected, onSelect, primary, branding, track, surface = 'hotel' }) {
  const fire = track || (() => {})
  const { t } = useLanguage()
  const isNarrow = useIsNarrow()
  const [hotels, setHotels] = useState(null) // null = unfetched, [] = empty
  const [upgrades, setUpgrades] = useState([])
  const [showUpgrades, setShowUpgrades] = useState(false)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  // The supplier search FAILED (429/timeout/down) rather than the area genuinely
  // having no hotels — so we can show "having trouble, retry" not "no hotels".
  const [providerError, setProviderError] = useState(false)
  const [retryKey, setRetryKey] = useState(0)
  // Content-first progressive pricing: phase 1 shows rate-less cards from the
  // content cache instantly; phase 2 (live price search) fills rates + the
  // budget in/out split in the background. `pricing` = phase 2 in flight;
  // `pricingFailed` = phase 2 errored/timed out (keep content cards + retry).
  const [pricing, setPricing] = useState(false)
  const [pricingFailed, setPricingFailed] = useState(false)
  const [expandedId, setExpandedId] = useState(null)
  // Property id of the hotel whose "more info" modal is open (null = closed).
  const [detailsFor, setDetailsFor] = useState(null)
  // Customer-facing filters: refundable-only + sort by distance to one course.
  const [refundableOnly, setRefundableOnly] = useState(false)
  const [sortCourseId, setSortCourseId] = useState(null)

  useEffect(() => {
    if (!checkin) { setHotels(null); return }
    let cancelled = false
    // Once live prices arrive with results they are authoritative — don't let a
    // late-resolving content response overwrite them (phase 1 is normally faster,
    // but guard the race anyway).
    let pricedLanded = false
    setHotels(null); setUpgrades([])
    setErr(null); setProviderError(false); setPricingFailed(false)
    const params = new URLSearchParams({
      checkin,
      nights: String(nights || 2),
      // No implicit star floor — the reseller's curation (include/exclude +
      // filter rules) decides what customers see. Honors "Any".
      minStars: '0',
    })
    if (maxNightly) params.set('budget', String(maxNightly))
    if (packageId) params.set('packageId', String(packageId))
    const qs = params.toString()

    // ── Phase 1: instant content cards (name/photo/stars, no prices) ──
    // DB-only on the server, so this returns in a few ms — the customer sees the
    // area's hotels immediately instead of a blank "no hotels" while prices load.
    setLoading(true)
    api.get('/hotels/content?' + qs).then(r => {
      if (cancelled || pricedLanded) return
      if (r.status < 400) { setHotels(r.data.hotels || []); setUpgrades([]) }
    }).catch(() => { /* content is best-effort; phase 2 still runs */ })
      .finally(() => { if (!cancelled) setLoading(false) })

    // ── Phase 2: background live price search — authoritative once it lands ──
    // Bounded: the embed axios instance has no default timeout, so cap the price
    // call here — a slow/degraded supplier must surface as a retry, never an
    // indefinite "Checking price…" limbo on the cards.
    setPricing(true)
    // Funnel: the customer ran a hotel search. 'results' fires below with the
    // priced count so we can tell a genuine coverage gap (0 hotels) apart
    // from a price/UX drop-off (hotels shown but not picked).
    fire('search', surface, { checkin, nights: Number(nights) || 2 })
    api.get('/hotels?' + qs, { timeout: 35000 }).then(r => {
      if (cancelled) return
      if (r.status >= 400) { setPricingFailed(true); fire('results', surface, { hotels: 0, providerError: true }); return }
      const priced = r.data.hotels || []
      const ups = r.data.upgrades || []
      const provErr = !!r.data.providerError
      setProviderError(provErr)
      const bookable = priced.length + ups.length
      // Report what the customer actually saw: bookable count + whether this
      // was a genuine empty area vs. a supplier hiccup.
      fire('results', surface, { hotels: bookable, empty: bookable === 0 && !provErr, providerError: provErr })
      if (priced.length > 0 || ups.length > 0) {
        // Prices in → authoritative bookable set (standard UX: in-budget + upgrades).
        pricedLanded = true
        setHotels(priced); setUpgrades(ups)
      } else if (provErr) {
        // Supplier hiccup, nothing priced → keep the content cards visible
        // (rate-less) and offer retry. Never blank.
        setPricingFailed(true)
      } else {
        // Genuine no-availability for these dates → clear so the empty state shows.
        pricedLanded = true
        setHotels([]); setUpgrades([])
      }
    }).catch(() => { if (!cancelled) { setPricingFailed(true); fire('results', surface, { hotels: 0, providerError: true }) } })
      .finally(() => { if (!cancelled) setPricing(false) })

    return () => { cancelled = true }
  }, [api, checkin, nights, maxNightly, packageId, retryKey])

  if (!checkin) {
    return <p style={{ ...muted, fontSize: 13 }}><em>Pick a check-in date above to see available hotels in the area.</em></p>
  }
  if (err) return <div style={errorBox}>{err}</div>
  // Nothing on screen yet: still fetching the instant content cards.
  if (!hotels) return <Loading />
  if (hotels.length === 0 && upgrades.length === 0) {
    // No content cards AND still working (content or prices in flight) → keep loading.
    if (loading || pricing) return <Loading />
    // A supplier hiccup (timeout / rate-limit) is NOT the same as an area with no
    // hotels — offer a retry instead of telling the customer to change the date.
    if (providerError || pricingFailed) {
      return (
        <div style={{ ...muted, fontSize: 13 }}>
          <p style={{ margin: '0 0 8px' }}>{t('sp_hotels_temp_unavailable', "We're having trouble loading hotels right now — this is usually brief.")}</p>
          <button
            onClick={() => setRetryKey(k => k + 1)}
            style={{ fontSize: 13, fontWeight: 600, padding: '6px 14px', borderRadius: 8, border: `1px solid ${primary || '#0f766e'}`, background: '#fff', color: primary || '#0f766e', cursor: 'pointer' }}
          >{t('sp_try_again', 'Try again')}</button>
        </div>
      )
    }
    return <p style={{ ...muted, fontSize: 13 }}>{t('sp_no_hotels_area', 'No hotels available in this area for those dates. Try a different check-in date.')}</p>
  }

  // Card renderer shared between in-budget and upgrade lists. The only
  // difference is the upgrade pill on upgrade cards. Customer never sees
  // absolute prices on either — only the upgrade delta on upgrades.
  function renderCard(h, isUpgrade) {
    const isSelected = selected?.externalId === h.propertyId
    const isExpanded = expandedId === h.propertyId
    const cheapest = h.rates?.[0]
    // Content-first: a card with no rates yet is a content-cache card whose price
    // is still loading (phase 2). Show it, but the customer can't Select until the
    // live rate arrives (Select needs a rateKey to book).
    const priced = !!(h.rates && h.rates.length > 0)
    // When a hotel offers only one room type there's no chooser to open, so
    // show that room's description inline (e.g. Maritime Inn). When a hotel
    // IS selected, always show which room type the customer picked.
    const singleRate = h.rates && h.rates.length === 1 ? h.rates[0] : null
    const selectedRoomLabel = isSelected && selected?.roomType
      ? [selected.roomType, selected.bedConfig].filter(Boolean).join(' · ')
      : null
    const singleRoomLabel = singleRate
      ? [singleRate.roomType, singleRate.bedConfig].filter(Boolean).join(' · ')
      : null
    return (
      <div key={h.propertyId} style={{
        background: '#fff',
        border: '1px solid ' + (isSelected ? primary : '#e2e8f0'),
        borderWidth: isSelected ? 2 : 1,
        borderRadius: 8,
        padding: 12,
        display: 'flex',
        // On a phone, stack the thumbnail above the details so the details
        // column (and the expanded room chooser) get the full card width
        // instead of being squeezed beside the image.
        flexDirection: isNarrow ? 'column' : 'row',
        gap: 12,
        ...bodyFontStyle(branding),
      }}>
        {h.image && <img src={h.image} alt={h.name} style={{ width: isNarrow ? '100%' : 110, height: 90, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 14 }}>{h.name}</strong>
            {h.starRating && <span style={{ fontSize: 11, color: '#f59e0b' }}>{'★'.repeat(Math.round(h.starRating))}</span>}
            <button
              type="button"
              onClick={() => setDetailsFor(h.propertyId)}
              aria-label={'View details about ' + h.name}
              title="View hotel details"
              style={{
                marginLeft: 4,
                display: 'inline-flex', alignItems: 'center', gap: 5,
                padding: '4px 10px 4px 8px',
                background: '#0284c7', border: 'none', borderRadius: 999,
                color: '#fff', fontSize: 12, fontWeight: 700,
                cursor: 'pointer', lineHeight: 1,
                boxShadow: '0 1px 2px rgba(2,132,199,0.35)',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#0369a1' }}
              onMouseLeave={e => { e.currentTarget.style.background = '#0284c7' }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="12" y1="16" x2="12" y2="12"/>
                <line x1="12" y1="8" x2="12.01" y2="8"/>
              </svg>
              {t('sp_details', 'Details')}
            </button>
            {isUpgrade && h.upgradeNightly != null && (
              <span style={{ fontSize: 11, padding: '2px 8px', background: '#fef3c7', color: '#92400e', borderRadius: 999, fontWeight: 600 }}>
                +{fmtMoney(h.upgradeNightly)} / night
              </span>
            )}
          </div>
          {h.address?.line1 && <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{h.address.line1}{h.address.city ? `, ${h.address.city}` : ''}</div>}
          {h.guestRating && <div style={{ fontSize: 12, color: '#475569', marginTop: 2 }}>{t('sp_guest_rating', 'Guest rating: {rating}/5', { rating: h.guestRating })}{h.guestReviewCount ? ` (${t('sp_reviews_count', '{count} reviews', { count: h.guestReviewCount })})` : ''}</div>}
          {h.coursesDistances && h.coursesDistances.length > 0 && (
            <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'baseline' }}>
              {h.coursesDistances.map((d, idx) => (
                <span key={d.coursesId} style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '2px 8px', background: '#f1f5f9', color: '#334155',
                  borderRadius: 999, fontSize: 11, fontWeight: 500,
                }}>
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
                  {t('sp_km_to_course', '{distance} km to {course}', { distance: d.distanceKm, course: d.courseName })}
                </span>
              ))}
              <span style={{ fontSize: 10, color: '#94a3b8', marginLeft: 4 }}>{t('sp_as_the_crow_flies', '(as the crow flies)')}</span>
            </div>
          )}
          {/* Cancellation / refundability badge.
              - When live rates were returned: green "Free cancellation"
                or red "Non-refundable" with a deadline if available.
              - When we're on the content-fallback path (no rate data),
                show a neutral hint so the customer knows the actual
                policy will surface at checkout. Better than silence —
                they're picking blind otherwise. */}
          {cheapest ? (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '2px 8px', borderRadius: 999, marginTop: 6, fontWeight: 600, fontSize: 11,
              background: cheapest.refundable ? '#dcfce7' : '#fee2e2',
              color:      cheapest.refundable ? '#166534' : '#991b1b',
            }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                {cheapest.refundable
                  ? <path d="M20 6 9 17l-5-5"/>
                  : <><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></>}
              </svg>
              {cheapest.refundable
                ? (cheapest.cancellationDeadline
                    ? t('sp_free_cancellation_until_inline', 'Free cancellation until {date}', { date: cheapest.cancellationDeadline.slice(0, 10) })
                    : t('sp_free_cancellation', 'Free cancellation'))
                : t('sp_non_refundable', 'Non-refundable')}
            </div>
          ) : (
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '2px 8px', borderRadius: 999, marginTop: 6, fontWeight: 500, fontSize: 11,
              background: '#f1f5f9', color: '#475569',
            }} title="Live rates weren't available for these dates. The cancellation policy of the rate we book will be shown on the review screen before payment.">
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><circle cx="12" cy="8" r="0.5" fill="currentColor"/></svg>
              {t('sp_cancellation_policy_at_checkout', 'Cancellation policy shown at checkout')}
            </div>
          )}
          {/* Selected room (takes priority) or, for single-room-type hotels,
              the only room's description so the customer always knows what
              they're getting. */}
          {selectedRoomLabel ? (
            <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, color: primary, display: 'flex', alignItems: 'center', gap: 5 }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>
              {numRooms > 1
                ? t('sp_selected_rooms', '{count} Rooms: {room}', { count: numRooms, room: selectedRoomLabel })
                : t('sp_selected_room', 'Your room: {room}', { room: selectedRoomLabel })}
            </div>
          ) : singleRoomLabel ? (
            <div style={{ marginTop: 8, fontSize: 12, color: '#475569' }}>
              {numRooms > 1
                ? t('sp_rooms_label', '{count} Rooms: {room}', { count: numRooms, room: singleRoomLabel })
                : t('sp_room_label', 'Room: {room}', { room: singleRoomLabel })}
            </div>
          ) : null}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', marginTop: 8, gap: 6 }}>
            {h.rates && h.rates.length > 1 && (
              <button type="button" onClick={() => setExpandedId(isExpanded ? null : h.propertyId)} style={secondaryBtn}>
                {isExpanded ? t('sp_hide_rooms', 'Hide rooms') : t('sp_room_options', '{count} room options', { count: h.rates.length })}
              </button>
            )}
            {priced ? (
              <button
                type="button"
                onClick={() => {
                  if (isSelected) { onSelect(null, null); return }
                  // More than one room type → open the room list so the customer
                  // picks explicitly (same as the "N room options" button), rather
                  // than silently taking the cheapest in-budget rate.
                  if (h.rates && h.rates.length > 1) { setExpandedId(h.propertyId); return }
                  fire('hotel_select', surface, { hotelId: h.propertyId, provider: cheapest?.provider || h.provider || null })
                  onSelect(h, cheapest)
                }}
                style={{
                  padding: '8px 14px',
                  background: isSelected ? '#fff' : primary,
                  color: isSelected ? primary : '#fff',
                  border: '1px solid ' + primary,
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {isSelected ? t('sp_selected', 'Selected ✓') : t('sp_select', 'Select')}
              </button>
            ) : (
              <button
                type="button"
                disabled
                title={pricing ? undefined : t('sp_price_unavailable_hint', "We couldn't load this hotel's live price just now — use Try again above.")}
                style={{
                  padding: '8px 14px',
                  background: '#f1f5f9',
                  color: '#94a3b8',
                  border: '1px solid #e2e8f0',
                  borderRadius: 6,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'default',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                }}
              >
                {/* While phase-2 pricing is in flight → spinner. Once it's done
                    and this card still has no rate → say so, don't spin forever. */}
                {pricing && (
                  <span style={{
                    width: 10, height: 10, borderRadius: '50%',
                    border: '2px solid #cbd5e1', borderTopColor: '#94a3b8',
                    display: 'inline-block', animation: 'ig-spin 0.8s linear infinite',
                  }} />
                )}
                {pricing
                  ? t('sp_checking_price', 'Checking price…')
                  : t('sp_price_unavailable', 'Price unavailable')}
              </button>
            )}
          </div>
          {isExpanded && (
            <div style={{ marginTop: 10, borderTop: '1px solid #f1f5f9', paddingTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4 }}>
                {t('sp_choose_room_type', 'Choose your room type')}
              </div>
              {h.rates.map(r => {
                const rateSelected = isSelected && selected?.rateKey === r.rateKey
                // EPIC #112 Phase 1: per-rate upcharge. The room type the
                // customer picks applies to all rooms in the package. Rates
                // within the package's nightly budget are "Included"; pricier
                // room types (e.g. a King Suite) show only the +$X/night delta
                // (markup already baked in) — never an absolute price.
                const hasUpcharge = Number(r.upgradeNightly) > 0
                return (
                  <div key={r.rateKey} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, padding: '6px 8px', background: rateSelected ? '#f0fdf4' : '#f8fafc', borderRadius: 4, border: '1px solid ' + (rateSelected ? primary : 'transparent') }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600 }}>{r.roomType || t('sp_standard_room', 'Standard room')}</div>
                      {r.bedConfig && <div style={{ color: '#64748b' }}>{r.bedConfig}</div>}
                      <div style={{ color: r.refundable ? '#059669' : '#dc2626', fontSize: 11, marginTop: 2 }}>
                        {r.refundable
                          ? (r.cancellationDeadline
                              ? t('sp_free_cancellation_until_inline', 'Free cancellation until {date}', { date: r.cancellationDeadline.slice(0, 10) })
                              : t('sp_free_cancellation', 'Free cancellation'))
                          : t('sp_non_refundable', 'Non-refundable')}
                      </div>
                    </div>
                    {hasUpcharge ? (
                      <div style={{ textAlign: 'right', fontSize: 12, color: '#92400e', fontWeight: 600, flexShrink: 0 }}>
                        +{fmtMoney(r.upgradeNightly)}<br/>/night
                      </div>
                    ) : (
                      <div style={{ textAlign: 'right', fontSize: 11, color: '#059669', fontWeight: 700, flexShrink: 0 }}>
                        {t('sp_included', 'Included')}
                      </div>
                    )}
                    <button type="button" onClick={() => { fire('hotel_select', surface, { hotelId: h.propertyId, provider: r?.provider || h.provider || null }); onSelect(h, r); setExpandedId(null) }} style={{ ...secondaryBtn, flexShrink: 0, whiteSpace: 'nowrap', borderColor: rateSelected ? primary : '#e2e8f0', color: rateSelected ? primary : '#475569' }}>
                      {rateSelected ? '✓' : t('sp_select', 'Select')}
                    </button>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>
    )
  }

  // When zero hotels are within budget but upgrades exist, auto-expand
  // the upgrade list. Otherwise the page renders as a single yellow
  // "click to reveal" callout with a greyed-out Continue button, which
  // reads as "page is broken" rather than "all options exceed budget."
  const noInBudget = hotels.length === 0 && upgrades.length > 0
  const showUpgradeList = noInBudget || showUpgrades

  // ── Customer filters: refundable-only + sort by distance to one course ──
  const isRefundable = (h) => !!(h.rates?.[0]?.refundable)
  const distanceTo = (h, courseId) => {
    const d = (h.coursesDistances || []).find(x => x.coursesId === courseId)
    return d && d.distanceKm != null ? Number(d.distanceKm) : Infinity
  }
  const applyFilterSort = (list) => {
    let out = refundableOnly ? list.filter(isRefundable) : list.slice()
    if (sortCourseId) out.sort((a, b) => distanceTo(a, sortCourseId) - distanceTo(b, sortCourseId))
    return out
  }
  // Course options for the sort dropdown — distinct across all shown hotels.
  const courseOptions = []
  const seenCourse = new Set()
  for (const h of [...(hotels || []), ...(upgrades || [])]) {
    for (const d of (h.coursesDistances || [])) {
      if (d.coursesId != null && !seenCourse.has(d.coursesId)) {
        seenCourse.add(d.coursesId)
        courseOptions.push({ coursesId: d.coursesId, courseName: d.courseName })
      }
    }
  }
  const visibleHotels = applyFilterSort(hotels)
  const visibleUpgrades = applyFilterSort(upgrades)

  return (
    <>
      {pricing && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
          padding: '8px 12px', borderRadius: 8, background: '#f0f9ff',
          border: '1px solid #bae6fd', color: '#075985', fontSize: 12.5, fontWeight: 500,
        }}>
          <span style={{
            width: 12, height: 12, borderRadius: '50%',
            border: '2px solid #bae6fd', borderTopColor: '#0284c7',
            display: 'inline-block', animation: 'ig-spin 0.8s linear infinite',
          }} />
          {t('sp_checking_latest_prices', 'Checking latest prices — hotels shown below; you can pick a room as soon as its price loads.')}
        </div>
      )}
      {/* Prices failed to load but we still have the area's hotels on screen
          (content-first). Offer a retry instead of leaving the cards stuck on
          "Checking price…" — the price search is temporarily unavailable, not the
          hotels. */}
      {!pricing && pricingFailed && (hotels.length > 0 || upgrades.length > 0) && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap',
          marginBottom: 10, padding: '10px 12px', borderRadius: 8,
          background: '#fffbeb', border: '1px solid #fde68a', color: '#92400e', fontSize: 12.5,
        }}>
          <span>{t('sp_prices_unavailable_retry', "We couldn't load live prices just now — the hotels are here, but pricing is temporarily unavailable.")}</span>
          <button
            onClick={() => setRetryKey(k => k + 1)}
            style={{ fontSize: 12.5, fontWeight: 600, padding: '6px 14px', borderRadius: 8, border: `1px solid ${primary || '#0f766e'}`, background: '#fff', color: primary || '#0f766e', cursor: 'pointer', whiteSpace: 'nowrap' }}
          >{t('sp_try_again', 'Try again')}</button>
        </div>
      )}
      {(hotels.length > 0 || upgrades.length > 0) && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, alignItems: 'center', marginBottom: 10, fontSize: 13 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#475569' }}>
            <Toggle checked={refundableOnly} onChange={setRefundableOnly} activeColor={primary} />
            <span style={{ cursor: 'pointer' }} onClick={() => setRefundableOnly(v => !v)}>{t('sp_refundable_only', 'Refundable only')}</span>
          </span>
          {courseOptions.length > 0 && (
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#475569' }}>
              {t('sp_sort_by_distance_to', 'Sort by distance to:')}
              <select value={sortCourseId || ''} onChange={e => setSortCourseId(e.target.value ? Number(e.target.value) : null)}
                style={{ padding: '5px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 13, maxWidth: 220 }}>
                <option value="">{t('sp_recommended_order', 'Recommended')}</option>
                {courseOptions.map(c => <option key={c.coursesId} value={c.coursesId}>{c.courseName}</option>)}
              </select>
            </label>
          )}
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 540, overflowY: 'auto', paddingRight: 4 }}>
        {refundableOnly && visibleHotels.length === 0 && hotels.length > 0 && (
          <div style={{ ...muted, fontSize: 13 }}>{t('sp_no_refundable_in_budget', 'No refundable hotels in your budget for these dates. Turn off "Refundable only" to see all options.')}</div>
        )}
        {visibleHotels.map(h => renderCard(h, false))}
        {upgrades.length > 0 && (
          <>
            {noInBudget && (
              <div style={{
                background: '#fffbeb',
                border: '1px solid #fde68a',
                borderRadius: 8,
                padding: '10px 14px',
                fontSize: 13,
                color: '#92400e',
                lineHeight: 1.5,
              }}>
                <strong>{t('sp_no_hotels_within_budget', "No hotels were available within your package's nightly budget for these dates.")}</strong>
                <div style={{ marginTop: 4, fontWeight: 400 }}>
                  {t('sp_options_above_budget_added', 'The options below are above the package budget. The extra amount shown on each card (per night) is added to your trip total.')}
                </div>
              </div>
            )}
            {!showUpgradeList ? (
              <button
                type="button"
                onClick={() => setShowUpgrades(true)}
                style={{
                  background: '#fffbeb',
                  border: '1px dashed #f59e0b',
                  color: '#92400e',
                  padding: '10px 14px',
                  borderRadius: 8,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                {upgrades.length > 1
                  ? t('sp_additional_per_night_more_options', 'For an additional {amount} / night, {count} more hotel options are available →', { amount: fmtMoney(upgrades[0].upgradeNightly || 0), count: upgrades.length })
                  : t('sp_additional_per_night_more_option_singular', 'For an additional {amount} / night, {count} more hotel option is available →', { amount: fmtMoney(upgrades[0].upgradeNightly || 0), count: upgrades.length })}
              </button>
            ) : (
              <>
                {!noInBudget && (
                  <div style={{ fontSize: 12, color: '#92400e', marginTop: 4, fontWeight: 600 }}>
                    {t('sp_upgrade_options_additional_cost', 'Upgrade options (additional cost beyond your package)')}
                  </div>
                )}
                {visibleUpgrades.map(h => renderCard(h, true))}
                {!noInBudget && (
                  <button type="button" onClick={() => setShowUpgrades(false)} style={{ ...secondaryBtn, alignSelf: 'flex-start' }}>
                    {t('sp_hide_upgrade_options', 'Hide upgrade options')}
                  </button>
                )}
              </>
            )}
          </>
        )}
      </div>
      {detailsFor && (
        <HotelDetailsModal
          api={api}
          propertyId={detailsFor}
          onClose={() => setDetailsFor(null)}
          primary={primary}
        />
      )}
    </>
  )
}

// ─── Hotel details modal — fired from the (i) button on each hotel card ─

function HotelDetailsModal({ api, propertyId, onClose, primary }) {
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [photoIdx, setPhotoIdx] = useState(0)

  useEffect(() => {
    setData(null); setErr(null); setPhotoIdx(0)
    api.get('/hotels/' + encodeURIComponent(propertyId)).then(r => {
      if (r.status >= 400) setErr(r.data?.message || `Error ${r.status}`)
      else setData(r.data.hotel)
    }).catch(e => setErr(e.message))
  }, [api, propertyId])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])

  const hotel = data
  const image = hotel?.images?.[photoIdx]
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(15,23,42,0.65)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 16,
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        background: '#fff', borderRadius: 12, maxWidth: 760, width: '100%',
        maxHeight: '90vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>
              {hotel?.name || 'Loading…'}
              {hotel?.starRating && <span style={{ marginLeft: 8, fontSize: 13, color: '#f59e0b' }}>{'★'.repeat(Math.round(hotel.starRating))}</span>}
            </div>
            {hotel && (
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                {[hotel.address?.line1, hotel.address?.city, hotel.address?.state, hotel.address?.country].filter(Boolean).join(', ')}
              </div>
            )}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" style={{
            background: '#f1f5f9', border: 'none', borderRadius: '50%',
            width: 32, height: 32, cursor: 'pointer', fontSize: 18, color: '#475569', flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1,
          }}>×</button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {err && <div style={{ ...errorBox, margin: 20 }}>{err}</div>}
          {!hotel && !err && <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading…</div>}
          {hotel && (
            <>
              {/* Photo gallery */}
              {image && (
                <div style={{ padding: 20, paddingBottom: 12 }}>
                  <img src={image.url} alt={image.caption || hotel.name} style={{ width: '100%', maxHeight: 360, objectFit: 'cover', borderRadius: 8, background: '#f1f5f9' }} />
                  {hotel.images.length > 1 && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 8, overflowX: 'auto' }}>
                      {hotel.images.map((img, i) => (
                        <button key={i} type="button" onClick={() => setPhotoIdx(i)} style={{
                          width: 64, height: 48, padding: 0, flexShrink: 0,
                          border: i === photoIdx ? `2px solid ${primary || '#0f172a'}` : '1px solid #e2e8f0',
                          borderRadius: 4, cursor: 'pointer', background: '#f1f5f9',
                        }}>
                          <img src={img.url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 2 }} />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Quick facts row */}
              <div style={{ padding: '0 20px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {hotel.guestRating != null && (
                  <DetailChip>Guest rating: <strong>{hotel.guestRating}/5</strong>{hotel.guestReviewCount ? ` (${hotel.guestReviewCount} reviews)` : ''}</DetailChip>
                )}
                {hotel.brand && <DetailChip>Brand: {hotel.brand}</DetailChip>}
                {hotel.category && <DetailChip>{hotel.category}</DetailChip>}
                {hotel.allInclusive && <DetailChip>All-inclusive</DetailChip>}
                {hotel.checkin?.beginTime && <DetailChip>Check-in from {hotel.checkin.beginTime}</DetailChip>}
                {hotel.checkout?.time && <DetailChip>Check-out by {hotel.checkout.time}</DetailChip>}
              </div>

              {/* Descriptions */}
              {hotel.descriptions?.length > 0 && (
                <div style={{ padding: '16px 20px 0' }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#0f172a' }}>About this hotel</h4>
                  {hotel.descriptions.map((d, i) => (
                    <div key={i} style={{ marginBottom: 12 }}>
                      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 2 }}>{d.label}</div>
                      <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: d.text }} />
                    </div>
                  ))}
                </div>
              )}

              {/* Amenities */}
              {hotel.amenities?.length > 0 && (
                <div style={{ padding: '16px 20px 0' }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#0f172a' }}>Amenities</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {hotel.amenities.map(a => <DetailChip key={a}>{a}</DetailChip>)}
                  </div>
                </div>
              )}

              {/* Check-in instructions */}
              {(hotel.checkin?.instructions || hotel.checkin?.specialInstructions) && (
                <div style={{ padding: '16px 20px 0' }}>
                  <h4 style={{ margin: '0 0 8px', fontSize: 14, color: '#0f172a' }}>Check-in details</h4>
                  {hotel.checkin.instructions && (
                    <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.6, marginBottom: 6 }} dangerouslySetInnerHTML={{ __html: hotel.checkin.instructions }} />
                  )}
                  {hotel.checkin.specialInstructions && (
                    <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.6 }} dangerouslySetInnerHTML={{ __html: hotel.checkin.specialInstructions }} />
                  )}
                </div>
              )}

              <div style={{ height: 20 }} />
            </>
          )}
        </div>
      </div>
    </div>
  )
}
function DetailChip({ children }) {
  return (
    <span style={{
      display: 'inline-block', padding: '4px 10px',
      background: '#f1f5f9', color: '#334155',
      borderRadius: 999, fontSize: 12, fontWeight: 500,
    }}>{children}</span>
  )
}

// ─── Tee-time picker (Pass D step 4) ───────────────────────────────────
//
// One picker per round in BookingFlow. Calls
// /api/public/reseller-embed/:iframeKey/courses/:coursesId/tee-times
// for the selected (course, date) — backend hits the unified golf-
// booking router which dispatches per-provider. Customer never sees
// absolute prices — in-budget times show as time-only chips, premium
// times collapse behind a "+$X for premium tee times" expander with
// only the per-golfer / total delta visible.
// Tee-time buckets — instead of showing every 9-minute slot we group
// into four time-of-day windows. Customer picks a window, sees only
// times in that range.
const TEE_BUCKETS = [
  { id: 'early',     label: 'Early Morning', labelKey: 'sp_early_morning', sub: '6:00 – 9:00 AM',     subKey: 'sp_early_morning_range', startMin: 6 * 60,  endMin: 9 * 60 },
  { id: 'morning',   label: 'Morning',       labelKey: 'sp_morning',       sub: '9:00 AM – 12:00 PM', subKey: 'sp_morning_range',       startMin: 9 * 60,  endMin: 12 * 60 },
  { id: 'afternoon', label: 'Afternoon',     labelKey: 'sp_afternoon',     sub: '12:00 – 3:00 PM',    subKey: 'sp_afternoon_range',     startMin: 12 * 60, endMin: 15 * 60 },
  { id: 'late',      label: 'Late',          labelKey: 'sp_late',          sub: '3:00 PM onward',     subKey: 'sp_late_range',          startMin: 15 * 60, endMin: 24 * 60 },
]
function teeMinutes(teeTime) {
  // teeTime is "HH:MM" or "HH:MM:SS"
  const [h, m] = String(teeTime || '').split(':').map(n => Number(n) || 0)
  return h * 60 + m
}
function bucketFor(teeTime) {
  const mins = teeMinutes(teeTime)
  return TEE_BUCKETS.find(b => mins >= b.startMin && mins < b.endMin)?.id || 'late'
}

function TeeTimePicker({ api, packageId, coursesId, date, golfers, selected, onSelect, primary, branding, internalViewer, hideUpgradeBadge, extrasMarketMode, extrasMarketBase, track, surface = 'round' }) {
  const fire = track || (() => {})
  const { t } = useLanguage()
  const [data, setData] = useState(null) // { slots, upgrades, fallback?, message? }
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [showUpgrades, setShowUpgrades] = useState(false)
  const [bucket, setBucket] = useState('morning')

  useEffect(() => {
    if (!coursesId || !date) { setData(null); return }
    let cancelled = false
    setLoading(true); setErr(null)
    const params = new URLSearchParams({ date, golfers: String(golfers || 1) })
    if (packageId) params.set('packageId', String(packageId))
    // Internal viewer: ask backend for the debug breakdown. Backend only
    // honors this when the Authorization header proves super-admin or
    // reseller-account membership.
    if (internalViewer) params.set('debug', '1')
    api.get(`/courses/${coursesId}/tee-times?${params.toString()}`).then(r => {
      if (cancelled) return
      if (r.status >= 400) setErr(r.data?.message || `Error ${r.status}`)
      else setData(r.data || { slots: [], upgrades: [] })
    }).catch(e => { if (!cancelled) setErr(e.message) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [api, coursesId, date, golfers, packageId, internalViewer])

  if (!coursesId || !date) {
    return <p style={{ ...muted, fontSize: 12, marginTop: 10 }}>{t('sp_pick_course_and_date', 'Pick a course and date to see available tee times.')}</p>
  }
  if (loading) return (
    <div style={{ marginTop: 10 }}>
      {/* Skeleton chips while we hit the adapter — matches the real
          chip grid so the layout doesn't jump when results arrive. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {Array.from({ length: 12 }).map((_, i) => (
          <div key={i} className="ig-skel" style={{
            width: 70, height: 32, borderRadius: 6,
            background: '#e2e8f0',
          }} />
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 10, fontSize: 12, color: '#64748b' }}>
        <span style={{
          width: 14, height: 14, borderRadius: '50%',
          border: '2px solid #cbd5e1', borderTopColor: primary,
          display: 'inline-block', animation: 'ig-spin 0.8s linear infinite',
        }} />
        {t('sp_looking_up_tee_times', 'Looking up available tee times…')}
      </div>
      {/* Inline keyframes — kept local so we don't have to wire a
          global stylesheet just for this. The shimmer + spin classes
          are namespaced ig-* to avoid collisions. */}
      <style>{`
        @keyframes ig-spin { to { transform: rotate(360deg); } }
        @keyframes ig-shimmer {
          0%   { background-position: -200px 0; }
          100% { background-position: 200px 0; }
        }
        .ig-skel {
          background: linear-gradient(90deg, #e2e8f0 0px, #f1f5f9 80px, #e2e8f0 160px) !important;
          background-size: 400px 100% !important;
          animation: ig-shimmer 1.2s linear infinite;
        }
      `}</style>
    </div>
  )
  if (err) return <div style={{ ...errorBox, marginTop: 10 }}>{err}</div>
  if (!data) return null
  if (data.requestOnly) {
    // Request-only (concierge) course: no live tee sheet. Fixed price set by the
    // reseller; the customer requests a preferred time and we confirm by email.
    // Bill it like a premium slot (delta over the package cap). At booking the
    // server re-prices from RequestPrice and parks the round for confirmation.
    const rp = data.requestPrice
    const upG = data.upgradePerGolfer || 0
    const upT = data.upgradeTotal || 0
    return (
      <div style={{ marginTop: 10, padding: 10, background: '#f5f3ff', border: '1px solid #ddd6fe', borderRadius: 6, fontSize: 12, color: '#5b21b6' }}>
        <div style={{ fontWeight: 700, marginBottom: 4 }}>{t('sp_ro_request_title', 'Request this tee time')}</div>
        <div style={{ color: '#6d28d9', marginBottom: 6 }}>
          {rp != null ? `$${Number(rp).toFixed(2)} / ${t('sp_golfer', 'golfer')} · ` : ''}
          {t('sp_ro_confirm_by_email', "we'll confirm your exact time by email")}
          {upT > 0 ? ` · +$${Number(upT).toFixed(2)} ${t('sp_ro_over_package', 'over your package')}` : ''}
          {data.requestNote ? ` · ${data.requestNote}` : ''}
        </div>
        <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>{t('sp_ro_preferred_window', 'Preferred time window')}
          <select
            value={selected?.teeTime || ''}
            onChange={e => onSelect({
              teeTime: e.target.value, ref: null,
              pricePerGolfer: rp,
              totalPrice: rp != null ? Math.round(rp * (golfers || 1) * 100) / 100 : null,
              upgradePerGolfer: upG, upgradeTotal: upT,
            }, !!e.target.value)}
            style={{ ...inp, width: 180 }}
          >
            <option value="">{t('sp_ro_pick_window', 'Pick a window…')}</option>
            {TIME_WINDOWS.map(w => <option key={w.value} value={w.value}>{w.label}</option>)}
          </select>
        </label>
      </div>
    )
  }
  if (data.fallback) {
    // Backend couldn't reach the adapter — let the customer enter a
    // time manually and we'll resolve later via ops queue.
    return (
      <div style={{ marginTop: 10, padding: 10, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 12, color: '#92400e' }}>
        {data.message || 'Live availability unavailable.'} Enter a preferred time and we'll confirm by email:
        <input
          type="time"
          value={selected?.teeTime || ''}
          onChange={e => onSelect({ teeTime: e.target.value, ref: null }, false)}
          style={{ ...inp, width: 130, marginTop: 6 }}
        />
      </div>
    )
  }

  const { slots: rawSlots = [], upgrades: rawUpgrades = [], courseCancelPolicy } = data
  // When the caller asked us to hide the upgrade badge (in-package round
  // on a HardCap course, or extras on a non-BillExtrasFullPrice course),
  // merge the upgrade tier into the standard slot tier so the customer
  // sees a single uniform list — no "+$X premium" badges, no expander.
  // The slot objects still carry their upgrade fields, but the picker
  // treats them as plain slots and the parent's onSelect handler skips
  // recording the upgrade (since the customer wasn't billed for it).
  const slots    = hideUpgradeBadge ? [...rawSlots, ...rawUpgrades] : rawSlots
  const upgrades = hideUpgradeBadge ? []                            : rawUpgrades
  // Multi-course orgs (e.g. Lachute returns Thompson #1 + Watson #2 under one
  // tee-time.com listing) — label each slot with its layout so the customer can
  // tell them apart. Single-course orgs return one name → no label shown.
  const _distinctCourses = new Set([...rawSlots, ...rawUpgrades].map(s => s.courseName).filter(Boolean))
  const showCourseName = _distinctCourses.size > 1
  if (slots.length === 0 && upgrades.length === 0) {
    return <p style={{ ...muted, fontSize: 12, marginTop: 10 }}>{t(Number(golfers) === 1 ? 'sp_no_tee_times_available_golfer_singular' : 'sp_no_tee_times_available_golfers', `No tee times available at this course on this date for {count} golfer${golfers !== 1 ? 's' : ''}. Try a different date or course.`, { count: golfers })}</p>
  }

  // Bucket the slots so we can render only the picked window.
  const slotsByBucket = { early: [], morning: [], afternoon: [], late: [] }
  const upgradesByBucket = { early: [], morning: [], afternoon: [], late: [] }
  for (const s of slots)    slotsByBucket[bucketFor(s.teeTime)].push(s)
  for (const u of upgrades) upgradesByBucket[bucketFor(u.teeTime)].push(u)
  // Fall back from morning when empty so the picker isn't blank by default.
  let activeBucket = bucket
  if ((slotsByBucket[activeBucket].length + upgradesByBucket[activeBucket].length) === 0) {
    const firstNonEmpty = TEE_BUCKETS.find(b => slotsByBucket[b.id].length + upgradesByBucket[b.id].length > 0)
    if (firstNonEmpty) activeBucket = firstNonEmpty.id
  }
  const visibleSlots    = slotsByBucket[activeBucket] || []
  const visibleUpgrades = upgradesByBucket[activeBucket] || []

  function chip(s, isUpgrade) {
    const isSelected = selected?.ref === s.ref
    // Badge text differs by context:
    //   extrasMarketMode → show full round cost the customer pays for
    //                      this slot (actual price × golfers). For
    //                      BillExtrasFullPrice courses the server
    //                      exposes s.totalPrice on every slot so the
    //                      badge is accurate whether the slot is below
    //                      or above max. Falls back to (extrasMarketBase
    //                      + upgradeTotal) for above-max slots if the
    //                      server didn't include totalPrice for some
    //                      reason — that gives the same answer.
    //   default (in-package picker on a Cap course) → show "+$X" delta
    //                      that the slot adds on top of the bundle
    let badge = null
    if (extrasMarketMode) {
      const total = s.totalPrice != null
        ? Number(s.totalPrice)
        : (Number(extrasMarketBase) || 0) + (Number(s.upgradeTotal) || 0)
      if (total > 0) badge = fmtMoney(total)
    } else if (isUpgrade) {
      // Show the PER-GOLFER premium so the amount matches the "per golfer" copy
      // above; the trip-summary line below shows the total added to the package.
      const perGolfer = s.upgradePerGolfer != null ? Number(s.upgradePerGolfer) : (Number(s.upgradeTotal) || 0)
      badge = '+' + fmtMoney(perGolfer) + (Number(golfers) > 1 ? ' /' + t('sp_golfer_lower', 'golfer') : '')
    }
    return (
      <button
        type="button"
        key={s.ref}
        onClick={() => { fire('round_select', surface, { coursesId, date, upgrade: !!isUpgrade }); onSelect(s, isUpgrade) }}
        style={{
          padding: '8px 12px',
          background: isSelected ? primary : '#fff',
          color: isSelected ? '#fff' : '#0f172a',
          border: '1px solid ' + (isSelected ? primary : '#e2e8f0'),
          borderRadius: 6,
          fontSize: 13,
          fontWeight: 500,
          cursor: 'pointer',
          display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: 1,
          ...bodyFontStyle(branding),
        }}
        title={isUpgrade
          ? (extrasMarketMode
              ? `Full round cost for ${golfers} golfer${golfers === 1 ? '' : 's'}`
              : `Premium tee time: +${fmtMoney(s.upgradePerGolfer || 0)} per golfer`)
          : (showCourseName && s.courseName ? s.courseName : '')}
      >
        <span>
          {formatTeeTime(s.teeTime)}
          {badge && <span style={{ marginLeft: 6, fontSize: 11, color: isSelected ? '#fff' : '#92400e' }}>{badge}</span>}
        </span>
        {showCourseName && s.courseName && (
          <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.8, color: isSelected ? '#fff' : '#64748b' }}>
            {s.courseName}
          </span>
        )}
      </button>
    )
  }

  return (
    <div style={{ marginTop: 10 }}>
      {/* Time-of-day bucket selector. Disabled on buckets with no
          slots so the customer doesn't pick into an empty list. */}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {TEE_BUCKETS.map(b => {
          const count = (slotsByBucket[b.id]?.length || 0) + (upgradesByBucket[b.id]?.length || 0)
          const isActive = activeBucket === b.id
          const isEmpty = count === 0
          return (
            <button
              key={b.id}
              type="button"
              onClick={() => !isEmpty && setBucket(b.id)}
              disabled={isEmpty}
              style={{
                padding: '8px 14px',
                background: isActive ? primary : '#fff',
                color:      isActive ? '#fff'   : (isEmpty ? '#cbd5e1' : '#0f172a'),
                border: '1px solid ' + (isActive ? primary : '#e2e8f0'),
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 600,
                cursor: isEmpty ? 'not-allowed' : 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 2,
                ...bodyFontStyle(branding),
              }}
            >
              <span>{t(b.labelKey, b.label)}</span>
              <span style={{ fontSize: 10, fontWeight: 400, opacity: 0.85 }}>
                {t(b.subKey, b.sub)}
                {!isEmpty
                  ? ` · ${t(count === 1 ? 'sp_time_count_singular' : 'sp_times_count', `{count} time${count === 1 ? '' : 's'}`, { count })}`
                  // Spell out WHY a bucket is empty when the customer is on a
                  // group ≥ 2 — they should know the time exists but doesn't
                  // have 4 (or whatever) seats free, not that the course just
                  // closed for that window.
                  : (Number(golfers) > 1
                      ? ` · ${t('sp_none_with_seats', 'none with {count} seats', { count: golfers })}`
                      : ` · ${t('sp_none', 'none')}`)}
              </span>
            </button>
          )
        })}
      </div>

      {/* If the standard window has zero in-budget slots but premium
          slots exist, auto-expand the premium list so the customer
          isn't stuck looking at "No standard tee times" + a collapsed
          CTA. Same fix shape as the hotel picker. */}
      {(() => {
        const noStandard = visibleSlots.length === 0 && visibleUpgrades.length > 0
        const showUpgradeList = noStandard || showUpgrades
        return (
          <>
            {visibleSlots.length > 0 ? (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {visibleSlots.map(s => chip(s, false))}
              </div>
            ) : visibleUpgrades.length === 0 ? (
              <div style={{ fontSize: 12, color: '#64748b' }}>{t('sp_no_standard_tee_times_window', 'No standard tee times in this window.')}</div>
            ) : null}
            {visibleUpgrades.length > 0 && (
              <div style={{ marginTop: 10 }}>
                {noStandard && (
                  <div style={{ fontSize: 12, color: '#92400e', marginBottom: 8, lineHeight: 1.5 }}>
                    <strong>{t('sp_no_standard_within_budget', "No standard tee times in this window are within your package's per-round budget.")}</strong>
                    <div style={{ marginTop: 2 }}>
                      {extrasMarketMode
                        ? t('sp_times_full_price_extra_round', 'The times below are charged at the full tee-time price for this extra round.')
                        : t('sp_premium_cost_per_golfer_note', 'The amount on each premium time is the extra cost per golfer. The total is added to your trip below.')}
                    </div>
                  </div>
                )}
                {!showUpgradeList ? (
                  <button
                    type="button"
                    onClick={() => setShowUpgrades(true)}
                    style={{
                      background: '#fffbeb',
                      border: '1px dashed #f59e0b',
                      color: '#92400e',
                      padding: '6px 10px',
                      borderRadius: 6,
                      fontSize: 12,
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    {extrasMarketMode
                      ? t(visibleUpgrades.length === 1 ? 'sp_more_time_full_price_singular' : 'sp_more_times_full_price', `{count} more time${visibleUpgrades.length > 1 ? 's' : ''} available (full price for this extra round) →`, { count: visibleUpgrades.length })
                      : t(visibleUpgrades.length === 1 ? 'sp_additional_more_time_singular' : 'sp_additional_more_times', `For an additional {amount}, {count} more time${visibleUpgrades.length > 1 ? 's' : ''} available →`, { amount: fmtMoney(visibleUpgrades[0].upgradeTotal || 0), count: visibleUpgrades.length })}
                  </button>
                ) : (
                  <>
                    {!noStandard && <div style={{ fontSize: 11, color: '#92400e', fontWeight: 600, marginBottom: 4 }}>{t('sp_premium_tee_times_additional_cost', 'Premium tee times (additional cost)')}</div>}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {visibleUpgrades.map(s => chip(s, true))}
                    </div>
                    {!noStandard && <button type="button" onClick={() => setShowUpgrades(false)} style={{ ...secondaryBtn, marginTop: 8 }}>{t('sp_hide_premium_times', 'Hide premium times')}</button>}
                  </>
                )}
              </div>
            )}
          </>
        )
      })()}
      <div style={{ fontSize: 11, color: '#64748b', marginTop: 8, fontStyle: 'italic' }}>
        {t('sp_cancellation_policy_label', 'Cancellation: {policy}', { policy: courseCancelPolicy || t('sp_cancellation_default', "Subject to the course's standard policy — usually 24-48 hour notice required for cancellation.") })}
      </div>
      {/* Internal-viewer debug panel — only shown when backend confirmed
          the viewer's auth + returned per-slot _debug blocks. Customers
          see nothing here. Shows the markup breakdown so upgrade math
          can be diagnosed without curl + token-pasting. */}
      {internalViewer && (data?.slots?.some(s => s._debug) || data?.upgrades?.some(u => u._debug)) && (
        <details style={{ marginTop: 12, padding: 8, background: '#0f172a', color: '#94a3b8', borderRadius: 6, fontSize: 11, fontFamily: 'monospace' }}>
          <summary style={{ cursor: 'pointer', color: '#fbbf24', fontWeight: 600 }}>
            🛠 Internal debug (viewer: {internalViewer.email}{internalViewer.groupId === 1 ? ' · super-admin' : ''})
          </summary>
          <table style={{ width: '100%', marginTop: 8, fontSize: 11, borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ color: '#cbd5e1' }}>
                <th style={{ textAlign: 'left', padding: '4px 8px' }}>Tee time</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Raw fee</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Markup %</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Sell price</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Max</th>
                <th style={{ textAlign: 'right', padding: '4px 8px' }}>Upgrade</th>
              </tr>
            </thead>
            <tbody>
              {/* Upgrades first so the over-budget ones (the interesting
                  rows) always show, even when there are many in-budget
                  slots that would otherwise fill the 60-row slice. */}
              {[...(data.upgrades || []), ...(data.slots || [])].slice(0, 60).map((s, i) => {
                const d = s._debug || {}
                return (
                  <tr key={s.ref || i} style={{ borderTop: '1px solid #1e293b' }}>
                    <td style={{ padding: '3px 8px' }}>{s.teeTime}</td>
                    <td style={{ padding: '3px 8px', textAlign: 'right' }}>${d.rawFee != null ? d.rawFee.toFixed(2) : '—'}</td>
                    <td style={{ padding: '3px 8px', textAlign: 'right' }}>{d.markupPct != null ? (d.markupPct * 100).toFixed(1) + '%' : '—'}</td>
                    <td style={{ padding: '3px 8px', textAlign: 'right' }}>${d.sellPrice != null ? d.sellPrice.toFixed(2) : '—'}</td>
                    <td style={{ padding: '3px 8px', textAlign: 'right' }}>{d.maxPrice != null ? '$' + d.maxPrice.toFixed(2) : '—'}</td>
                    <td style={{ padding: '3px 8px', textAlign: 'right', color: d.upgradeIfOverBudget > 0 ? '#f59e0b' : '#94a3b8' }}>
                      {d.upgradeIfOverBudget > 0 ? '+$' + d.upgradeIfOverBudget.toFixed(2) : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </details>
      )}
    </div>
  )
}

// ─── Resume-availability banner ─────────────────────────────────────────
//
// Shown on the Review screen when the customer arrives via the
// /customer/trips "Continue this trip" flow. Verifies each saved tee
// time is still bookable and surfaces the result inline so a stale
// slot doesn't blow up at payment time. While loading, shows a
// progress indicator. If any round is unavailable, lists up to 3
// nearest-time alternatives the customer can click to swap in.

function ResumeAvailabilityBanner({ checking, availability, err, onPickAlternative }) {
  if (err) {
    return (
      <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, color: '#991b1b' }}>
        <strong>Couldn't verify tee-time availability:</strong> {err}. Use "Back to edit" to re-pick your tee times manually.
      </div>
    )
  }
  if (checking || !availability) {
    return (
      <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, color: '#1e40af', display: 'flex', alignItems: 'center', gap: 10 }}>
        <Spinner />
        <div>
          <strong>Resuming your saved trip.</strong> Checking that your saved tee times are still available...
        </div>
      </div>
    )
  }
  const allGood = availability.every(r => r.available);
  if (allGood) {
    return (
      <div style={{ background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 8, padding: 12, marginBottom: 16, fontSize: 13, color: '#065f46' }}>
        <strong>All tee times are still available.</strong> Confirm and pay below to lock in your trip.
      </div>
    )
  }
  const bad = availability.filter(r => !r.available);
  return (
    <div style={{ background: '#fef3c7', border: '1px solid #f59e0b', borderRadius: 8, padding: 14, marginBottom: 16 }}>
      <div style={{ fontSize: 13, color: '#78350f', fontWeight: 600, marginBottom: 8 }}>
        {bad.length} of your saved tee time{bad.length > 1 ? 's are' : ' is'} no longer available
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {bad.map(r => (
          <div key={r.roundIndex} style={{ background: '#fff', border: '1px solid #fcd34d', borderRadius: 6, padding: 10 }}>
            <div style={{ fontSize: 13, color: '#78350f' }}>
              <strong>Round {r.roundIndex + 1}</strong>: {r.courseName} on {r.playDate} at {r.teeTime} is no longer available.
              {r.providerError && ' (Course system error — try again or pick a different time.)'}
            </div>
            {r.alternatives && r.alternatives.length > 0 && (() => {
              const inBudget = r.alternatives.filter(a => a.inBudget)
              const upgrades = r.alternatives.filter(a => !a.inBudget)
              return (
                <div style={{ marginTop: 8 }}>
                  {inBudget.length > 0 && (
                    <>
                      <div style={{ fontSize: 12, color: '#92400e', marginBottom: 6 }}>Pick a nearby time:</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: upgrades.length ? 10 : 0 }}>
                        {inBudget.map(alt => (
                          <button
                            key={alt.teeTime} type="button"
                            onClick={() => onPickAlternative(r.roundIndex, alt)}
                            style={{
                              padding: '6px 12px', background: '#fff',
                              border: '1px solid #cbd5e1', borderRadius: 16,
                              fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#0f172a',
                            }}
                          >{alt.teeTime}</button>
                        ))}
                      </div>
                    </>
                  )}
                  {upgrades.length > 0 && (
                    <>
                      <div style={{ fontSize: 12, color: '#92400e', marginBottom: 6 }}>
                        Premium times (extra fee added to your total):
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {upgrades.map(alt => (
                          <button
                            key={alt.teeTime} type="button"
                            onClick={() => onPickAlternative(r.roundIndex, alt)}
                            title={`+${fmtMoney(alt.upgradeTotal)} total (${fmtMoney(alt.upgradePerGolfer)} per golfer)`}
                            style={{
                              padding: '6px 12px', background: '#fffbeb',
                              border: '1px solid #f59e0b', borderRadius: 16,
                              fontSize: 13, fontWeight: 600, cursor: 'pointer', color: '#78350f',
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                            }}
                          >
                            <span>{alt.teeTime}</span>
                            <span style={{
                              background: '#f59e0b', color: '#fff',
                              fontSize: 11, padding: '1px 6px', borderRadius: 10, fontWeight: 700,
                            }}>+{fmtMoney(alt.upgradeTotal)}</span>
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )
            })()}
            {(!r.alternatives || r.alternatives.length === 0) && !r.providerError && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#92400e' }}>
                No other times available on that date. Use "Back to edit" to pick a different date or course.
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
function Spinner() {
  return (
    <div style={{
      width: 14, height: 14, border: '2px solid #bfdbfe', borderTopColor: '#1e40af',
      borderRadius: '50%', animation: 'rcp-spin 0.8s linear infinite',
    }}>
      <style>{`@keyframes rcp-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  )
}

// ─── Helpers / shared ───────────────────────────────────────────────────

function Section({ title, subtitle, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <h3 style={{ margin: '0 0 4px', fontSize: 15, color: '#0f172a' }}>{title}</h3>
      {subtitle && <p style={{ margin: '0 0 12px', fontSize: 12, color: '#64748b' }}>{subtitle}</p>}
      <div style={{ marginTop: subtitle ? 4 : 12 }}>{children}</div>
    </div>
  )
}
function Field({ label, children, style }) {
  return (
    <div style={{ marginBottom: 12, ...style }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  )
}
function Line({ label, value, bold, currency }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: 14, fontWeight: bold ? 700 : 400 }}><span>{label}</span><span>{fmtMoney(value, currency)}</span></div>
}
function Loading() { return <div style={{ padding: 60, textAlign: 'center', color: '#64748b' }}>Loading…</div> }

function addDays(dateStr, days) {
  if (!dateStr) return ''
  const d = new Date(dateStr + 'T00:00:00')
  d.setDate(d.getDate() + Number(days))
  return d.toISOString().slice(0, 10)
}
function today() { return new Date().toISOString().slice(0, 10) }

// Haversine straight-line distance in km. Mirrors the server-side
// helper in resellerEmbedController.js — same rounding so the chip
// the customer sees on the hotel picker matches what shows up here
// on the tee-time picker.
function haversineKm(lat1, lng1, lat2, lng2) {
  if (lat1 == null || lng1 == null || lat2 == null || lng2 == null) return null
  const toRad = d => d * Math.PI / 180
  const dLat = toRad(Number(lat2) - Number(lat1))
  const dLng = toRad(Number(lng2) - Number(lng1))
  const a = Math.sin(dLat / 2) ** 2
          + Math.cos(toRad(Number(lat1))) * Math.cos(toRad(Number(lat2))) * Math.sin(dLng / 2) ** 2
  return 6371 * 2 * Math.asin(Math.sqrt(a))
}
function roundDistanceKm(km) {
  if (km == null) return null
  if (km < 10)  return Math.round(km * 10) / 10
  if (km < 100) return Math.round(km)
  return Math.round(km / 5) * 5
}

// Format a number as "$1,234.56" with thousands separators. Used
// EVERYWHERE prices show in the iframe — keeps the display consistent.
// Pass a currency code (e.g. "CAD") to prefix it: "CAD $1,234.56".
function fmtMoney(value, currency) {
  const n = Number(value)
  if (!Number.isFinite(n)) return ''
  const formatted = '$' + n.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  return currency ? `${currency} ${formatted}` : formatted
}

// formatPhone / isValidPhone now live in src/utils/phone.js (shared, +1-aware).

// Convert "HH:MM:SS" or "HH:MM" (24-hour) to a friendly 12-hour
// "h:mm AM/PM". Strips seconds — golfers don't pick to-the-second.
function formatTeeTime(raw) {
  if (!raw) return ''
  const [hStr, mStr] = String(raw).split(':')
  const h = Number(hStr), m = Number(mStr)
  if (!Number.isFinite(h) || !Number.isFinite(m)) return raw
  const period = h >= 12 ? 'PM' : 'AM'
  const h12 = h === 0 ? 12 : (h > 12 ? h - 12 : h)
  return `${h12}:${String(m).padStart(2, '0')} ${period}`
}
function addonPriceTotal(a, numGolfers) {
  let total = 0
  if (a.PricePerGolfer != null) total += Number(a.PricePerGolfer) * Number(numGolfers)
  if (a.PricePerPackage != null) total += Number(a.PricePerPackage)
  return total
}
function tierLabel(tier, t) {
  if (tier.DaysBeforeMax == null) return t('sp_days_before_min', '{min}+ days before', { min: tier.DaysBeforeMin })
  return t('sp_days_before_range', '{min}–{max} days before', { min: tier.DaysBeforeMin, max: tier.DaysBeforeMax })
}

const inp = { width: '100%', padding: '10px 12px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box', minWidth: 0, maxWidth: '100%' }
const row = { display: 'flex', gap: 12 }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }
const empty = { padding: 60, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }
const muted = { color: '#64748b', fontSize: 14 }
const selectS = { padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', background: '#fff', flex: 1, minWidth: 220 }
const secondaryBtn = { padding: '8px 14px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }
const ghostBtn = { padding: '4px 8px', background: 'transparent', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 4, cursor: 'pointer', fontSize: 14 }
